// main.ts — the BROWSER HOST SHELL: boots the TS client's ShellSession over
// the shipped art packs and runs it user-playable in a real browser. The art
// pack matches the shipped shell's screen-size selection (824 @ gameScale 1.5
// on normal windows, 832 @ 1.0 under 480px; ?824/?832 override) — see
// selectArtSize below.
//
// The host is a thin wrapper around the session substrate the tests already
// prove (test/client/shell-smoke.test.ts is the construction reference):
//   - the scripted clock becomes performance.now()/10 ticks fed as DELTAS to
//     advanceTicks (capped per frame against the background-tab spiral),
//   - frame() blits to a WINDOW-SIZED canvas via putImageData (the WASM
//     client's GetSurfaceProperties sizing; window resizes re-partition the
//     display the way HostPollPendingResize does),
//   - pointer events map back through the CSS scale into injectPen,
//   - Shell::PlayGame's playLevel/playSavedGame seams stand up GameSessions
//     (game.cpp PlayLevel's do-while: next-level chain, retry, pvar carry),
//   - prefs/saves/completes persist in localStorage (web/stores.ts).
//
// Audio (M6): the REAL SoundMgr over the shipped htsfx.pdb drives a
// WebAudioSoundDevice; the AudioContext unlock follows the wi-ios-audio-fix
// pattern (touchend/click/keydown + visibilitychange-visible + pageshow —
// modern WebKit doesn't treat touchstart as user activation and iOS suspends
// the context across interruptions). Enable/volume honor the prefs
// (game.cpp:445-457); the GameOptions sliders write straight through the
// ShellEnv.sndm seam. If htsfx.pdb is missing the shell falls back to the
// silent MemorySoundMgr.
//
// Multiplayer (M5b): the DOM lobby (dom/lobby-controller.ts ↔ game/Lobby.cpp)
// runs over a real WebSocket XTransport; the staged BeginGame lands in
// runSimulation below, which builds the lockstep MP GameSession that
// renders/pumps in the SAME rAF loop as SP.
// Chat is the shipped WEB overlay (web/chat.ts ↔ webchatcontroller.cpp).
// ?api=<serverinfo-url|local> overrides the server list exactly like the WASM
// shell's ?api → WI_API_URL (chooseserverform.cpp:388-394; 'local' targets
// scripts/local-mp.sh's leaderboard on :8786); default prod. ?beta shows
// non-production servers (ENV_BETA, chooseserverform.cpp:303-309).
//
// DLC (the Add-On Mission Packs flow): the DOM dlc-controller (game/
// dlmissionpack.cpp) over the real HTTP stack —
// FetchHttpService (gphttp), HttpPackManager over an IndexedDB-backed
// PackStore (gppackm; packs are MBs — hydrated at boot before
// InitFromInstalled), HttpPackInfoManager (gppim). Downloaded packs mount
// onto the live PackFileReader around each mission (game.cpp:1635/1674
// Mount/Unmount), so their missions enumerate in the mission lists and PLAY.

import { IniReader } from '../../../sim/src/data/ini.ts';
import type { IniSection, AnirJson } from '../../../sim/src/index.ts';
import { kfGobMobileUnit, kfGobSelected, kfGobActive } from '../../../sim/src/gob/gob.ts';
import type { Gob } from '../../../sim/src/gob/gob.ts';
import {
  dbgOverlay,
  toggleDebugPaths,
  toggleDebugLines,
  toggleDebugShowFps,
} from '../render/debugoverlay.ts';
import { noteFrameIntervalMs } from '../render/projectile-interp.ts';
import { setFullRepaintPerFrame } from '../ui/gamerenderer.ts';
import { parseCini } from '../../../sim/tools/vendor/cini.cjs';
import { MemPdbReader } from '../io/pdbreader.ts';
import { PackFileReader } from '../io/packfile.ts';
import { TexAtlasMgr } from '../gfx/texatlasmgr.ts';
import { CoordMap } from '../gfx/coords.ts';
import { anirJsonFromPack } from '../gfx/anirpack.ts';
import { installTelemetry, track, setTelemetryIdentity } from './telemetry.ts';
import {
  perfGameStart,
  perfGameEnd,
  perfNoteFrame,
  perfNotePaint,
  perfNoteGobs,
} from './perf-report.ts';
import {
  gszVersion,
  type PackId,
  type SaveStore,
  HostOpenSaveGameStream,
  knGameReinitializeSave,
  ClearResumeSlotOnReturnToMenu,
} from '../io/savegame.ts';
import { CompleteManager } from '../io/completemgr.ts';
import { ShellSession } from '../ui/shell.ts';
import { GameSession } from '../ui/gamesession.ts';
import { Rect } from '../ui/rect.ts';
import { kfCtlVisible } from '../ui/uikit.ts';
import {
  penDownEvent,
  penUpEvent,
  penMoveEvent,
  penDownEvent2,
  penUpEvent2,
  penMoveEvent2,
  newEvent,
  kfEvtRightButton,
  kfEvtFinger,
  askStringEvent,
  gameOverEvent,
  knGoAbortLevel,
  knGoLoadSavedGame,
  kfRedrawDirty,
} from '../ui/event.ts';
import {
  keyBitPageUp,
  keyBitPageDown,
  keyBitDpadLeft,
  keyBitDpadRight,
  keyBitDpadButton,
} from '../ui/inputui.ts';
import { XTransport } from '../net/xtransport.ts';
import { FetchHttpService } from '../net/httpservice.ts';
import { HttpPackManager } from '../net/httppackmanager.ts';
import { HttpPackInfoManager } from '../net/httppackinfomanager.ts';
import { kgidNull } from '../../../sim/src/sm/message.ts';
import { TRect } from '../../../sim/src/types.ts';
import { decodePng } from './png.ts';
import { GlRenderer } from '../gfx/gl/glrenderer.ts';
import type { GlDibBitmap } from '../gfx/gl/glsurface.ts';
import type { CpuDibBitmap } from '../gfx/dibbitmap.ts';
import { WebBuffer } from './buffer-shim.ts';
import { IdbPackStore } from './stores.ts';
import { openPersistentStores } from './migrate-wasm.ts';
import type { PrefStore } from '../io/prefs.ts';
import type { CompleteStore } from '../io/completemgr.ts';
import { ChatOverlay } from '../dom/chat-overlay.ts';
import { buildAddBotUrl } from './bot-control.ts'; // B4b "Add Bot" → POST /bots/add (pure URL builder)
import { GameHost } from '../host/gamehost.ts';
import { MenuController, stripHelpTags, type Difficulty } from '../dom/menu-controller.ts';
import { MenuView } from '../dom/menu-view.ts';
import { warmWiFonts } from '../dom/fonts.ts';
import { LobbyController } from '../dom/lobby-controller.ts';
import { LobbyView } from '../dom/lobby-view.ts';
import { CommunityController, type CommunityTab } from '../dom/community-controller.ts';
import { CommunityView } from '../dom/community-view.ts';
import { type ProfileSignInResult } from '../dom/profile-controller.ts';
import {
  LoginHandler,
  knGetTokenResultSuccess,
  knGetTokenResultNoPassword,
} from '../ui/loginhandler.ts';
import { openReplayViewer, kszReplayViewerUrl } from '../ui/replaylink.ts';
import { startReplayViewer, type ReplayHandle } from './replay-player.ts';
import { kszPackUrl } from '../net/httpservice.ts';
import type { ObjectivesSnapshot } from '../dom/ingame/objectives-snapshot.ts';
import type { ObjectivesSummaryInfo } from '../ui/objectives.ts';
import { PauseMenuView } from '../dom/ingame/pause-menu-view.ts';
import type { HudState } from '../dom/ingame/hud-format.ts';
import { GameOptionsView } from '../dom/ingame/game-options-view.ts';
import type { GameOptionsModel } from '../dom/ingame/game-options-io.ts';
import type { EcomInfo } from '../ui/ecom.ts';
import type { CutSceneInfo } from '../ui/cutscene.ts';
import { LoadSaveView } from '../dom/ingame/load-save-view.ts';
import { WaitingView } from '../dom/ingame/waiting-view.ts';
import { MessageBoxView } from '../dom/ingame/message-box-view.ts';
import { TestOptionsView } from '../dom/ingame/test-options-view.ts';
import { HelpManualOverlayView } from '../dom/ingame/help-manual-overlay-view.ts';
import { createCommandUI } from '../dom/ingame/command-ui.ts';
import { createHudSeams } from '../dom/ingame/hud-seams.ts';
import { createObjectivesUI } from '../dom/ingame/objectives-ui.ts';
import { createCutSceneUI } from '../dom/ingame/cutscene-ui.ts';
import { createEcomUI } from '../dom/ingame/ecom-ui.ts';
import { createMessageBoxUI } from '../dom/ingame/messagebox-ui.ts';
import { createSummaryUI } from '../dom/ingame/summary-ui.ts';
import { inGameMenuButtons, type InGameMenuButton } from '../ui/gameoptions.ts';
import type { ShellEnv } from '../ui/shellform.ts';
import { kfPlrObserver } from '../../../sim/src/player/side.ts';
import { DlcController } from '../dom/dlc-controller.ts';
import { DlcView } from '../dom/dlc-view.ts';
import { makeMenuOptionsIO } from '../dom/options-io.ts';
import { makeMenuSaves, defaultSaveSlot } from '../dom/saves-io.ts';
import {
  extractAtlasArt,
  extractButtonArt,
  titleArtToDataUrl,
  resolveHelpImageDataUrl,
} from '../dom/title-art.ts';
import { formScale } from '../dom/chrome.ts'; // the faithful design-stage scale (the install-pack DOM modal)
import { ksfxGuiButtonTap } from '../ui/uikit.ts'; // the GUI tap sfx (the install-pack DOM modal buttons)
import { armInstallPrompt, maybeShowInstallPrompt } from './install-prompt.ts'; // PWA "Add to Home Screen" affordance
import { UpdateCheck } from './update-check.ts'; // already-running clients pick up new builds (#206)
import { SoundMgr } from '../audio/soundmgr.ts';
import { WebAudioSoundDevice } from '../audio/webaudiodev.ts';
import {
  knPrefVolume,
  kfPrefSoundMuted,
  kszPrefAskUrl,
  SavePreferences,
  kszPrefDeviceId,
  kszPrefUsername,
  kszPrefPassword,
  kszPrefToken,
  kfPrefAnonymous,
} from '../io/prefs.ts';
import { CreateMissionList, kmltAll, kmltSinglePlayer } from '../ui/missionpick.ts';
import { sendPlayBeacon } from '../net/playbeacon.ts';
import templatesJson from '../../../sim/data/GobTemplates.ini.json';
import anirJson from '../../../sim/data/anir.json';

// ---- art pack + scale selection (game/sdl/web/index.html:216-241) -----------
// The shipped shell picks the art pack by URL param, else screen size:
//   ?824 → 24px tiles, ?832 → 32px tiles, ?2432 → 32px tiles at 24bpp true
//   color (htdata2432.pdb; overrides ?824/?832 exactly like the shipped shell,
//   whose 2432 block runs last); no param → 824 when the window is at least
//   480px in BOTH dimensions (cy/320 >= 1.5 && cx/320 >= 1.5), else 832.
//   gameScale = 1.5 for 24px art (tiles land at 36 CSS px — prod's desktop
//   default) AND for 2432 (gameArtDepth 24 — index.html:241), 1.0 for 832.
// The pixel oracles mint per mode (?832/?824/?2432 — auto/static-frame.mjs).
// ?2432's sim anim data is pack-derived (see boot(); gfx/anirpack.ts).
const urlParams = new URLSearchParams(location.search);
// ?fullpaint — restore the SDL-era full-repaint-per-frame (the scoped-invalidation
// diagnosis escape hatch; see GameRenderer.frameStartInvalidate)
setFullRepaintPerFrame(urlParams.has('fullpaint'));
const gameArtDepth = urlParams.has('2432') ? 24 : 8; // index.html:222-226
function selectArtSize(): number {
  if (gameArtDepth === 24) return 32; // 2432: 32px tiles (overrides 824/832)
  if (urlParams.has('824')) return 24;
  if (urlParams.has('832')) return 32;
  const standalone = (window.navigator as { standalone?: boolean }).standalone === true;
  const cx = standalone ? document.body.getBoundingClientRect().width : window.innerWidth;
  const cy = standalone ? document.body.getBoundingClientRect().height : window.innerHeight;
  return cy / 320 >= 1.5 && cx / 320 >= 1.5 ? 24 : 32;
}
const gameArtSize = selectArtSize();
const kszArtPack =
  gameArtDepth === 24 ? 'htdata2432.pdb' : `htdata${gameArtSize === 24 ? '824' : '832'}atlas.pdb`;

// HostHelpers::GetSurfaceProperties (game/sdl/web/hosthelpers.cpp:161-185) —
// the shipped WASM client sizes the game surface to the BROWSER WINDOW:
// CSS window size / gameScale, height floored at 320, width capped at
// kpcMax-32 (=2016) and rounded down to a multiple of 4. game pixels ==
// CSS pixels / gameScale (devicePixelRatio is only traced, never applied,
// in the C++).
const kGameScale = gameArtSize === 24 || gameArtDepth === 24 ? 1.5 : 1.0; // index.html:241

// The in-game UI CHROME magnification — the HUD bar, the build/structure/upgrade panels,
// and the unit-menu buttons. It used to be pinned to kGameScale (the MAP present scale), so
// on phones (kGameScale 1.0) the in-game UI rendered too small even though the map scale was
// right. Decouple it from the MAP: the chrome now magnifies with the rest of the DOM UI
// (chrome.ts formScale, which fills a phone's viewport). formScale ≈ 2 chrome px per design
// unit, so /2 recovers the equivalent magnify; the kGameScale floor keeps the chrome from
// ever rendering SMALLER than the old map-matched size (portrait, where formScale is
// width-bound). World-ANCHORED placement (unit-menu/confirm anchor, sim-ui labels) keeps
// kGameScale so it stays aligned to the canvas — only the chrome SIZE follows kUiMagnify.
// Computed once at load, like kGameScale (the map scale + art pack are load-fixed too).
const kUiMagnify = Math.max(kGameScale, formScale() / 2);
function surfaceSize(): { cx: number; cy: number } {
  // the standalone (iOS Add-To-Home-Screen) arm mirrors hosthelpers.cpp:170-176
  const standalone = (window.navigator as { standalone?: boolean }).standalone === true;
  const cx = Math.trunc(
    (standalone ? document.body.getBoundingClientRect().width : window.innerWidth) / kGameScale,
  );
  const cy = Math.trunc(
    (standalone ? document.body.getBoundingClientRect().height : window.innerHeight) / kGameScale,
  );
  const kpcMax = 64 * 32; // gfx/coords.ts ktcMax*32 (ht.h:759)
  return {
    cx: Math.min(cx, kpcMax - 32) & ~3, // hosthelpers.cpp:182
    cy: Math.max(320, cy), // hosthelpers.cpp:179
  };
}

const screen = surfaceSize();
const templates = (templatesJson as { sections: IniSection[] }).sections;
// The SIM's anim metadata. 832-derived anir.json drives BOTH 8bpp modes (the
// packs' sim-relevant .anir fields converge — gfx/anirpack.ts documents the
// GetSpecialPoint32 four-thirds normalization that makes 824 ≡ 832; pinned by
// test/client/art-anim-parity.test.ts). 2432's anim data genuinely diverges
// (strip lists/frame counts/holds), so boot() swaps in the MOUNTED pack's own
// metadata — exactly what the C++ ?2432 client's sim consumes.
let anir = anirJson as unknown as AnirJson;

// ---- DOM + the renderer seam -------------------------------------------------
// The PRODUCTION renderer is WebGL2 (the plan's "same compositing math, WebGL2
// presentation"): the session draws into GL-backed DibBitmap surfaces and
// present is a framebuffer blit. ?cpu forces the CPU reference compositor
// (ImageData/putImageData — the oracle path), and a failed webgl2 context
// creation falls back to it automatically.

const canvas = document.getElementById('screen') as HTMLCanvasElement;
const elStatus = document.getElementById('statustext')!;
const status = (s: string): void => {
  elStatus.textContent = s;
};
// The boot download bar (index.html #statusbar/#statusbarfill): fills from the
// end of the status text to the right edge as the packs stream in. Pass a
// fraction in [0,1] to show/fill it, or null to hide it once loading is done.
const elStatusBar = document.getElementById('statusbar') as HTMLElement;
const elStatusBarFill = document.getElementById('statusbarfill') as HTMLElement;
const progress = (frac: number | null): void => {
  if (frac === null) {
    elStatusBar.style.display = 'none';
    return;
  }
  elStatusBar.style.display = 'block';
  elStatusBarFill.style.width = `${Math.max(0, Math.min(1, frac)) * 100}%`;
};

// Generic client telemetry / error reporting (prod-gated). Installed before the first WebGL2 attempt
// so a webgl2_init_failed below is captured; the uncaught-error + unhandledrejection handlers it wires
// cover everything else. Identity (did/account) is registered later in boot() once the shell exists.
installTelemetry(gszVersion);

let glr: GlRenderer | null = null;
if (!urlParams.has('cpu')) {
  try {
    // onDiag surfaces in-renderer WebGL2 failures (texture-allocation OOM, context loss) as telemetry.
    glr = GlRenderer.create(canvas, (kind, detail) =>
      track(kind, detail, kind.endsWith('restored') ? 'info' : 'error'),
    );
  } catch (e) {
    console.warn('webgl2 unavailable — falling back to the CPU reference renderer:', e);
    // The silent CPU fallback means we'd never learn a real user lost the GPU path — report it.
    track('webgl2_init_failed', { msg: String((e as Error)?.message ?? e) }, 'error');
  }
}
const rendererName = glr !== null ? 'webgl2' : 'cpu';
// the surface factory the sessions thread to every DibBitmap creation
const surfaceFactory =
  glr === null ? undefined : (cx: number, cy: number): CpuDibBitmap => glr!.createSurface(cx, cy);

// CPU reference present state (unused under GL)
const ctx2d = glr === null ? canvas.getContext('2d')! : null;
let img: ImageData | null = null;
let dst32: Uint32Array | null = null;

// emscripten_set_canvas_element_size + emscripten_set_element_css_size
// (display.cpp:199-204): backing store = game pixels, CSS = game * gameScale
function applyCanvasSize(): void {
  canvas.width = screen.cx;
  canvas.height = screen.cy;
  canvas.style.width = `${screen.cx * kGameScale}px`;
  canvas.style.height = `${screen.cy * kGameScale}px`;
  if (ctx2d !== null) {
    img = new ImageData(screen.cx, screen.cy);
    dst32 = new Uint32Array(img.data.buffer);
  }
}
applyCanvasSize();

function blitCpu(rgba: Uint8Array): void {
  // force alpha opaque (fog/unpainted dib regions carry partial alpha)
  const src32 = new Uint32Array(rgba.buffer, rgba.byteOffset, screen.cx * screen.cy);
  for (let i = 0; i < dst32!.length; i++) dst32![i] = src32[i] | 0xff000000;
  ctx2d!.putImageData(img!, 0, 0);
}

// Display::FrameComplete's present (display.cpp:291-307): GL = draw the back
// surface to the canvas (SDL_RenderCopy + Present); CPU = putImageData.
// IDLE GATE (battery): with preserveDrawingBuffer:true the compositor retains
// the last presented frame, so a rAF where nothing drew (drawSerial unmoved —
// a settled menu, a paused game) issues ZERO GL work. Resize forces one.
let lastPresentSerial = -1;
function present(): void {
  if (glr !== null) {
    if (glr.drawSerial === lastPresentSerial) return; // static frame — idle
    glr.present((act().back as GlDibBitmap).data);
    lastPresentSerial = glr.drawSerial;
  } else {
    blitCpu(act().frame());
  }
}

// ---- the shared art stack + stores (built in boot()) -----------------------

let pakr: PackFileReader;
let tam: TexAtlasMgr;
let coords: CoordMap;
let gameIni: IniReader;
let shell: ShellSession;
// (the in-game OBJECTIVES briefing view instance now lives INSIDE the shared createObjectivesUI
// factory — see objectivesUi in boot(); wiUi.closeObjectives still drives it by DOM id.)
// (the WIN/LOSE RESULTS SUMMARY view instance now lives INSIDE the shared createSummaryUI factory —
// see summaryUi in boot(); its setReplayReady() lights up VIEW REPLAY on the live summary.)
// MP: the archive key the server announced (ScReplayAvailable) for the just-won game, or null until it
// arrives. The MP summary's VIEW REPLAY (lit by setReplayReady) opens the viewer with this key. Reset when
// a fresh MP summary mounts so a prior game's recording is never offered on a game that wasn't announced.
let latestReplayKey: string | null = null;

// ── IN-CLIENT replay playback (community-view-plan M4) ──────────────────────────────
// Mount the embedded viewer library (replay-player.ts) in a full-viewport overlay over the DOM
// shell — no page reload, no new tab; ✕/Escape tears it down and control returns to the caller's
// screen via onClosed. The recording + browse index come from the replay origin (ACAO *); add-on
// mission packs from the game's own content base; the art pack + prewarm worker are same-origin
// (staged by build-ts-client.mjs). A RUNNING GAME owns the GL canvas/input, so watching from the
// in-game summary falls back to the standalone viewer tab (openReplayViewer) — in-client playback
// is shell-only by construction.
let replayOverlayUp = false;
function watchReplayInClient(key: string, onClosed?: () => void): void {
  if (key.length === 0) return;
  if (gameHost.runningGame() !== null || replayOverlayUp) {
    openReplayViewer(key);
    onClosed?.();
    return;
  }
  replayOverlayUp = true;
  const root = document.createElement('div');
  root.id = 'dom-replay';
  root.style.cssText = 'position:fixed;inset:0;z-index:30;background:#000';
  document.body.appendChild(root);
  let handle: ReplayHandle | null = null;
  let restartKey: string | null = null; // a browse-panel pick swaps recordings in place
  const teardown = (): void => {
    if (!replayOverlayUp) return;
    replayOverlayUp = false;
    handle?.stop();
    handle = null;
    root.remove();
    const next = restartKey;
    restartKey = null;
    onClosed?.();
    if (next !== null) watchReplayInClient(next, onClosed);
  };
  startReplayViewer({
    mount: root,
    source: { key },
    replayBase: kszReplayViewerUrl,
    packUrlFor: (k) => `${kszPackUrl}/${k}`,
    browse: {
      apiBase: kszReplayViewerUrl,
      currentKey: key,
      onPick: (k) => {
        restartKey = k;
        teardown();
      },
    },
    onExit: teardown,
  })
    .then((h) => {
      if (!replayOverlayUp) {
        h.stop(); // closed before boot finished (defensive; no exit control exists yet)
        return;
      }
      handle = h;
    })
    .catch((e: unknown) => {
      console.warn('[replay] in-client viewer failed; opening the standalone viewer', e);
      teardown();
      openReplayViewer(key);
    });
}

// ?dom Phase 5 (Form 3): the summary mount/unmount closures (bound in boot()),
// reused by the wiUi.openSummaryPreview automation seam to render the REAL view.
let summaryMount: ((info: ObjectivesSummaryInfo, onChoice: (idc: number) => void) => void) | null =
  null;
let summaryUnmount: (() => void) | null = null;
// ?dom Phase 5: the mounted in-game LOAD/SAVE overlay + the MP WAITING overlay, or null
// when down; the load/save/waiting mount closures (bound in boot()) reused by the
// wiUi.open{Load,Save,Waiting}Preview automation seams.
let domLoadSaveView: LoadSaveView | null = null;
let domWaitingView: WaitingView | null = null;
let loadGameMount: ((store: SaveStore, done: (slot: number | null) => void) => void) | null = null;
let saveGameMount: ((store: SaveStore, done: (slot: number | null) => void) => void) | null = null;
let loadSaveUnmount: (() => void) | null = null;
let waitingMount: ((title: string) => () => void) | null = null;
let waitingUnmount: (() => void) | null = null;
// forms.ini-elim (Phase 5): the DEDICATED MP "LAG ALERT" DOM box mount/unmount (bound in
// boot()), reused by the wiUi.openLagBoxPreview automation seam to render the REAL box.
let lagBoxMount:
  ((title: string, body: string, done: (fOk: boolean) => void) => () => void) | null = null;
let lagBoxUnmount: (() => void) | null = null;
// the developer TEST OPTIONS overlay (Alt+T) — the C++ DEV_BUILD TestOptionsForm
// brought back as a DOM panel (test-options-view.ts). null when down; toggle*() below.
let domTestOptionsView: TestOptionsView | null = null;
let testOptionsToggle: (() => void) | null = null;
// ?dom Phase 5 (Form 2): the mounted in-game PAUSE MENU overlay, or null when down.
let domPauseMenuView: PauseMenuView | null = null;
// ?dom Phase 5 (Form 2): the pause-menu mount/unmount closures (bound in boot()),
// reused by the wiUi.openPauseMenuPreview automation seam to render the REAL view.
let pauseMenuMount:
  ((buttons: InGameMenuButton[], onSelect: (idc: number) => void) => void) | null = null;
// ?dom Phase 5 (Form 4): the mounted in-game GAME OPTIONS overlay, or null when down.
let domGameOptionsView: GameOptionsView | null = null;
// ?dom Phase 5 (Form 4): the game-options mount/unmount closures (bound in boot()),
// reused by the wiUi.openGameOptionsPreview automation seam to render the REAL view.
let gameOptionsMount: ((model: GameOptionsModel, done: () => void) => void) | null = null;
let gameOptionsUnmount: (() => void) | null = null;
// (the in-mission E-COM view instance now lives INSIDE the shared createEcomUI factory.)
// ?dom Phase 5 (Form 6): the ecom mount/unmount closures (bound in boot()), reused
// by the wiUi.ecomForceShow automation seam to render the REAL view in isolation.
let ecomMount: ((info: EcomInfo, done: () => void) => void) | null = null;
let ecomUnmount: (() => void) | null = null;
// (the in-mission CUTSCENE view instance now lives INSIDE the shared createCutSceneUI factory.)
// ?dom Phase 5 (kidfCutScene 1047): the cutscene mount/unmount closures (bound in
// boot()), reused by the wiUi.cutsceneForceShow automation seam to render the REAL
// view in isolation.
let cutSceneMount: ((info: CutSceneInfo, done: () => void) => void) | null = null;
let cutSceneUnmount: (() => void) | null = null;
// ?dom Phase 5 (build/order STEP 1): the mounted in-game "Are you sure?" CONFIRMATION
// overlay (kidfAreYouSure), or null when down. The confirm/unmount closures (bound in
// boot()) are reused by the wiUi.confirmForceShow automation seam to render the REAL
// view in isolation.
let confirmMount:
  | ((
      title: string,
      titleColor: string,
      anchor: { cx: number; cy: number } | null,
      onResult: (confirmed: boolean) => void,
    ) => void)
  | null = null;
let confirmUnmount: (() => void) | null = null;
// ?dom Phase 5 (survey I10 — the HtMessageBox family): the in-game MESSAGE BOX view instance now
// lives INSIDE the shared createMessageBoxUI factory (see messageBoxUi in boot()). The mount/unmount
// closures (bound in boot()) are reused by the wiUi.messageBoxForceShow automation seam to render
// the REAL view in isolation.
let messageBoxMount:
  | ((
      title: string,
      message: string,
      variant: 'ok' | 'query',
      whiteBorder: boolean,
      done: (fOk: boolean) => void,
    ) => void)
  | null = null;
let messageBoxUnmount: (() => void) | null = null;
// ?dom (forms.ini-elim): the DEDICATED, modeless MP "LAG ALERT" overlay — a SEPARATE
// MessageBoxView instance/root from the singleton domMessageBox above, so the lag box can be
// up while a resign/observe box shows and is hidden/re-shown for a different lagger.
let domLagBoxView: MessageBoxView | null = null;
// the last OK/Yes/No the message-box preview reported (the wiUi.messageBoxResult seam).
let messageBoxLastResult: boolean | null = null;
// ?dom Phase 5 (build/order STEP 2): the in-game gob CONTEXT MENU (UnitMenu) overlay,
// mounted over the LIVE canvas when BuildUI.PopupMenu fires (a tap on an owned gob).
// The mount/unmount fns (bound in boot()) are reused by the wiUi.unitMenuForceShow
// automation seam to render the REAL view in isolation.
let unitMenuUnmount: (() => void) | null = null;
// ?dom Phase 5 (build/order STEP 3): the in-game UNIT BUILD carousel (MobileUnitBuildForm
// — VTS "ORDER VEHICLE" / HRC "RECRUIT PERSONNEL") overlay, mounted over the LIVE canvas
// when the VTS/HRC Build arm fires. The mount/unmount fns (bound in boot()) are reused by
// the wiUi.buildPanelForceShow automation seam to render the REAL view in isolation.
let buildPanelUnmount: (() => void) | null = null;
// ?dom Phase 5 (build/order STEP 3, FORM C): the in-game STRUCTURE BUILD carousel
// (StructureBuildForm — kidfBuildStructure "ORDER BUILDING") overlay, mounted over the LIVE
// canvas when the HQ Build arm fires. The SAME BuildPanelView (structure variant). The
// mount/unmount fns (bound in boot()) are reused by wiUi.structurePanelForceShow.
let structurePanelUnmount: (() => void) | null = null;
// ?dom Phase 5 (FORM E): the in-game RESEARCH carousel (UpgradeForm — kidfUpgrade "R&D
// TECHNOLOGIES") overlay, mounted over the LIVE canvas when the ResearchGob Research arm fires.
// The SAME BuildPanelView (research variant). The mount/unmount fns (bound in boot()) are
// reused by wiUi.researchPanelForceShow.
let upgradePanelUnmount: (() => void) | null = null;
// ?dom Phase 5: the SECTION-ONLY in-game HELP overlay (the build panels' "?" → the relevant
// help.txt section), mounted OVER the build panel (it stays open underneath). The mount/
// unmount closures (bound in boot()) are reused by the wiUi.helpForceShow automation seam.
let helpMount: ((anchor: string) => void) | null = null;
let helpUnmount: (() => void) | null = null;
// ?dom Phase 5: the FULL-manual in-game HELP overlay (the pause-menu HELP → the whole
// data/help.txt manual, the SAME mountHelpManual renderer as the main-menu HELP), mounted
// modal over the paused canvas. Distinct from the SECTION-only help overlay above
// (helpMount/helpUnmount, now standing up through the shared command-ui factory).
let domHelpManualView: HelpManualOverlayView | null = null;
let helpManualMount: ((done: () => void) => void) | null = null;
let helpManualUnmount: (() => void) | null = null;
// ?dom Phase 5/2: the persistent in-game HUD bar / overlay labels / radar now stand up through
// the SHARED createHudSeams factory (their HudView/SimUIView/MiniMapView instances live INSIDE
// it — see the `hud` seam in boot()). boot() assigns the automation-facing hudForceRender here
// (wiUi.hudForceRender pushes a synthetic HudState into the live HUD view for the smoke).
let hudForceRenderSeam: ((state: HudState) => boolean) | null = null;
// ?dom Phase 5 (Form 5): the per-frame HUD-visibility sync (bound in boot()), called
// from onFrame to tear the HUD down at game over. No-op before boot.
let syncHud: () => void = () => {};
// ?dom Phase 5 (kidfCutScene 1047 — intro-over-black): true while a full-screen DOM
// CUTSCENE overlay is mounted (the opening intro). The intro plays over BLACK, so
// while this holds the game canvas (#screen) is HIDDEN and the HUD bottom bar is torn
// down — no map/menu/minimap shows behind the cutscene panel. Set/cleared by the
// cutscene mount/unmount (boot()). An E-Com does NOT set this (the briefing E-Com sits
// OVER the visible map, matching the canvas where a CutScene form covers the screen but
// an E-Com does not). The cutscene's active state now lives INSIDE the shared createCutSceneUI
// factory (cutSceneUi.isActive()); syncReveal + the HUD `suppressed` gate read it from there.
// ?dom Phase 5: reveal-sync (bound in boot()) — flips #screen + the HUD on/off to match
// the cutscene's active state (cutSceneUi.isActive() — hide during the intro, reveal once it
// clears). No-op before boot. Called via cutSceneUi.onActiveChange + per-frame by syncHud.
let syncReveal: () => void = () => {};

// the persistent stores hydrate in boot() (openPersistentStores: IndexedDB-
// backed + the one-time WASM-IDBFS and legacy-localStorage migrations)
let prefStore: PrefStore;
let saveStore: SaveStore;
let completeStore: CompleteStore;
let completeMgr: CompleteManager;

// ---- the DLC pack stack (gphttp/gppackm/gppim; built in boot()) -------------

let http: FetchHttpService;
let packm: HttpPackManager;
let ppim: HttpPackInfoManager;

// ---- multiplayer globals -----------------------------------------------------

// ?api=<url|local> — the WASM shell's WI_API_URL seam (index.html:134 +
// chooseserverform.cpp:388-394); 'local' = the scripts/local-mp.sh leaderboard.
// Honored ONLY with ?auto, like the shipped shell (index.html:127-135): a bare
// ?api link would point the login POSTs at an attacker-chosen server. Local
// dogfood rides ?auto&api=local. ?beta is ungated, also like the shipped shell
// (index.html:123-125).
const kszLocalServerInfoUrl = 'http://127.0.0.1:8786/api/serverinfo';
const apiParam = urlParams.has('auto') ? urlParams.get('api') : null;
const serverInfoUrl =
  apiParam === null ? undefined : apiParam === 'local' ? kszLocalServerInfoUrl : apiParam;
const envBeta = urlParams.has('beta');
// The HTML/DOM general-UI shell is the ONLY UI (canvas-removal R1): the menu /
// mission-select / options / help / load / multiplayer-lobby / DLC chrome is
// HTML and the canvas is hidden until a game launches; the in-game forms (HUD,
// pause menu, objectives, options, load/save, e-com, cutscene, message/lag
// boxes) are all DOM too. The legacy canvas forms shell + its in-game modals
// have been removed. The session still owns the shared prefs/options/sndm and
// the form managers that back the game's render surface.

const mp: { tra: XTransport | null } = { tra: null }; // gptra's holder
let chatc: ChatOverlay; // HostGetChatController()

// B4b "Add Bot": the wss:// proxy of the server we connected to (ServerInfo.websocket_proxy), captured
// when makeTransport opens the game socket. addBot derives the DO's HTTP base from it (wss→https) and
// POSTs /bots/add — the SAME DO the client is connected to (no external bot-control plane). Optional
// ?botsToken= forwards a deployment token (prod-gated); unset on local/dev (the route is dev-allowed).
const botsToken = urlParams.get('botsToken') ?? undefined;

// HostInitiateWebView (game/sdl/web/hosthelpers.cpp:238-276) — the in-page
// iframe overlay with a Close button. Popup-safe: the Leaderboard/Register/
// Forgot presses arrive through the queued-event pump, OUTSIDE the browser's
// user-activation window, so window.open would be popup-blocked; an in-page
// overlay needs no activation (this is exactly why the shipped shell uses
// one). Styling mirrors the C++ EM_ASM block; position:fixed pins it over the
// canvas (the shipped page reaches the same full-window coverage through its
// own body layout). Safe areas are 0 on web (display has no notch insets).
function initiateWebView(_pszTitle: string, pszUrl: string): void {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '0';
  container.style.top = '0';
  container.style.width = '100%';
  container.style.height = '100%';
  container.style.backgroundColor = 'white';
  document.body.appendChild(container);

  const closeButton = document.createElement('button');
  closeButton.innerText = 'Close';
  closeButton.style.position = 'absolute';
  closeButton.style.right = '8px';
  closeButton.style.top = '4px';
  closeButton.style.width = '70px';
  closeButton.style.height = '24px';
  closeButton.style.fontWeight = 'bold';
  closeButton.style.fontSize = '15px';
  container.appendChild(closeButton);
  closeButton.addEventListener('click', () => {
    container.remove();
  });

  const iframe = document.createElement('iframe');
  iframe.src = pszUrl;
  iframe.style.width = '100%';
  iframe.style.height = '100%';
  container.appendChild(iframe);
}

// HostInitiateAsk/HostGetAskString (game/sdl/web/hosthelpers.cpp:207-230):
// window.prompt after a 250ms audio drain; completion posts askStringEvent
let askString = '';
function initiateAsk(title: string, max: number, def: string): void {
  setTimeout(() => {
    const input = window.prompt(title, def);
    if (input) {
      askString = max > 0 ? input.slice(0, max) : input;
      shell.evm.PostEvent(newEvent(askStringEvent));
    }
  }, 250);
}

// WebAddBot (gameform.cpp:22-32 — was the EM_ASM window.WI_BOT_CONTROL POST to an external bot-control
// plane). B4b repoints it at the DO directly: the bot is a DO-resident SEAT, so the host's "Add Bot"
// button POSTs /bots/add to the SAME DO the client is connected to. The URL derivation (wss→https +
// query) is the pure buildAddBotUrl (bot-control.ts) — unit-tested without a browser; here we just fetch.
function addBot(roomid: number, gameid: number): void {
  // PRIMARY (public, no token): send CsAddBotRequest over our OWN authenticated game socket — the DO adds
  // a bot to the game we created, creator-scoped by the connection, so nothing secret ships in the client.
  // Falls back to the token-gated HTTP /bots/add (automation) only if we're not on a game socket.
  if (mp.tra?.RequestAddBot()) {
    console.log('[addbot] requested (ws)');
    return;
  }
  const url = buildAddBotUrl(gameHost.connectedWebsocketProxy, roomid, gameid, botsToken);
  if (url === null) {
    console.warn('[addbot] not in a game / no server — cannot add a bot');
    return;
  }
  void fetch(url, { method: 'POST' })
    .then((r) => r.json())
    .then((j) => {
      console.log('[addbot] requested (http)', JSON.stringify(j));
    })
    .catch((e: unknown) => {
      console.log('[addbot] error', e instanceof Error ? e.message : String(e));
    });
}

// ---- audio (gsndm over htsfx.pdb; built in boot()) --------------------------

let sndm: SoundMgr | null = null; // null = MemorySoundMgr fallback
let sndDev: WebAudioSoundDevice | null = null; // the CURRENT device (RestoreState re-opens)

// the wi-ios-audio-fix unlock pattern: idempotent, wired to real user gestures
// plus the iOS interruption-recovery events; cheap enough to leave attached
function wireAudioUnlock(): void {
  const unlock = (): void => {
    sndDev?.unlock();
  };
  window.addEventListener('touchend', unlock);
  window.addEventListener('click', unlock);
  window.addEventListener('keydown', unlock);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') unlock();
  });
  window.addEventListener('pageshow', unlock);
  // the canvas swallows pointer events (preventDefault suppresses the click
  // compatibility event on some engines) — unlock straight off the pen too
  canvas.addEventListener('pointerup', unlock);
}

// ---- session orchestration (the game host) ----------------------------------
// The game-session lifecycle, the launch seam (PlayLevel/PlaySavedGame/
// RunSimulation), the pump, and the MP wiring live in GameHost (host/gamehost.ts)
// — node-testable, menu-agnostic. main.ts builds it in boot(), wires the menu's
// flow into it, and drives its pump from the rAF loop below.

let gameHost!: GameHost; // built in boot()

// the session that owns the screen now (running game, else the menu) — present()
// and the pointer/wiTs reaches go through it
const act = (): ShellSession | GameSession => gameHost.currentSession();

// canvas-removal R3: GetModalForm() now returns the engine's PaintLayer slot
// type (no forms.ini Form exists, so it is always null at runtime). The wiAuto
// automation seams below reach into a modal Form's control tree; this casts the
// (always-null) result to the Form-shaped structural type those seams expect, so
// they stay type-clean. They never fire — no layer is ever modal.
interface AutoControl {
  GetFlags(): number;
  GetRect(prc: Rect): void;
  GetText?(): string | null;
  SetChecked?(f: boolean): void;
  Select?(i: number, fNotify: boolean, fEnsureVisible: boolean): void;
  m_szLabel?: string;
}
interface AutoModalForm {
  GetId(): number;
  GetControlPtr(idc: number): AutoControl | null;
  GetRect(prc: Rect): void;
  m_cctl: number;
  m_apctl: ({ GetId(): number } | null)[];
  GetSelectedMission?(): unknown;
  m_pml?: { GetMissionIdentifier(i: unknown): { szLvlFilename: string; packid: PackId } | null };
}
const modalFormAuto = (): AutoModalForm | null =>
  act().mfrmm.GetModalForm() as unknown as AutoModalForm | null;

// ── single-player telemetry (ops dashboard, Phase 4) ─────────────────────────
// Cookieless, fire-and-forget. We POST one event when an SP mission attempt STARTS and one
// when it RESOLVES (win/lose/abort), so the dashboard can show SP outcomes, per-mission win%,
// and the start→completion funnel. Each event carries the level filename, pack id, outcome,
// play seconds, client version, plus a light identity (see spIdentity): the device id — a random
// id persisted in the browser's prefs (origin-scoped, so it identifies one browser+device profile,
// not a unique human; resets on cleared site data / a different browser), the same gszDeviceId the
// MP login uses, so the dashboard can count distinct SP players (≈ browser profiles), not just
// sessions — and, when the player has a non-anonymous account configured, their account name, so
// SP play attributes to the same identity they use in MP. No cookies. MP runs through runSimulation
// (never playMissionLoop), so this is SP-only by construction. Gated to the production host so local
// dev + the headless sweep don't pollute the dataset.
const kszSpEventUrl = 'https://api.warfareincorporated.com/api/spevent';
const spTelemetryOn = (): boolean =>
  typeof window !== 'undefined' &&
  typeof fetch === 'function' &&
  /(^|\.)warfareincorporated\.com$/.test(window.location.hostname);
// Light SP identity, read live from prefs at send time so a mid-session login is reflected:
// `did` = the browser's stored device id (always, when present); `a` = account name only when the
// player is NOT anonymous. Empty/anonymous → the field is omitted. Defensive against an unset shell.
function spIdentity(): Record<string, string> {
  const prefs = shell?.prefs;
  if (!prefs) return {};
  const did = prefs.GetString(kszPrefDeviceId);
  const out: Record<string, string> = did ? { did } : {};
  const user = prefs.GetString(kszPrefUsername).trim();
  if (user && !prefs.GetBool(kfPrefAnonymous)) out.a = user;
  return out;
}

// ── DEBUG / TEST OPTIONS gate ────────────────────────────────────────────────────
// The Test Options panel carries cheats (God mode, Clear Fog). OFF the production host
// (staging / localhost / the headless sweep) it stays freely usable so the team can test;
// ON the production host it is LOCKED unless this browser has been unlocked. Unlock is
// sticky per-browser: visit <prod>/?debug=<token> ONCE and the flag persists in
// localStorage (visit /?debug=off — anything but the token — to clear it). This is an
// OBSCURITY gate, not real security: the client bundle + wiUi are reachable via devtools,
// so a determined user can always cheat in SINGLE-PLAYER. Its only job is to keep the
// casual tap-hold / Alt+T paths from surfacing the panel for ordinary players. God mode is
// already SP-only (fingerhandler enforces !fMultiplayer) and MP blocks it, so this does not
// guard the leaderboard — that would need server-side validation.
const kszDebugUnlockToken = 'spiffdev'; // TUNE: the team's prod unlock phrase
const kszDebugUnlockKey = 'wiDebugUnlocked'; // the sticky localStorage flag
const isProdHost = (): boolean =>
  typeof window !== 'undefined' && /(^|\.)warfareincorporated\.com$/.test(window.location.hostname);
// Apply a ?debug=<token> param ONCE at load: the matching token sets the sticky flag,
// anything else clears it. try/catch — localStorage/window can throw (private mode, node).
(function applyDebugUnlockParam(): void {
  try {
    const v = new URLSearchParams(window.location.search).get('debug');
    if (v === null) return;
    if (v === kszDebugUnlockToken) localStorage.setItem(kszDebugUnlockKey, '1');
    else localStorage.removeItem(kszDebugUnlockKey);
  } catch {
    /* no storage — leave locked */
  }
})();
// The live gate the user-facing entry points (Alt+T, power-meter tap-hold) check before
// opening the panel. Re-read each call so a just-applied unlock takes effect without reload.
const debugPanelUnlocked = (): boolean => {
  if (!isProdHost()) return true; // staging / localhost / sweep: always on
  try {
    return localStorage.getItem(kszDebugUnlockKey) === '1';
  } catch {
    return false;
  }
};

// The stats API base — the native LEADERBOARD screen fetches JSON from here (GET
// /stats/leaderboard.json + /stats/profile.json, served by workers/leaderboard). Same host the C++
// MainMenuForm pointed its leaderboard webview at (Shell.cpp:26-35), now consumed natively.
const kszStatsBase = 'https://api.warfareincorporated.com/stats';

// The current non-anonymous stats username ('' when anonymous / signed out), per
// LoginHandler::StatsUsername (Shell.cpp:26-35 `p`). Scopes the leaderboard's "your rank" pin,
// highlights this player's row, and gates the MY PROFILE fast path. Requires a non-empty TOKEN —
// the same check StatsUsername makes — so a player who SIGNED OUT (token cleared, username kept for
// form pre-fill) correctly reads as signed-out everywhere, instead of the username alone lighting
// the pin / auto-opening their profile as if still signed in.
function currentStatsPlayerName(): string {
  const prefs = shell?.prefs;
  if (!prefs) return '';
  if (prefs.GetBool(kfPrefAnonymous)) return '';
  if (prefs.GetString(kszPrefToken) === '') return '';
  return prefs.GetString(kszPrefUsername).trim();
}

function spEvent(body: Record<string, unknown>): void {
  // Community-pack PLAY beacon (community-content-plan §4.6 / M2): a mission START on a
  // community pack counts a play. Same prod-host gate + identity (did / signed-in token)
  // as the SP telemetry below; built-in + Custom-URL packs are filtered in sendPlayBeacon.
  if (body.t === 'start') {
    const prefs = shell?.prefs;
    sendPlayBeacon(Number(body.pack) >>> 0, {
      enabled: spTelemetryOn(),
      did: prefs?.GetString(kszPrefDeviceId) ?? '',
      token: prefs && !prefs.GetBool(kfPrefAnonymous) ? (prefs.GetString(kszPrefToken) ?? '') : '',
    });
  }
  if (!spTelemetryOn()) return;
  try {
    // keepalive: an 'end' event fired as the player navigates away still flushes.
    void fetch(kszSpEventUrl, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, ...spIdentity(), v: gszVersion }),
    }).catch(() => {
      /* telemetry must never surface to the player */
    });
  } catch {
    /* fetch can throw synchronously on some bad inputs — swallow */
  }
}

// The community-pack REPORT endpoint (wi-api; POST /api/reportpack). A deliberate user
// action from the ADD-ON MISSION PACKS info line (community-content-plan §4.4 / M2) —
// fired anonymously with the browser's device id (or a signed-in token). Fire-and-forget:
// a report must never surface an error to the player, and never blocks the UI.
const kszReportPackUrl = 'https://api.warfareincorporated.com/api/reportpack';
function reportCommunityPack(packId: number): void {
  if (typeof fetch !== 'function') return;
  const prefs = shell?.prefs;
  const did = prefs?.GetString(kszPrefDeviceId) ?? '';
  const token =
    prefs && !prefs.GetBool(kfPrefAnonymous) ? (prefs.GetString(kszPrefToken) ?? '') : '';
  if (did === '' && token === '') return; // the server needs an identity
  try {
    void fetch(kszReportPackUrl, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: packId >>> 0, did, token }),
    }).catch(() => {
      /* never surface to the player */
    });
  } catch {
    /* swallow a synchronous fetch throw */
  }
}

// ── first-touch acquisition (player attribution) ─────────────────────────────
// ONCE per browser, at startup, report how this device (did) first arrived: the referrer HOST
// (document.referrer, host only — we strip the path/query so no URLs/PII leave the browser), any
// utm_* on the landing URL, and the landing path. The server keys on did (INSERT OR IGNORE) so only
// the first touch is kept; joined with play signals it answers "which channels produce real players".
// Gated to the prod host (like spEvent) and guarded by a per-did localStorage marker so a returning
// player doesn't re-post (the empty/internal referrer of a later visit must not shadow first-touch).
const kszAcqEventUrl = 'https://api.warfareincorporated.com/api/acquisition';
function captureAcquisition(): void {
  if (!spTelemetryOn()) return;
  const did = shell?.prefs?.GetString(kszPrefDeviceId);
  if (!did) return;
  let already = false;
  const mark = `wi.acq.${did}`;
  try {
    already = localStorage.getItem(mark) === '1';
  } catch {
    /* storage blocked — fall through, server dedups on did */
  }
  if (already) return;
  let ref = '';
  try {
    if (document.referrer) ref = new URL(document.referrer).hostname;
  } catch {
    /* opaque referrer */
  }
  let us: string | null = null,
    um: string | null = null,
    uc: string | null = null;
  try {
    const q = new URLSearchParams(window.location.search);
    us = q.get('utm_source');
    um = q.get('utm_medium');
    uc = q.get('utm_campaign');
  } catch {
    /* no/!bad query */
  }
  const body: Record<string, unknown> = { did, ref, land: window.location.pathname };
  if (us) body.us = us;
  if (um) body.um = um;
  if (uc) body.uc = uc;
  try {
    void fetch(kszAcqEventUrl, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).catch(() => {
      /* telemetry must never surface to the player */
    });
    try {
      localStorage.setItem(mark, '1');
    } catch {
      /* best-effort; server INSERT OR IGNORE is the backstop */
    }
  } catch {
    /* swallow */
  }
}

// ---- multiplayer (M5b: Lobby::Shell over a real WebSocket) -------------------

// SimpleRequest::Get over the browser fetch (the serverinfo/auth GETs)
async function httpGet(url: string): Promise<{ body: string | null; error?: string }> {
  try {
    const res = await fetch(url);
    if (res.status < 200 || res.status >= 300) {
      return { body: null, error: `Server returned ${res.status}` };
    }
    return { body: await res.text() };
  } catch (e) {
    return { body: null, error: e instanceof Error ? e.message : String(e) };
  }
}

// ---- keyboard state (HostGetCurrentKeyState, game/sdl/host.cpp:738-760) -----
// SDL_GetKeyboardState is a STATE array: DisplayInitComplete disables key
// EVENTS (hosthelpers.cpp:284-290) but SDL keeps the state array current, so
// the shipped web client scrolls the map with held arrow keys via
// InputUIForm::OnTimer's poll (InputUI.cpp:129-148). Track the same five keys
// (the s_akk table) from real keydown/keyup; e.code is the physical key, like
// an SDL scancode.
let dwKeysDown = 0;
const keyBitFromCode: Record<string, number> = {
  ArrowUp: keyBitPageUp,
  ArrowDown: keyBitPageDown,
  ArrowLeft: keyBitDpadLeft,
  ArrowRight: keyBitDpadRight,
  Enter: keyBitDpadButton,
};
window.addEventListener('keydown', (e) => {
  dwKeysDown |= keyBitFromCode[e.code] ?? 0;
});
window.addEventListener('keyup', (e) => {
  dwKeysDown &= ~(keyBitFromCode[e.code] ?? 0);
});
// Developer debug overlays (render/debugoverlay.ts), ports of the C++ DEV_BUILD
// Test Options DRAW_PATHS/DRAW_LINES: Alt+P toggles unit navigation paths, Alt+L
// toggles unit/tower target lines. Read e.code (the physical key) so it works on
// macOS where Option produces special characters; ignore when an input is focused
// so typing in the chat box never toggles.
window.addEventListener('keydown', (e) => {
  if (!e.altKey || e.ctrlKey || e.metaKey) return;
  const el = document.activeElement;
  if (
    el &&
    (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || (el as HTMLElement).isContentEditable)
  )
    return;
  if (e.code === 'KeyP') {
    e.preventDefault();
    toggleDebugPaths();
  } else if (e.code === 'KeyL') {
    e.preventDefault();
    toggleDebugLines();
  }
  // Alt+T — open/close the developer TEST OPTIONS panel (the C++ DEV_BUILD
  // TestOptionsForm; test-options-view.ts), the home of the Draw Paths / Draw
  // Target Lines / Show FPS / God Mode toggles + Clear Fog.
  else if (e.code === 'KeyT') {
    e.preventDefault();
    if (debugPanelUnlocked()) testOptionsToggle?.();
  }
});
// lost-keyup recovery (focus moved away mid-hold) — SDL gets this for free
window.addEventListener('blur', () => {
  dwKeysDown = 0;
});
const hostGetCurrentKeyState = (keyBits: number): number => dwKeysDown & keyBits;

// ---- the pump (rAF over the scripted 1/100s tick clock) ---------------------

const kctMaxPerFrame = 25; // rAF cap after a long GC pause (overage drops)

const perf = {
  frames: 0,
  pumpMs: 0,
  blitMs: 0,
  worstPumpMs: 0,
  fps: 0,
  _emaPump: 0,
  _emaBlit: 0,
  _winStart: 0,
  _winFrames: 0,
};

// ── app-lifecycle correlation for the MP "kicked to menu" reports (issue #11) ──
// The notification-panel swipe / rotate-to-portrait / resize cases all HIDE or
// RESIZE the page. Stamp the last hide/show/resize so a multiplayer game end can
// report how recently one of those fired — emitted once per MP game end
// (mp_session_end) so the ops dashboard can tell a lifecycle-triggered teardown
// apart from a plain network drop. Correlation only; changes no game behavior.
let lastHiddenAtMs = 0;
let lastVisibleAtMs = 0;
let lastResizeAtMs = 0;
document.addEventListener('visibilitychange', () => {
  if (document.hidden) lastHiddenAtMs = performance.now();
  else lastVisibleAtMs = performance.now();
});
// inGame edge tracking: emit mp_session_end the frame a MULTIPLAYER game ends
// (inGame true→false, having seen it live as MP). Driven each frame from both the
// rAF loop and the hidden fallback pump so an end that happens while backgrounded
// (the bug #11 case) is still reported.
let prevInGame = false;
let sawMpThisGame = false;
function noteGameLifecycle(): void {
  const nowIn = gameHost.inGame();
  if (nowIn && !gameHost.inSinglePlayerGame()) sawMpThisGame = true;
  // Frame-cadence perf sample spans every game (MP + SP): open the accumulator on the
  // false->true edge, close+emit it on true->false (perf-report.ts / 'perf' telemetry).
  if (!prevInGame && nowIn) perfGameStart(gameHost.inSinglePlayerGame() ? 'sp' : 'mp');
  if (prevInGame && !nowIn) {
    if (sawMpThisGame) {
      const now = performance.now();
      track('mp_session_end', {
        msSinceHidden: lastHiddenAtMs > 0 ? Math.round(now - lastHiddenAtMs) : -1,
        msSinceVisible: lastVisibleAtMs > 0 ? Math.round(now - lastVisibleAtMs) : -1,
        msSinceResize: lastResizeAtMs > 0 ? Math.round(now - lastResizeAtMs) : -1,
        hidden: document.hidden,
      });
    }
    perfGameEnd();
  }
  if (!nowIn) sawMpThisGame = false;
  prevInGame = nowIn;
}

// ---- window resize (host.cpp:44-71) ------------------------------------------
// The native callback only sets a flag; the work runs from the frame loop —
// the same deferral the C++ uses (coalesces resize storms; never reenters
// game code from the event callback).
let fPendingResize = false;
window.addEventListener('resize', () => {
  fPendingResize = true;
  lastResizeAtMs = performance.now();
});

function servicePendingResize(): void {
  if (!fPendingResize) return;
  lastPresentSerial = -1; // the canvas resize clears the drawing buffer — force a present
  fPendingResize = false;
  const siz = surfaceSize(); // HostHelpers::GetSurfaceProperties
  if (siz.cx === screen.cx && siz.cy === screen.cy) return;
  screen.cx = siz.cx;
  screen.cy = siz.cy;
  applyCanvasSize();
  // gpdisp->SetSize + ggame.OnDisplayResize + RedrawEverything — the C++ has
  // ONE display/form stack; here the dormant shell resizes too so the menu is
  // right when the game ends
  shell.resize(screen.cx, screen.cy);
  gameHost.runningGame()?.resize(screen.cx, screen.cy);
}

let lastT = -1;
let msLastRaf = 0; // rAF liveness — the fallback pump's stall detector

function onFrame(now: number): void {
  // Wall-clock interval since the previous rAF — the true displayed cadence. Feeds the
  // projectile-glide governor (issue #11): a GPU- or CPU-bound combat choke stretches this,
  // whereas the pump CPU time it used to watch stays low on a fill-rate-bound GPU. Render-only.
  const dtFrame = msLastRaf > 0 ? now - msLastRaf : 0;
  msLastRaf = now;
  // Real browsers never deliver rAF to hidden tabs; environments that still
  // do (headless automation, fake-hidden tests) defer to the fallback pump
  // below so the hidden clock has ONE owner — behavior-neutral in production.
  if (document.hidden) {
    requestAnimationFrame(onFrame);
    return;
  }
  noteFrameIntervalMs(dtFrame); // spikes/first-frame are filtered inside
  if (gameHost.inGame()) perfNoteFrame(dtFrame); // per-game frame-cadence histogram (perf-report.ts)
  servicePendingResize(); // HostPollPendingResize (host.cpp:57-71)
  const t = Math.floor(now / 10);
  if (lastT < 0) lastT = t;
  let n = t - lastT;
  lastT = t;
  if (n > kctMaxPerFrame) n = kctMaxPerFrame;
  // Present EVERY composited frame, ticks or not (display.cpp:291-307 — the
  // C++ presents unconditionally per frame). With preserveDrawingBuffer:false
  // a browser may composite a CLEARED buffer on any rAF that didn't draw:
  // Safari does exactly that (spec-strict), so on a 120Hz display the n==0
  // alternate frames flashed black and a settled screen went permanently
  // black. The present is a single ~0.03ms blit from the persistent back
  // texture — unconditional is both correct and cheap.
  if (n <= 0) {
    // A no-tick frame still must run syncHud(): a game that just ended (esp. while
    // the tab was hidden and the clock was driven by the fallback pump) would
    // otherwise leave the HUD/minimap mounted over the menu until the next tick
    // boundary — the stale-radar-on-the-menu leak in issue #11.
    syncHud();
    noteGameLifecycle();
    present();
    requestAnimationFrame(onFrame);
    return;
  }
  {
    const t0 = performance.now();
    gameHost.pump(n); // advance the running game, else tick the menu
    syncHud(); // ?dom: tear the persistent HUD down at game over (Phase 5, Form 5)
    noteGameLifecycle();
    const t1 = performance.now();
    present();
    const t2 = performance.now();
    const pumpMs = t1 - t0,
      blitMs = t2 - t1;
    perf.frames++;
    perf._emaPump = perf._emaPump * 0.95 + pumpMs * 0.05;
    perf._emaBlit = perf._emaBlit * 0.95 + blitMs * 0.05;
    perf.pumpMs = perf._emaPump;
    perf.blitMs = perf._emaBlit;
    if (pumpMs > perf.worstPumpMs) perf.worstPumpMs = pumpMs;
    perfNotePaint(pumpMs, blitMs); // per-game peak pump/blit (perf-report.ts)
    perf._winFrames++;
    if (now - perf._winStart >= 1000) {
      perf.fps = (perf._winFrames * 1000) / (now - perf._winStart);
      perf._winStart = now;
      perf._winFrames = 0;
      // ~1/sec: sample the live gob count (army size) for the perf report's peak
      perfNoteGobs(gameHost.runningGame()?.sim.ctx.gobm.GetGobCount() ?? 0);
      if (fShowStats) {
        status(
          `${perf.fps.toFixed(0)} fps  pump ${perf.pumpMs.toFixed(2)}ms  ` +
            `blit ${perf.blitMs.toFixed(2)}ms  worst ${perf.worstPumpMs.toFixed(1)}ms`,
        );
      }
    }
  }
  requestAnimationFrame(onFrame);
}

// ---- the background-tab fallback pump ----------------------------------------
// rAF does not fire in hidden tabs; the shipped client LIMPS through them: the
// emterpreter loop keeps running on throttled setTimeout (~1Hz) and the C++
// paces by HostGetMillisecondCount, so the sim consumes the full hidden
// wall-clock and the MP lockstep stays serviced. Mirror that for MP: a 250ms
// interval takes the clock over whenever the tab is hidden (or rAF stalls
// >500ms for any other reason) and consumes the FULL elapsed time in 1s
// chunks — no kctMaxPerFrame drop. A single wake is bounded at 60s against
// pathological timer throttling (Chrome's intensive throttling fires ~1/min
// after 5 hidden minutes — the WASM client crawls the same way there). The
// blit is skipped while hidden (nothing is visible; the paint events still
// run inside the pump).
//
// SINGLE-PLAYER is the deliberate departure from prod (issue #110): a hidden
// SP mission PAUSES — a closed iPad cover / backgrounded PWA must not keep
// simming (battery) or let countdowns creep. The pause slides the clock
// forward without consuming it, so nothing catches up on return-to-visible
// (the rAF loop's kctMaxPerFrame overage-drop bounds any residue at 250ms).
// The condition re-evaluates every wake, so an MP game that begins while
// hidden (a lobby start) picks the pump straight back up; the hidden MENU
// keeps its old ticking behavior (the lobby processes events from its tick).
const kctHiddenChunk = 100; // 1s of ticks per pump() call
const kctMaxHiddenCatchup = 100 * 60; // bound one wake's catch-up
setInterval(() => {
  if (lastT < 0) return; // the rAF loop owns the clock's birth
  const now = performance.now();
  if (!document.hidden && now - msLastRaf < 500) return; // rAF is alive
  if (document.hidden && gameHost.inSinglePlayerGame()) {
    // issue #110 — SP pause
    lastT = Math.floor(now / 10);
    return;
  }
  servicePendingResize();
  // Tear down in-game overlays here too: a multiplayer game can END while the tab
  // is hidden (a mid-game disconnect the reconnect couldn't rescue), and the rAF
  // loop that normally runs syncHud() is not firing. Without this the HUD/minimap
  // stays mounted and shows over the menu on return-to-foreground (issue #11).
  syncHud();
  noteGameLifecycle();
  const t = Math.floor(now / 10);
  let n = t - lastT;
  if (n <= 0) return;
  if (n > kctMaxHiddenCatchup) {
    lastT = t - kctMaxHiddenCatchup;
    n = kctMaxHiddenCatchup;
  }
  while (n > 0) {
    const c = Math.min(n, kctHiddenChunk);
    gameHost.pump(c);
    lastT += c;
    n -= c;
  }
  if (!document.hidden) present();
}, 250);

const fShowStats = urlParams.has('stats');

// ---- pointer input -----------------------------------------------------------

function canvasPos(e: PointerEvent): { x: number; y: number } {
  const r = canvas.getBoundingClientRect();
  let x = Math.floor(((e.clientX - r.left) * screen.cx) / r.width);
  let y = Math.floor(((e.clientY - r.top) * screen.cy) / r.height);
  if (x < 0) x = 0;
  else if (x >= screen.cx) x = screen.cx - 1;
  if (y < 0) y = 0;
  else if (y >= screen.cy) y = screen.cy - 1;
  return { x, y };
}

// True when the device's PRIMARY pointer is touch (phone / tablet) — those
// default to the touch FingerHandler instead of the web build's forced mouse
// mode (game.cpp:296-301 / prefs.ts ApplyPreferences). Desktops and touchscreen
// laptops report a fine primary pointer (mouse/trackpad) and stay in mouse mode;
// an iPad with a trackpad attached likewise becomes fine → mouse mode. iPadOS
// (which UA-masquerades as Macintosh) reports a coarse primary pointer when no
// pointing device is attached, so the pointer media query handles it directly.
function prefersTouchControls(): boolean {
  try {
    return window.matchMedia('(pointer: coarse)').matches;
  } catch {
    // pre-pointer-media-query browser (not expected on a WebGL2 target): fall
    // back to raw touch capability.
    return (navigator.maxTouchPoints ?? 0) > 0 || 'ontouchstart' in window;
  }
}

// host.cpp:284-353 — the shipped host tracks TWO touch fingers (gtouches[2]):
// slot 0 raises pen events, slot 1 raises pen2 events, both with kfEvtFinger
// (the FingerHandler's two-finger drag-select consumes pen2 when touch is the
// active UI). Mouse/pen pointers ride the SDL_MOUSE* path: single-pointer,
// no kfEvtFinger, right-button/shift → kfEvtRightButton (host.cpp:358-385).
// On prod-web touch the browser ALSO synthesizes mouse events, but host.cpp
// eats them while finger events are pending (PeekSdlEvent) — pointer events
// arrive unified here, so there is nothing to dedupe.
const touchSlots: (number | null)[] = [null, null]; // gtouches[2]
const lastMove = [
  { x: -1, y: -1 },
  { x: -1, y: -1 },
]; // per-slot move dedupe
let penPointerId: number | null = null; // the mouse/pen pointer
let cPen2Events = 0; // host-boundary pen2 production (the e2e observability)

const aPenDown = [penDownEvent, penDownEvent2];
const aPenMove = [penMoveEvent, penMoveEvent2];
const aPenUp = [penUpEvent, penUpEvent2];

// Stop the OS/browser from taking over zoom on any screen (gameplay included).
// iOS Safari ignores the viewport's user-scalable=no, and even a
// touch-action:none canvas still double-tap-zooms on many iOS versions, so we
// suppress it explicitly: cancel the pinch gesture* events and swallow the
// second of any two taps landing within the OS double-tap window. The game's
// own input rides pointer events, which are unaffected by these preventDefaults.
function suppressOsZoom(): void {
  // pinch-zoom (iOS Safari emits the non-standard gesture* events)
  for (const t of ['gesturestart', 'gesturechange', 'gestureend']) {
    document.addEventListener(t, (e) => e.preventDefault(), { passive: false });
  }
  // double-tap-to-zoom: cancel the 2nd tap if it lands <=300ms after the 1st.
  // BUT skip interactive DOM controls: preventDefault() on touchend cancels the
  // browser's synthesized click, so on a fast 2nd tap a build-panel row (which
  // orders a unit on click) would lose its order — rapid tapping then queues
  // fewer units than taps. Those controls suppress their own double-tap-zoom via
  // touch-action:manipulation (ensureA11yStyles, chrome.ts), so it's safe to let
  // their clicks through here while the canvas/page keep the zoom guard.
  let lastTouchEnd = 0;
  document.addEventListener(
    'touchend',
    (e) => {
      const now = performance.now();
      const t = e.target;
      const onControl =
        t instanceof Element &&
        t.closest(
          'button, a, input, select, textarea, label, ' +
            '[role="button"], [role="option"], [role="slider"], [role="radio"]',
        ) !== null;
      if (!onControl && now - lastTouchEnd <= 300) e.preventDefault();
      lastTouchEnd = now;
    },
    { passive: false },
  );
  // trackpad/desktop double-click zoom (harmless extra coverage)
  document.addEventListener('dblclick', (e) => e.preventDefault(), { passive: false });
}

function wirePointerEvents(): void {
  canvas.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    if (e.pointerType === 'touch') {
      // SDL_FINGERDOWN (host.cpp:284-300): first free slot wins; a third
      // finger is dropped at the host boundary like the C++'s two slots
      for (let i = 0; i < 2; i++) {
        if (touchSlots[i] === null) {
          touchSlots[i] = e.pointerId;
          canvas.setPointerCapture(e.pointerId);
          const { x, y } = canvasPos(e);
          lastMove[i].x = x;
          lastMove[i].y = y;
          if (i === 1) cPen2Events++;
          act().injectPen(aPenDown[i], x, y, kfEvtFinger);
          break;
        }
      }
      return;
    }
    if (penPointerId !== null) return;
    penPointerId = e.pointerId;
    canvas.setPointerCapture(e.pointerId);
    const { x, y } = canvasPos(e);
    lastMove[0].x = x;
    lastMove[0].y = y;
    // right button (or shift) = kfEvtRightButton — host.cpp:358-366 (the
    // stylus handler's map drag); contextmenu is suppressed below
    const ff = e.button === 2 || e.shiftKey ? kfEvtRightButton : 0;
    act().injectPen(penDownEvent, x, y, ff);
  });
  canvas.addEventListener('pointermove', (e) => {
    if (e.pointerType === 'touch') {
      // SDL_FINGERMOTION (host.cpp:329-353): untracked fingers are eaten
      const i = touchSlots.indexOf(e.pointerId);
      if (i < 0) return;
      const { x, y } = canvasPos(e);
      if (x === lastMove[i].x && y === lastMove[i].y) return;
      lastMove[i].x = x;
      lastMove[i].y = y;
      if (i === 1) cPen2Events++;
      act().injectPen(aPenMove[i], x, y, kfEvtFinger);
      return;
    }
    // SDL_MOUSEMOTION -> penMoveEvent whether or not a button is down
    // (game/sdl/host.cpp:378-386) — hover highlights ride move events
    if (penPointerId !== null && e.pointerId !== penPointerId) return;
    if (penPointerId === null && !e.isPrimary) return;
    const { x, y } = canvasPos(e);
    if (x === lastMove[0].x && y === lastMove[0].y) return;
    lastMove[0].x = x;
    lastMove[0].y = y;
    // SDL_BUTTON_RMASK || KMOD_SHIFT (host.cpp:378-382)
    const ff = (e.buttons & 2) !== 0 || e.shiftKey ? kfEvtRightButton : 0;
    act().injectPen(penMoveEvent, x, y, ff);
  });
  const up = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') {
      // SDL_FINGERUP (host.cpp:308-327); pointercancel doubles as the
      // CheckTouchTracking lost-up recovery (host.cpp:218-260)
      const i = touchSlots.indexOf(e.pointerId);
      if (i < 0) return;
      touchSlots[i] = null;
      const { x, y } = canvasPos(e);
      if (i === 1) cPen2Events++;
      act().injectPen(aPenUp[i], x, y, kfEvtFinger);
      return;
    }
    if (e.pointerId !== penPointerId) return;
    penPointerId = null;
    const { x, y } = canvasPos(e);
    // host.cpp:368-376
    const ff = e.button === 2 || e.shiftKey ? kfEvtRightButton : 0;
    act().injectPen(penUpEvent, x, y, ff);
  };
  canvas.addEventListener('pointerup', up);
  canvas.addEventListener('pointercancel', up);
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  // Two-finger trackpad scroll (and mouse wheel) pans the live map — the
  // browser analogue of SDL_MOUSEWHEEL. The menu/lobby owns the canvas
  // off-game, so this only acts while a game is running.
  canvas.addEventListener(
    'wheel',
    (e) => {
      const gs = gameHost.inGame() ? gameHost.runningGame() : null;
      if (gs == null) return;
      e.preventDefault();
      // Normalize the delta to CSS pixels (most trackpads already report pixels;
      // line/page modes are mouse-wheel fallbacks), then through the same CSS->game
      // scale canvasPos() applies. deltaY is positive-down, so the view moves WITH
      // the gesture — the map scrolls like a scrollbar (OS natural-scroll honored).
      const r = canvas.getBoundingClientRect();
      let dx = e.deltaX,
        dy = e.deltaY;
      if (e.deltaMode === 1) {
        dx *= 16;
        dy *= 16;
      } // DOM_DELTA_LINE
      else if (e.deltaMode === 2) {
        dx *= r.width;
        dy *= r.height;
      } // DOM_DELTA_PAGE
      gs.panViewByPixels((dx * screen.cx) / r.width, (dy * screen.cy) / r.height);
    },
    { passive: false },
  );
}

// Refresh-to-resume, the unload half. The per-tick autosave only re-writes the
// resume slot every ~500ms (gamesession.ts:1570), and a browser refresh/close
// does NOT post the canvas appStopEvent (the C++ suspend hook, game.cpp:1819) —
// so without this up to ~500ms of play would be lost on resume. On a tab-hide or
// unload, synchronously flush the running game's resume slot to its LATEST state.
// SaveReinitializeGame is single-player-gated and a no-op with no game in progress
// (gamesession.ts:1465-1471), so this fires only for a live SP mission. The store
// write-through is fire-and-forget (stores.ts) — pagehide is the most reliable
// last-gasp hook (bfcache/mobile-safe), with visibilitychange→hidden covering the
// tab-background case the OS may freeze before pagehide.
function wireResumeFlush(): void {
  const flush = (): void => {
    gameHost.runningGame()?.SaveReinitializeGame();
  };
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) flush();
  });
}

// The closed-cover / backgrounded-tab silence (issue #110), the audio half:
// iOS keeps a hidden page's AudioContext running (audibly) well past the cover
// closing, and its own auto-suspend is late and inconsistent — the reported
// bug. Tear the context down on hide whenever no MP game is running (an MP
// game keeps audio so a backgrounded desktop tab still alerts); the SP sim
// itself pauses in the fallback pump above, so no new plays fight the
// teardown. CLOSED rather than suspended because iOS returns a page-suspended
// context as a zombie ('running' but silent) after the PWA foregrounds — the
// always-attached wireAudioUnlock listeners (visibilitychange→visible,
// pageshow, gestures) re-open a fresh context on return instead, the same
// path that opens it at app launch.
//
// pagehide covers the PWA-close/navigation path directly (the same last-gasp
// reasoning as wireResumeFlush): closing the app mid-SFX otherwise tears the
// context down with a source still sounding — the brief fade-out tone.
function wireHiddenAudioTeardown(): void {
  const teardown = (): void => {
    if (!gameHost.mpStats.live) sndDev?.teardownHidden();
  };
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) teardown();
  });
  window.addEventListener('pagehide', teardown);
}

// ---- boot --------------------------------------------------------------------

const iniFromPack = (name: string): IniReader => {
  const pb = pakr.MapFile(name);
  if (pb === null) throw new Error(`${name} not in pack`);
  return new IniReader(parseCini(WebBuffer.from(pb)) as IniSection[]);
};

const nextPaint = (): Promise<void> =>
  new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));

// Resolve a pack name to its fetch URL, appending the per-build content-version
// stamp (?v=<hash>) when one was injected (window.__PACKV; see build-ts-client.mjs).
// The stamp lets pages/_headers mark the packs immutable so the edge caches them —
// loads stop re-hitting the Pages asset origin every time. Falls back to the bare
// name for unstamped dev / automation servers.
function packUrl(name: string): string {
  const v = window.__PACKV?.[name];
  return v ? `${name}?v=${v}` : name;
}

// Fetch a data pack with bounded retry+backoff. The packs are the critical boot
// payload, and a single transient origin/CDN hiccup (a bare 404/5xx) must NOT
// black-screen the whole game — boot() used to throw straight out on the first
// non-OK response. Returns the bytes, or null if every attempt failed (the caller
// decides whether that pack is fatal: art = yes, sfx = audio-disabled).
//
// onProgress (when supplied) reports download progress by streaming the body:
// (loaded, total) bytes as chunks arrive, so boot() can show a percentage next
// to the "loading game data…" line — the art pack is multi-MB and dominates the
// cold (uncached) load, and a frozen status line otherwise looks hung. total is
// 0 when the server omits Content-Length (the caller falls back to a byte count).
// Each attempt re-reports from 0, so the callback is called once more with the
// final (loaded, loaded) on completion to settle the combined tally.
async function fetchPackBytes(
  name: string,
  onProgress?: (loaded: number, total: number) => void,
): Promise<Uint8Array | null> {
  const kAttempts = 4;
  for (let attempt = 0; attempt < kAttempts; attempt++) {
    if (attempt > 0) {
      status(`loading game data… (retry ${attempt})`);
      await new Promise((r) => setTimeout(r, 250 * attempt)); // 250/500/750 ms
    }
    try {
      const resp = await fetch(packUrl(name));
      if (!resp.ok) continue;
      // No caller-supplied progress (or no streamable body — some polyfills):
      // the original one-shot path.
      if (!onProgress || !resp.body) return new Uint8Array(await resp.arrayBuffer());
      const total = Number(resp.headers.get('content-length')) || 0;
      const reader = resp.body.getReader();
      const chunks: Uint8Array[] = [];
      let loaded = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        loaded += value.length;
        onProgress(loaded, total);
      }
      // Settle: a cached/empty-Content-Length body may never have reported a
      // total, so report the final size as its own total (loaded === total).
      onProgress(loaded, loaded);
      const out = new Uint8Array(loaded);
      let off = 0;
      for (const c of chunks) {
        out.set(c, off);
        off += c.length;
      }
      return out;
    } catch {
      /* network error — fall through and retry */
    }
  }
  return null;
}

async function boot(): Promise<void> {
  // Now that the shell/prefs exist, telemetry events carry the live SP identity (did/account).
  setTelemetryIdentity(spIdentity);
  warmWiFonts(); // warm the self-hosted WI web fonts so the DOM UI lays out in the real faces
  // If a view first paints + measures (buttonWidthU) BEFORE the WI face decodes (cold cache /
  // slow font fetch), it sizes against the narrower fallback and font-display:swap then repaints
  // the wider face into a fixed-width frame. Re-layout once the faces are ready so the measured
  // widths match the rendered face (the views re-render on resize). Self-heals an otherwise
  // resize-only correction; no-op where document.fonts is absent.
  globalThis.document?.fonts?.ready
    .then(() => {
      window.dispatchEvent(new Event('resize'));
    })
    .catch(() => {
      /* fallback stays */
    });
  status('loading game data…');
  // Combined download progress across both packs, drawn as the bar that fills
  // from the end of the status text to the right edge — otherwise the line sits
  // frozen through the multi-MB cold load and looks hung. Each pack reports its
  // latest (loaded, total); we sum and refill on every chunk. The bar stays
  // hidden until every in-flight pack has a Content-Length (no total → no
  // meaningful fill fraction); in practice the production packs always do.
  const packProgress = new Map<string, { loaded: number; total: number }>();
  const onPackProgress =
    (name: string) =>
    (loaded: number, total: number): void => {
      packProgress.set(name, { loaded, total });
      let loadedSum = 0;
      let totalSum = 0;
      let haveAllTotals = true;
      for (const p of packProgress.values()) {
        loadedSum += p.loaded;
        if (p.total > 0) totalSum += p.total;
        else haveAllTotals = false;
      }
      if (haveAllTotals && totalSum > 0) progress(loadedSum / totalSum);
    };
  const [packBytes, sfxBytes] = await Promise.all([
    fetchPackBytes(kszArtPack, onPackProgress(kszArtPack)), // the index.html:216-241 selection (824 default desktop)
    fetchPackBytes('htsfx.pdb', onPackProgress('htsfx.pdb')),
  ]);
  if (!packBytes) throw new Error(`pack fetch failed: ${kszArtPack}`);

  progress(null); // download done — retire the bar before the decode phase
  status('decoding art…');
  await nextPaint();

  const rdr = new MemPdbReader();
  if (!rdr.Open(packBytes)) throw new Error('MemPdbReader.Open failed');
  pakr = new PackFileReader();
  if (!pakr.Push(null, kszArtPack, rdr)) throw new Error('PackFileReader.Push failed');
  tam = new TexAtlasMgr();
  if (!tam.Init(pakr, decodePng)) throw new Error('TexAtlasMgr.Init failed');
  coords = new CoordMap(gameArtSize); // gcxTile rides the selected pack
  if (gameArtDepth === 24) anir = anirJsonFromPack(pakr); // ?2432: the pack's own sim anim data
  // canvas-removal R2: the shipped forms.ini is no longer read at runtime (the in-game UI is
  // DOM-sourced; the build forms Init from the in-code BuildUI.formsGeom). It is no longer
  // packed into the shipped pdb either, so there is nothing to load.
  gameIni = iniFromPack('game.ini');

  // persistent state: IndexedDB-backed stores with the one-time migrations —
  // the WASM client's IDBFS (/persistent: saves, prefs.json, Completes/) and
  // the legacy TS localStorage documents (newest-wins saves, existing-TS-wins
  // prefs, union completes; run-once marker after success)
  ({ prefStore, saveStore, completeStore } = await openPersistentStores());

  completeMgr = new CompleteManager(completeStore, pakr);
  completeMgr.Init();

  // gphttp/gppackm/gppim (game.cpp's M5 content globals): the DLC pack cache
  // hydrates from IndexedDB BEFORE InitFromInstalled reads it (the C++ API is
  // synchronous over the cache dir); pack infos cache the same way
  http = new FetchHttpService();
  const packStore = await IdbPackStore.open('wi.ts.packs');
  packm = new HttpPackManager(http, packStore);
  packm.InitFromInstalled();
  const infoStore = await IdbPackStore.open('wi.ts.packinfos');
  ppim = new HttpPackInfoManager(http, infoStore);

  // (the chat overlay — HostGetChatController() — is constructed below, once objBtnArt exists)

  // gsndm over htsfx.pdb (game.cpp:445 gsndm.Init()) — the device factory
  // hands out WebAudioSoundDevices (RestoreState re-opens after a
  // SaveStateAndClear dropped one); unlock listeners resume the CURRENT one
  if (sfxBytes !== null) {
    const rdrSfx = new MemPdbReader();
    const pakrSfx = new PackFileReader();
    if (rdrSfx.Open(sfxBytes) && pakrSfx.Push(null, 'htsfx.pdb', rdrSfx)) {
      const m = new SoundMgr(pakrSfx, {
        openSoundDevice: () => (sndDev = new WebAudioSoundDevice()),
      });
      if (m.Init()) {
        sndm = m;
        wireAudioUnlock();
      } else {
        console.warn('SoundMgr.Init failed — audio disabled');
      }
    }
  } else {
    console.warn('htsfx.pdb not served — audio disabled');
  }

  // (Phase 5): the in-game OBJECTIVES briefing renders as a DOM overlay over
  // the live canvas. The host threads this seam
  // into every GameSession; on OK the overlay unmounts and calls done() to
  // resume. The faithful chrome (backdrop + 9-slice buttons) is the SAME pack art
  // the DOM menu uses. The overlay sits in #dom-objectives, above #screen (z=20).
  const objBgArt = extractAtlasArt(tam, 'titlescreenbkgd.png');
  const objBtnArt = extractButtonArt(tam);
  // HostGetChatController() — the ONE full-screen WI-themed chat overlay
  // (dom/chat-overlay.ts), shared by every MP chat surface (the DOM lobby's room /
  // create-game / staging screens AND in-game), replacing the legacy white
  // web/chat.ts overlay. Built here so the 9-slice button art (objBtnArt) is ready;
  // shell.sndm is reached lazily (assigned later) and only on first Show.
  chatc = new ChatOverlay({
    buttonArt: () => objBtnArt ?? undefined,
    playSfx: (sfx: number) => {
      shell.sndm.PlaySfx(sfx);
    },
  });
  // (Phase 5, Form 1) the in-game OBJECTIVES BRIEFING overlay now stands up through the SHARED
  // createObjectivesUI factory (dom/ingame/objectives-ui.ts) — the EXACT mountObjectives logic
  // (the SAME #dom-objectives root + titlescreen backdrop + button art) the editor's Play host
  // also uses. domShowObjectives is wired into GameHostDeps below; wiUi.closeObjectives drives the
  // OK button by DOM id (objectivesClose), so it's unaffected.
  const objectivesUi = createObjectivesUI({
    tam,
    objBtnArt,
    playSfx: (sfx: number) => {
      shell.sndm.PlaySfx(sfx);
    },
  });

  // (Phase 5, Form 3): the WIN / LOSE RESULTS SUMMARY now stands up through the SHARED
  // createSummaryUI factory (dom/ingame/summary-ui.ts) — the EXACT mountSummary logic (the SAME
  // #dom-objectives-summary root + the win/lose ObjectivesView) the editor's Play host also uses.
  // ShowObjectives calls this (via env.domObjectivesSummary) for the win/lose summary forms; the
  // briefing (ksoObjectives) stays on the Form-1 path above. The MP-replay bits (main.ts-specific)
  // are threaded as deps: onMpShow clears latestReplayKey on a fresh MP summary; replayKey reads the
  // announced key; onWatchReplay opens the standalone viewer. setReplayReady (used by
  // onReplayAvailable below) lights up the VIEW REPLAY on the LIVE summary. The returned mount/
  // teardown are assigned to the summaryMount/summaryUnmount let vars so the wiUi.openSummaryPreview
  // automation seam + the GameHostDeps wiring stay unchanged.
  const summaryUi = createSummaryUI({
    tam,
    objBtnArt,
    playSfx: (sfx: number) => {
      shell.sndm.PlaySfx(sfx);
    },
    // in-client when the shell owns the screen; a still-running game → the standalone viewer tab
    onWatchReplay: (key) => watchReplayInClient(key),
    replayKey: () => latestReplayKey,
    onMpShow: () => {
      latestReplayKey = null;
    },
  });
  summaryMount = summaryUi.domObjectivesSummary;
  summaryUnmount = summaryUi.teardown;

  // (Phase 5): the in-game LOAD / SAVE slot-list overlay (kidfLoadGame/kidfSaveGame)
  // over the live canvas (z=20). The HOST enumerates the
  // slots via the SAME saves-io the shell LOAD SAVED GAME uses, then reports the picked slot
  // (or null = BACK/Cancel) back to PickLoad/PickSaveGameStream, which opens the stream /
  // writes the save. Re-entrant: the load empty-slot re-show loop re-mounts.
  const loadSaveRoot = document.createElement('div');
  loadSaveRoot.id = 'dom-loadsave';
  loadSaveRoot.style.display = 'none';
  document.body.appendChild(loadSaveRoot);
  const unmountLoadSave = (): void => {
    loadSaveRoot.style.display = 'none';
    domLoadSaveView?.destroy();
    domLoadSaveView = null;
    loadSaveRoot.replaceChildren();
  };
  const mountLoadSave = (
    mode: 'load' | 'save',
    store: SaveStore,
    done: (slot: number | null) => void,
  ): void => {
    unmountLoadSave();
    loadSaveRoot.style.display = 'block';
    const rows = makeMenuSaves(store).list();
    domLoadSaveView = new LoadSaveView({
      root: loadSaveRoot,
      mode,
      rows,
      defaultSlot: defaultSaveSlot(rows, mode),
      buttonArt: objBtnArt ?? undefined,
      titleBg: objBgArt !== null ? titleArtToDataUrl(objBgArt) : undefined,
      playSfx: (sfx: number) => {
        shell.sndm.PlaySfx(sfx);
      },
      // unmount BEFORE the continuation (so a follow-on message box / sim resume isn't behind us).
      onPick: (slot: number) => {
        unmountLoadSave();
        done(slot);
      },
      onCancel: () => {
        unmountLoadSave();
        done(null);
      },
    });
  };
  const mountLoadGame = (store: SaveStore, done: (slot: number | null) => void): void =>
    mountLoadSave('load', store, done);
  const mountSaveGame = (store: SaveStore, done: (slot: number | null) => void): void =>
    mountLoadSave('save', store, done);
  loadGameMount = mountLoadGame;
  saveGameMount = mountSaveGame;
  loadSaveUnmount = unmountLoadSave;

  // (Phase 5): the MP "WAITING FOR ALL PLAYERS…" sim-start overlay (kidfWaiting) — a
  // full-cover black overlay; mount + RETURN the unmount closure (the host fires it at the
  // first block / teardown).
  const waitingRoot = document.createElement('div');
  waitingRoot.id = 'dom-waiting';
  waitingRoot.style.display = 'none';
  document.body.appendChild(waitingRoot);
  const unmountWaiting = (): void => {
    waitingRoot.style.display = 'none';
    domWaitingView?.destroy();
    domWaitingView = null;
    waitingRoot.replaceChildren();
  };
  const mountWaiting = (title: string): (() => void) => {
    unmountWaiting();
    waitingRoot.style.display = 'block';
    domWaitingView = new WaitingView({ root: waitingRoot, title });
    return unmountWaiting;
  };
  waitingMount = mountWaiting;
  waitingUnmount = unmountWaiting;

  // (Phase 5, Form 2): the in-game PAUSE MENU renders as a DOM overlay over
  // the live canvas. DoInGameMenu calls this
  // with the visible buttons (the SP/MP descriptor) + an onSelect that routes the
  // chosen kidc through the SHARED post-modal handler. We unmount the overlay FIRST,
  // then run the action — so a sub-form (Options/Help/Save/Load) or the DOM
  // objectives overlay it opens isn't hidden behind the pause menu. The overlay
  // sits in #dom-pausemenu, above #screen (z=20).
  const pauseMenuRoot = document.createElement('div');
  pauseMenuRoot.id = 'dom-pausemenu';
  pauseMenuRoot.style.display = 'none';
  document.body.appendChild(pauseMenuRoot);
  const unmountPauseMenu = (): void => {
    pauseMenuRoot.style.display = 'none';
    domPauseMenuView?.destroy();
    domPauseMenuView = null;
    pauseMenuRoot.replaceChildren();
  };
  const mountPauseMenu = (buttons: InGameMenuButton[], onSelect: (idc: number) => void): void => {
    pauseMenuRoot.style.display = 'flex';
    domPauseMenuView = new PauseMenuView({
      buttons,
      root: pauseMenuRoot,
      onSelect: (idc: number) => {
        unmountPauseMenu();
        onSelect(idc);
      },
      titleBg: objBgArt !== null ? titleArtToDataUrl(objBgArt) : undefined,
      buttonArt: objBtnArt ?? undefined,
      playSfx: (sfx: number) => {
        shell.sndm.PlaySfx(sfx);
      },
    });
  };
  // expose the pause-menu mount + a SHAPED descriptor builder for the automation
  // preview seam (wiUi.openPauseMenuPreview) so it can render the REAL view with the
  // REAL inGameMenuButtons output for a given (SP/MP) state without a live MP game.
  pauseMenuMount = mountPauseMenu;

  // (Phase 5, Form 4): the in-game GAME OPTIONS renders as a DOM overlay over
  // the live canvas. DoModalGameOptionsForm
  // (reached from the pause-menu OPTIONS) calls this with the pure GameOptionsModel
  // (the read/apply built off the game's shellEnv) + a done() that resumes. OK/CANCEL
  // run the model (apply / revert-mute) then unmount + done. The overlay sits in
  // #dom-game-options, above #screen (z=20).
  const gameOptionsRoot = document.createElement('div');
  gameOptionsRoot.id = 'dom-game-options';
  gameOptionsRoot.style.display = 'none';
  document.body.appendChild(gameOptionsRoot);
  const unmountGameOptions = (): void => {
    gameOptionsRoot.style.display = 'none';
    domGameOptionsView?.destroy();
    domGameOptionsView = null;
    gameOptionsRoot.replaceChildren();
  };
  const mountGameOptions = (model: GameOptionsModel, done: () => void): void => {
    gameOptionsRoot.style.display = 'flex';
    const close = (): void => {
      unmountGameOptions();
      done();
    };
    domGameOptionsView = new GameOptionsView({
      model,
      root: gameOptionsRoot,
      // the view runs model.ok()/model.cancel() FIRST (the apply / mute-revert),
      // then close unmounts + resumes (the canvas EndForm epilogue → finish).
      onOk: close,
      onCancel: close,
      titleBg: objBgArt !== null ? titleArtToDataUrl(objBgArt) : undefined,
      buttonArt: objBtnArt ?? undefined,
      playSfx: (sfx: number) => {
        shell.sndm.PlaySfx(sfx);
      },
    });
  };
  gameOptionsMount = mountGameOptions;
  gameOptionsUnmount = unmountGameOptions;

  // (Phase 5, Form 6) the in-mission E-COM narrative overlay now stands up through the SHARED
  // createEcomUI factory (dom/ingame/ecom-ui.ts) — the EXACT mountEcom logic (the SAME #dom-ecom
  // root + ecom backdrops + cached character portraits) the editor's Play host also uses. The
  // returned domEcom/teardown are assigned to the ecomMount/ecomUnmount let vars below so the
  // wiUi.ecomForceShow / closeEcomPreview seams + the GameHostDeps wiring stay unchanged.
  const ecomUi = createEcomUI({
    tam,
    objBtnArt,
    playSfx: (sfx: number) => {
      shell.sndm.PlaySfx(sfx);
    },
  });
  ecomMount = ecomUi.domEcom;
  ecomUnmount = ecomUi.teardown;

  // (Phase 5, kidfCutScene 1047) the in-mission CUTSCENE player overlay now stands up through the
  // SHARED createCutSceneUI factory (dom/ingame/cutscene-ui.ts) — the EXACT mount/unmount + image
  // loader that lived inline here, so the shipped client AND the editor's Play host share it. The
  // module OWNS the active flag (isActive() — read below by the HUD `suppressed` gate + the
  // domStoryOverlayActive opt); onActiveChange re-runs syncReveal (the host job: hide #screen +
  // tear the HUD down while the intro plays over black, reveal once it clears).
  const cutSceneUi = createCutSceneUI({
    tam,
    objBtnArt,
    playSfx: (sfx: number) => {
      shell.sndm.PlaySfx(sfx);
    },
    onActiveChange: () => {
      syncReveal();
    },
  });
  cutSceneMount = cutSceneUi.domCutScene;
  cutSceneUnmount = cutSceneUi.teardown;

  // (Phase 5 — build/order STEP 1–3): the in-game COMMAND overlays — the "Are you sure?"
  // CONFIRMATION (kidfAreYouSure), the SECTION-ONLY HELP (a build panel's "?"), the gob
  // CONTEXT MENU (UnitMenu), and the UNIT / STRUCTURE / RESEARCH build carousels — now stand
  // up through the SHARED createCommandUI factory (dom/ingame/command-ui.ts), the EXACT
  // mount/unmount logic that lived inline here, so the shipped client AND the mission editor's
  // Play host share one implementation. The returned mount/unmount fns are assigned to the
  // module-level *Mount/*Unmount let vars below (near the build-panel art), keeping every
  // wiUi.* preview seam + the GameHostDeps wiring unchanged.

  // (Phase 5): the FULL-manual in-game HELP overlay. Driven by the pause-menu HELP
  // (handleInGameMenuChoice kidcHelp) via env.domHelpManual(done). The overlay renders the
  // WHOLE data/help.txt manual (the SAME mountHelpManual renderer as the main-menu HELP),
  // modal over the paused canvas. OK / Esc → unmount + run the pause-menu finish (un-pause
  // SP + done).
  const helpManualRoot = document.createElement('div');
  helpManualRoot.id = 'dom-help-manual';
  helpManualRoot.style.display = 'none';
  document.body.appendChild(helpManualRoot);
  const unmountHelpManual = (): void => {
    helpManualRoot.style.display = 'none';
    domHelpManualView?.destroy();
    domHelpManualView = null;
    helpManualRoot.replaceChildren();
  };
  const mountHelpManualOverlay = (done: () => void): void => {
    unmountHelpManual(); // defensive — only one manual overlay at a time
    helpManualRoot.style.display = 'block';
    domHelpManualView = new HelpManualOverlayView({
      root: helpManualRoot,
      // the SAME inline-image resolver + chrome the section HelpView / menu HELP use.
      resolveImage: (src: string) => resolveHelpImageDataUrl(pakr, tam, src),
      titleBg: objBgArt !== null ? titleArtToDataUrl(objBgArt) : undefined,
      buttonArt: objBtnArt ?? undefined,
      playSfx: (sfx: number) => {
        shell.sndm.PlaySfx(sfx);
      },
      // OK / Esc → unmount THEN run the pause-menu finish (the canvas Help()'s done).
      onClose: () => {
        unmountHelpManual();
        done();
      },
    });
  };
  helpManualMount = mountHelpManualOverlay;
  helpManualUnmount = unmountHelpManual;

  // (Phase 5 — survey I10): the in-game MESSAGE BOX overlay (the HtMessageBox
  // family — alerts / warnings / errors / Yes-No queries) now stands up through the SHARED
  // createMessageBoxUI factory (dom/ingame/messagebox-ui.ts) — the EXACT mountMessageBox logic
  // (the SAME #dom-messagebox root) the editor's Play host also uses. Driven by HtMessageBox
  // (ui/msgbox.ts) via env.domMessageBox — the resign/leave WARNING (AskResignGame), the GAME OVER
  // observe query, the MP comm/server-message errors, the objectives alert. DISPLAY-only: the view
  // reports the user's OK/Yes/No through done() and the host routes it to the SAME callback the
  // canvas DoModal uses (NO Message constructed here). The returned domMessageBox/teardown are
  // assigned to the messageBoxMount/messageBoxUnmount let vars below so the wiUi.messageBoxForceShow
  // automation seam + the GameHostDeps wiring stay unchanged. (This is the SHARED, modal singleton —
  // the DEDICATED modeless MP "LAG ALERT" box below rides its OWN MessageBoxView, untouched.)
  const messageBoxUi = createMessageBoxUI({
    tam,
    objBtnArt,
    playSfx: (sfx: number) => {
      shell.sndm.PlaySfx(sfx);
    },
  });
  messageBoxMount = messageBoxUi.domMessageBox;
  messageBoxUnmount = messageBoxUi.teardown;

  // (forms.ini-elim, Phase 5): the MP "LAG ALERT" Yes/No box — formerly the LAST in-game
  // always-on canvas message-box (the modeless lag CreateHtMessageBox, removed canvas-removal
  // R2). It rides a DEDICATED MessageBoxView in its OWN root (#dom-lagbox), NOT the shared
  // singleton above,
  // because the lag box is modeless: it can coexist with a resign/observe box and is hidden/
  // re-shown for a different lagger. The seam MOUNTS the box and RETURNS its teardown closure
  // (the session's OnLagNotify/CheckLagForm poll). done(fOk) routes Yes/No to the SAME
  // CsKillLaggingPlayer vote (wire-identical — no Message/cmdq).
  const lagBoxRoot = document.createElement('div');
  lagBoxRoot.id = 'dom-lagbox';
  lagBoxRoot.style.display = 'none';
  document.body.appendChild(lagBoxRoot);
  const unmountLagBox = (): void => {
    lagBoxRoot.style.display = 'none';
    domLagBoxView?.destroy();
    domLagBoxView = null;
    lagBoxRoot.replaceChildren();
  };
  const mountLagBox = (title: string, body: string, done: (fOk: boolean) => void): (() => void) => {
    unmountLagBox(); // tear any prior lag box down first (no leaked listeners)
    lagBoxRoot.style.display = 'block';
    domLagBoxView = new MessageBoxView({
      root: lagBoxRoot,
      title,
      message: body,
      variant: 'query',
      whiteBorder: true,
      // MODELESS — the canvas lag DialogForm (forms.ini.pp 1029/1058) is NON-autotakedown: it
      // ignores outside pen-down / Esc and waits for a real Yes/No press. So the DOM box must NOT
      // dismiss on backdrop-click / Esc either — a stray dismiss would resolve(false) → cast a
      // spurious CsKillLaggingPlayer{fYes 0} ("keep the lagger") WIRE VOTE the canvas never sends.
      modeless: true,
      buttonArt: objBtnArt ?? undefined,
      playSfx: (sfx: number) => {
        shell.sndm.PlaySfx(sfx);
      },
      // the user picked Yes (disconnect) / No (keep): unmount FIRST, then report — the session's
      // CheckLagForm poll observes the captured result and sends the vote.
      onResult: (fOk: boolean) => {
        unmountLagBox();
        done(fOk);
      },
    });
    return unmountLagBox;
  };
  lagBoxMount = mountLagBox;
  lagBoxUnmount = unmountLagBox;

  // the developer TEST OPTIONS panel (Alt+T) — the C++ DEV_BUILD TestOptionsForm
  // (game/GameOptions.cpp:903) brought back as a DOM overlay. Available in ALL builds,
  // like the Alt+L / Alt+P overlays it sits alongside. It toggles the SAME live
  // dbgOverlay flags (so it stays in sync with the shortcuts) + the running game's
  // sim flags. DISPLAY/debug-only — no command/wire touch. Sits at z=20 (modal) like
  // the other in-game dialogs; pointer-events:all so its checkboxes are clickable.
  const testOptionsRoot = document.createElement('div');
  testOptionsRoot.id = 'dom-test-options';
  testOptionsRoot.style.display = 'none';
  document.body.appendChild(testOptionsRoot);
  const unmountTestOptions = (): void => {
    testOptionsRoot.style.display = 'none';
    domTestOptionsView?.destroy();
    domTestOptionsView = null;
    testOptionsRoot.replaceChildren();
  };
  const mountTestOptions = (): void => {
    unmountTestOptions(); // never two
    testOptionsRoot.style.display = 'block';
    // the LIVE toggles — only those with a real TS hook (no fabricated hooks):
    //   Draw Paths / Draw Target Lines / Show FPS → the dbgOverlay flags (the SAME
    //   the Alt+P / Alt+L / Alt+T shortcuts drive); God Mode → the running game's
    //   ctx.fGodMode (the sim cheat). Clear Fog (action) → ctx.fogm.RevealAll().
    const options = [
      {
        label: 'Draw paths',
        get: () => dbgOverlay.paths,
        toggle: () => {
          toggleDebugPaths();
        },
      },
      {
        label: 'Draw target lines',
        get: () => dbgOverlay.lines,
        toggle: () => {
          toggleDebugLines();
        },
      },
      {
        label: 'Show FPS',
        get: () => dbgOverlay.showFps,
        toggle: () => {
          toggleDebugShowFps();
        },
      },
      {
        label: 'God mode',
        get: () => gameHost.runningGame()?.sim.ctx.fGodMode ?? false,
        toggle: () => {
          const ctx = gameHost.runningGame()?.sim.ctx;
          if (ctx !== undefined) {
            ctx.fGodMode = !ctx.fGodMode;
            dbgOverlay.dirty = true;
          }
        },
      },
    ];
    // Force Reload — always available: drop the service worker + its offline caches, then
    // reload, so a returning dev is guaranteed the freshest build (never a cached bundle or
    // shell). Navigations are already network-first (sw.js); this is the belt-and-braces
    // "make sure I'm on the latest" button.
    // Clear Fog — the SAME RevealAll() SetObserving uses; only offered when a game runs.
    const actions = [
      {
        label: 'Force Reload',
        run: () => {
          void forceReloadLatest();
        },
      },
    ];
    const fogm = gameHost.runningGame()?.sim.ctx.fogm;
    if (fogm !== undefined && fogm !== null) {
      actions.push({
        label: 'Clear Fog',
        run: () => {
          fogm.RevealAll();
          dbgOverlay.dirty = true;
        },
      });
    }
    domTestOptionsView = new TestOptionsView({
      root: testOptionsRoot,
      options,
      actions,
      buttonArt: objBtnArt ?? undefined,
      playSfx: (sfx: number) => {
        shell.sndm.PlaySfx(sfx);
      },
      onClose: () => {
        unmountTestOptions();
      },
    });
  };
  // the Alt+T toggle: open the panel if down, close it if up (the keyboard handler
  // + the wiUi automation seam call this).
  testOptionsToggle = (): void => {
    if (domTestOptionsView !== null) unmountTestOptions();
    else mountTestOptions();
  };

  // (Phase 5 — build/order STEP 1–3): stand up the SHARED in-game COMMAND overlays — the
  // "Are you sure?" CONFIRMATION (kidfAreYouSure), the SECTION-ONLY HELP (a build panel's "?"),
  // the gob CONTEXT MENU (UnitMenu), and the UNIT / STRUCTURE / RESEARCH build carousels —
  // through the createCommandUI factory (dom/ingame/command-ui.ts), the EXACT mount/unmount
  // logic that lived inline here, lifted so the shipped client AND the mission editor's Play
  // host share one implementation. Each handler lazily OWNS its root <div> (the SAME ids —
  // #dom-confirm/#dom-help/#dom-unitmenu/#dom-buildpanel/#dom-structurepanel/#dom-upgradepanel)
  // + its own view; mounting replaces any open instance; unmount is idempotent. The factory
  // extracts the SAME pack art (buildformbkgd / pip / per-class damage glyphs / helpup-down /
  // cancelup-down + the section help's inline thumbnails over pakr/tam) the inline code did, at
  // the SAME scales (kUiMagnify chrome / kGameScale anchors). COMMAND-ADJACENT, DISPLAY-only —
  // every command enqueue stays in buildui.ts.
  const commandUi = createCommandUI({
    tam,
    pakr,
    magnify: kUiMagnify,
    gameScale: kGameScale,
    objBtnArt,
    playSfx: (sfx: number) => {
      shell.sndm.PlaySfx(sfx);
    },
  });
  // assign the module-level *Mount/*Unmount let vars from the factory — this keeps every
  // wiUi.* preview seam (confirm/help/unitMenu/build/structure/research forceShow) + the
  // GameHostDeps wiring below referencing the SAME closures, unchanged.
  confirmMount = commandUi.domConfirm;
  confirmUnmount = commandUi.unmountConfirm;
  helpMount = commandUi.domHelp;
  helpUnmount = commandUi.unmountHelp;
  unitMenuUnmount = commandUi.unmountUnitMenu;
  buildPanelUnmount = commandUi.unmountBuildPanel;
  structurePanelUnmount = commandUi.unmountStructurePanel;
  upgradePanelUnmount = commandUi.unmountUpgradePanel;

  // (Phase 5) the PERSISTENT in-game overlays — the HUD BOTTOM BAR (credits/power/MENU), the
  // OVERLAY TEXT LABELS (objective/alert/countdown/FPS), and the RADAR (minimap) — now stand up
  // through the SHARED createHudSeams factory (dom/ingame/hud-seams.ts), the EXACT seam logic that
  // lived inline here, so the shipped client AND the editor's Play host share one implementation.
  // domHud/domSimUI are pushed by the session (GameHostDeps below); the minimap is host-pulled each
  // frame (hud.syncMinimap in syncHud). `suppressed` is the opening-cutscene gate (no HUD/labels/
  // radar over the intro); getSession reaches the running game; the MENU/credits/power taps route
  // the SAME way the inline HudView did.
  const hud = createHudSeams({
    tam,
    magnify: kUiMagnify,
    gameScale: kGameScale,
    objBtnArt,
    playSfx: (sfx: number) => {
      shell.sndm.PlaySfx(sfx);
    },
    getSession: () => gameHost.runningGame(),
    suppressed: () => cutSceneUi.isActive(),
    onMenu: () => {
      gameHost.runningGame()?.openInGameMenuForAutomation();
    },
    onCreditsTap: () => {
      gameHost.runningGame()?.openObjectivesForAutomation();
    },
    onPowerTapHold: () => {
      if (debugPanelUnlocked()) testOptionsToggle?.();
    },
    onAttackMoveToggle: () => {
      gameHost.runningGame()?.toggleAttackMove();
    },
  });
  hudForceRenderSeam = hud.hudForceRender; // expose to the module-level wiUi.hudForceRender seam
  // intro-over-black (kidfCutScene 1047): the single authority for the game canvas'
  // visibility while a game runs. The opening intro is a CUTSCENE that must play over
  // BLACK — so while the cutscene is active (cutSceneUi.isActive()), #screen is HIDDEN (HUD torn down,
  // below). Once the cutscene clears (real gameplay), #screen is revealed. Off-game the
  // menu/lobby views own the canvas (their hideCanvas), so this only acts in-game.
  syncReveal = (): void => {
    if (!gameHost.inGame()) return; // the menu/lobby view owns the canvas off-game
    if (cutSceneUi.isActive()) {
      canvas.style.display = 'none'; // the intro plays over black
      hud.unmountHud(); // no HUD behind the intro
      hud.unmountSimUI(); // no overlay labels behind the intro
    } else {
      canvas.style.display = ''; // gameplay — reveal the map
    }
  };
  // keep the HUD's lifecycle tied to inGame: tear it down at game over (the running
  // game stops pushing, so without this the bar would linger). syncHud() runs each
  // frame (cheap) and unmounts when the game ends.
  syncHud = (): void => {
    syncReveal(); // reveal/hide the canvas + HUD to match the intro-cutscene state
    if (!gameHost.inGame()) hud.unmountHud();
    // the in-game overlay text labels (objective / alert / countdown / FPS) — like the
    // HUD, tear them down at game over (the running game stops pushing).
    if (!gameHost.inGame()) hud.unmountSimUI();
    // ?dom Phase 2: the RADAR (minimap). Unlike the HUD/labels (the FORMS push them from
    // their Update), the minimap model is host-pushed HERE each frame (the white viewport
    // box tracks scroll, which moves between sim ticks). Mount/render while in-game (the
    // seam lazily mounts on the first push, gated on the intro cutscene); tear down at
    // game over. (No canvas minimap fallback — the form was deleted.)
    if (gameHost.inGame()) hud.syncMinimap();
    else hud.unmountMinimap();
    // a transient in-mission CUTSCENE overlay left up when the game ends (e.g. a forced
    // game-over mid-cutscene) would linger over the menu — tear it down at game over
    // like the HUD. (The ecom is story-modal-queued + always dismissed before the
    // game ends in normal flow; the cutscene gets the same safety net here.)
    if (!gameHost.inGame()) cutSceneUi.teardown();
    // the shared COMMAND overlays (gob CONTEXT MENU + UNIT/STRUCTURE/RESEARCH carousels + the
    // SECTION-ONLY HELP) left open when the game ends would linger over the menu — tear them
    // down at game over for promptness (their getModel poll closes them once the gob/builder
    // is gone, but the running game stops ticking; the host CloseBuildPanelDom ends the form
    // too). Each unmount is idempotent — a no-op when that overlay is already down.
    if (!gameHost.inGame()) {
      commandUi.unmountUnitMenu();
      commandUi.unmountBuildPanel();
      commandUi.unmountStructurePanel();
      commandUi.unmountUpgradePanel();
      commandUi.unmountHelp();
    }
    // likewise a FULL-manual HELP overlay (the pause-menu HELP) left up at game over.
    if (!gameHost.inGame() && domHelpManualView !== null) helpManualUnmount?.();
  };

  // the game host owns the game lifecycle + the launch seam (host/gamehost.ts);
  // the menu's flow forwards into it, and it reads the menu's prefs/options/
  // sndm/gtGameSpeed via attach() below
  gameHost = new GameHost({
    pakr,
    tam,
    coords,
    gameIni,
    screen,
    createSurface: surfaceFactory,
    templates,
    anir,
    prefStore,
    saveStore,
    completeMgr,
    packm,
    ppim,
    mp,
    chatc,
    serverInfoUrl,
    envBeta,
    httpGet,
    hostGetCurrentKeyState,
    spEvent,
    // route the HtMessageBox family (alerts/warnings/errors/queries) to the DOM
    // overlay (survey I10) — incl. the user's resign/leave WARNING + observe query.
    domMessageBox: messageBoxUi.domMessageBox,
    // route the in-game MP "LAG ALERT" Yes/No to its DEDICATED modeless DOM overlay.
    domLagBox: mountLagBox,
    // route the in-game OBJECTIVES briefing to the DOM overlay.
    domShowObjectives: objectivesUi.domShowObjectives,
    // route the in-game PAUSE MENU to the DOM overlay (Form 2).
    domInGameMenu: mountPauseMenu,
    // route the WIN/LOSE RESULTS SUMMARY to the DOM overlay (Form 3).
    domObjectivesSummary: summaryUi.domObjectivesSummary,
    // MP: the server announced the won game's recording is in R2 — remember its key and light up the
    // summary's VIEW REPLAY (it activates in place if the player is still on the summary / spectating).
    onReplayAvailable: (key: string) => {
      latestReplayKey = key;
      summaryUi.setReplayReady();
    },
    // route the in-game LOAD / SAVE slot-list forms + the MP WAITING overlay.
    domLoadGame: mountLoadGame,
    domSaveGame: mountSaveGame,
    domWaiting: mountWaiting,
    // route the in-game GAME OPTIONS to the DOM overlay (Form 4).
    domGameOptions: mountGameOptions,
    // route the pause-menu HELP (the FULL manual) to the DOM overlay.
    domHelpManual: mountHelpManualOverlay,
    // route the in-mission E-COM overlay to the DOM overlay (Form 6).
    domEcom: ecomUi.domEcom,
    // route the in-mission CUTSCENE player to the DOM overlay (1047).
    domCutScene: cutSceneUi.domCutScene,
    // WireJumpToMission's DOM guard — the s_00→s_01 hand-off waits for the
    // intro CUTSCENE overlay to clear (the DOM cutscene never lands on the form
    // stack, so the GetModalForm() guard alone would hand off mid-intro and the s_01
    // briefing E-Com would overlap the intro). True while #dom-cutscene is mounted
    // (cutSceneUi.isActive() is the synchronously-maintained mount flag).
    domStoryOverlayActive: () => cutSceneUi.isActive(),
    // route the kidfAreYouSure CONFIRMATION to the DOM overlay (STEP 1).
    domConfirm: commandUi.domConfirm,
    // route the build panels' "?" HELP (section-only) to the DOM overlay.
    domHelp: commandUi.domHelp,
    // route the gob CONTEXT MENU (UnitMenu) to the DOM overlay (STEP 2).
    domUnitMenu: commandUi.domUnitMenu,
    // route the UNIT BUILD carousel (VTS/HRC) to the DOM overlay (STEP 3).
    domBuildPanel: commandUi.domBuildPanel,
    // route the STRUCTURE BUILD carousel (HQ) to the DOM overlay (FORM C).
    domStructurePanel: commandUi.domStructurePanel,
    // route the RESEARCH carousel (research center) to the DOM overlay (FORM E).
    domUpgradePanel: commandUi.domUpgradePanel,
    // push the persistent HUD bottom bar over the live canvas (Form 5). The canvas
    // in-game HUD is GONE (SimUI/InputUI are control-less PaintLayers now), so the
    // DOM HUD is ALWAYS the in-game HUD.
    domHud: hud.domHud,
    // push the in-game overlay text labels (objective / alert / countdown / FPS)
    // over the live canvas as web-font DOM.
    domSimUI: hud.domSimUI,
  });

  const fTouchUI = prefersTouchControls();
  console.log(`[input] ${fTouchUI ? 'touch (FingerHandler)' : 'mouse (StylusHandler)'} UI`);
  shell = new ShellSession({
    pakr,
    tam,
    coords,
    gameIni,
    screen,
    prefStore,
    createSurface: surfaceFactory,
    sndm: sndm ?? undefined,
    // game.cpp:296-301 forces mouse mode on web (StylusHandler); skip that on a
    // touch-primary device so phones/tablets default to the touch FingerHandler.
    // DELIBERATE divergence from the WASM web build (which force-mouse-mode'd ALL web),
    // KEPT by user decision (2026-06-19) — touch users get real touch controls (two-finger
    // select, pen-hold menu). Do NOT revert to match WASM. See the wi-input-handler-modes note.
    forceMouseMode: !fTouchUI,
    flow: {
      playLevel: (pmiid, nRank, done) => {
        gameHost.launchSinglePlayer(pmiid, nRank, done);
      },
      playSavedGame: (pstm, done) => {
        gameHost.launchSavedGame(pstm, done);
      },
      getLastMissionIdentifier: () =>
        gameHost.lastMissionIdentifier() ?? {
          packid: { id: 0, hash: new Uint8Array(16) },
          szLvlFilename: '',
        },
      saveStore,
      fDemo: false,
      // Shell.cpp:285-301 — the WEB onbeforeunload glue: warn while a game is up
      enableNavigation: (fEnable: boolean) => {
        window.onbeforeunload = fEnable
          ? null
          : (e: BeforeUnloadEvent) => {
              e.preventDefault();
              return '';
            };
      },
    },
    // gppackm/gppim into the mission lists: downloaded packs enumerate in
    // SelectMissionForm/CreateGameForm (missionpick.ts over EnumPacks/Mount)
    mlDeps: { packm, ppim },
    envExtras: {
      takeSavedGameStream: () => gameHost.takeSavedGameStream(),
      // HostOpenUrl IS window.open on the shipped web host (hosthelpers.cpp:131)
      openUrl: (pszUrl: string) => {
        window.open(pszUrl, '_blank');
      },
      initiateWebView,
      hostAnalytics: () => {},
      // HostInitiateAsk/HostGetAskString (the lobby room/password + DLC
      // custom-URL text entry) — the window.prompt arm above
      initiateAsk,
      getAskString: () => askString,
      // WebAddBot (B4b) — the host's "Add Bot" button POSTs /bots/add to the connected DO.
      addBot,
      // route the SHELL-side HtMessageBox family (the MP lobby/login/room/game
      // comm + server-message errors, the LOAD SAVED GAME error, the launch
      // error) to the DOM MessageBoxView too — the SAME single seam the in-game
      // boxes use.
      domMessageBox: messageBoxUi.domMessageBox,
    },
  });
  gameHost.attach(shell); // the host reads the menu's prefs/options/sndm/gtGameSpeed

  // game.cpp:450-457 — the prefs' volume/mute onto the live sound manager
  // (GetInteger returns -1 for a missing/non-number key; guard like the C++)
  if (sndm !== null) {
    const nVolume = shell.prefs.GetInteger(knPrefVolume);
    if (nVolume >= 0) sndm.SetVolume(nVolume);
    sndm.Enable(!shell.prefs.GetBool(kfPrefSoundMuted));
  }

  gameHost.useExternalMenu(); // the HTML menu drives launches; don't tick a canvas menu
  startDomMenu();

  wirePointerEvents();
  wireResumeFlush(); // flush the SP resume slot on tab-hide/close so a refresh resumes the LATEST state
  wireHiddenAudioTeardown(); // issue #110 — closed cover / hidden tab: drop audio when no MP game runs
  suppressOsZoom(); // block OS double-tap / pinch zoom on every screen (iOS Safari)
  captureAcquisition(); // first-touch acquisition: how this browser found WI (once per did, prod only)
  maybeShowInstallPrompt(); // PWA install banner, now that the main menu is up (install-prompt.ts)
  if (!fShowStats) status('');
  requestAnimationFrame(onFrame);
}

// ---- a small debug/automation handle (the verify harness reads it) -----------

declare global {
  interface Window {
    wiTs?: Record<string, unknown>;
    // Per-build content-version stamps for the data packs, injected into
    // index.html by scripts/build-ts-client.mjs. boot() appends ?v=<stamp> so the
    // immutable-cached packs (pages/_headers) bust correctly when content changes.
    __PACKV?: Record<string, string>;
  }
}

window.wiTs = {
  ready: () => shell !== undefined,
  inGame: () => gameHost.inGame(),
  // the active presentation renderer (+ engine stats) — webgl2 unless ?cpu or
  // context-creation fallback
  renderer: () => rendererName,
  glStats: () => (glr === null ? null : { ...glr.stats }),
  // the back surface bytes regardless of renderer (oracle/debug readback)
  frameBytes: () => {
    const back = act().back;
    const bytes = glr !== null ? (back as GlDibBitmap).readback() : back.rgba;
    return { cx: back.cx, cy: back.cy, bytes: Array.from(bytes) };
  },
  // the audio stack's observable state (the browser verification harness):
  // unlocked = the AudioContext exists AND is running; cPlays = buffers
  // actually scheduled on a channel; busy = per-channel occupancy
  audio: () => ({
    present: sndm !== null,
    enabled: sndm?.IsEnabled() ?? false,
    volume: sndm?.GetVolume() ?? -1,
    unlocked: sndDev?.unlocked ?? false,
    cPlays: sndDev?.cPlays ?? 0,
    busy: sndDev === null ? [] : [0, 1, 2, 3].map((i) => !sndDev!.IsChannelFree(i)),
  }),
  formId: () => act().mfrmm.GetModalForm()?.GetId() ?? null,
  updateCount: () => gameHost.runningGame()?.updateCount() ?? -1,
  paused: () => gameHost.runningGame()?.sim.sim.IsPaused() ?? false,
  // the wiAuto synthetic control press (event.cpp:469-486) on the active session
  press: (idc: number) => {
    act().pressControl(idc);
  },
  // a control's kfCtlVisible state on the current modal form (the staged
  // BEGIN/READY gates show their buttons when the flow allows)
  controlVisible: (idc: number) => {
    const pctl = modalFormAuto()?.GetControlPtr(idc);
    return pctl != null && (pctl.GetFlags() & kfCtlVisible) !== 0;
  },
  // checkbox state poke (the login form's ANONYMOUS box; wiAuto setChecked)
  setChecked: (idc: number, f: boolean) => {
    modalFormAuto()?.GetControlPtr(idc)?.SetChecked?.(f);
  },
  // select row i of a list control on the current modal form
  selectListItem: (idc: number, i: number) => {
    modalFormAuto()?.GetControlPtr(idc)?.Select?.(i, true, true);
  },
  // the mission-list rows of a CreateGameForm/SelectMissionForm list control:
  // [{ i, lvl, packId }] (the form's mission list maps each row's data to a
  // MissionIdentifier — ts-mp-ui.mjs's walk)
  challengeRows: (idcList: number) => {
    const pfrm = modalFormAuto();
    if (pfrm === null || pfrm.m_pml === undefined) return null;
    const plstc = pfrm.GetControlPtr(idcList) as unknown as {
      m_pliFirst: { pvData: unknown; pliNext: unknown } | null;
    } | null;
    if (plstc === null) return null;
    const rows: { i: number; lvl: string; packId: number }[] = [];
    let pli = plstc.m_pliFirst;
    for (let i = 0; pli !== null; i++, pli = pli.pliNext as typeof pli) {
      const miid = pfrm.m_pml.GetMissionIdentifier(pli.pvData);
      if (miid !== null) {
        rows.push({ i, lvl: miid.szLvlFilename, packId: miid.packid.id >>> 0 });
      }
    }
    return rows;
  },
  // the DLC catalog rows (DownloadMissionPackForm's list; data = IndexEntry)
  packRows: (idcList: number) => {
    const plstc = modalFormAuto()?.GetControlPtr(idcList) as unknown as {
      m_pliFirst: { pvData: unknown; pliNext: unknown } | null;
    } | null;
    if (plstc === null || plstc === undefined) return null;
    const rows: {
      i: number;
      id: number;
      title: string;
      cMissions: number;
      cPlayersMin: number;
      cPlayersMax: number;
      installed: number;
    }[] = [];
    let pli = plstc.m_pliFirst;
    for (let i = 0; pli !== null; i++, pli = pli.pliNext as typeof pli) {
      const e = pli.pvData as {
        packid: PackId;
        title: string;
        cMissions: number;
        cPlayersMin: number;
        cPlayersMax: number;
      } | null;
      if (e !== null && typeof e === 'object' && 'packid' in e) {
        rows.push({
          i,
          id: e.packid.id >>> 0,
          title: e.title,
          cMissions: e.cMissions,
          cPlayersMin: e.cPlayersMin,
          cPlayersMax: e.cPlayersMax,
          installed: packm.IsInstalled(e.packid),
        });
      }
    }
    return rows;
  },
  // the browser-MP lockstep observability (the E2E verdict inputs)
  mpStats: () => {
    const gs = gameHost.runningGame();
    const ms = gameHost.mpStats;
    return {
      live: ms.live,
      connected: mp.tra !== null,
      reports: ms.reports,
      syncErrors: ms.syncErrors,
      syncFlag: gs?.m_fSyncError ?? false,
      lastBlock: ms.hashes.length > 0 ? ms.hashes[ms.hashes.length - 1][0] : -1,
      updates: gs?.updateCount() ?? -1,
      hashes: ms.hashes,
    };
  },
  // end the running game (the harness abort path — ts-mp-ui.mjs endGame)
  endGame: () => {
    const gs = gameHost.runningGame();
    if (gs === null || gs.fGameOver) return false;
    const evt = newEvent(gameOverEvent);
    evt.dw = knGoAbortLevel;
    gs.evm.PostEvent(evt);
    return true;
  },
  // scripted MP play: wander one own mobile unit through the PROVEN command
  // path (SetTarget → gcmdq → CsClientCommands; ts-mp-ui.mjs issueScriptedMove)
  scriptMove: (step: number) => {
    const gs = gameHost.runningGame();
    if (gs === null || gs.fGameOver || gs.m_fSyncError) return false;
    const ctx = gs.sim.ctx;
    const side = ctx.plrLocal?.GetSide();
    if (side === undefined) return false;
    const own = [];
    const want = kfGobActive | kfGobMobileUnit;
    for (let pgob = ctx.gobm.GetFirstGob(); pgob !== null; pgob = ctx.gobm.GetNextGob(pgob)) {
      if (pgob.GetSide() === side && (pgob.GetFlags() & want) === want) own.push(pgob);
    }
    if (own.length === 0) return false;
    const punt = own[step % own.length] as unknown as {
      GetCenter(wpt: { wx: number; wy: number }): void;
      SetTarget(
        gid: number,
        wx: number,
        wy: number,
        wxC: number,
        wyC: number,
        a: number,
        b: number,
      ): void;
    };
    const wpt = { wx: 0, wy: 0 };
    punt.GetCenter(wpt);
    const dx = (2 + (step % 3)) * 256 * (step & 1 ? 1 : -1);
    const dy = (2 + ((step >> 1) % 3)) * 256 * (step & 2 ? 1 : -1);
    const trmap = ctx.trmap;
    const wx = Math.max(2 * 256, Math.min(wpt.wx + dx, (trmap.GetWidth() - 2) * 256));
    const wy = Math.max(2 * 256, Math.min(wpt.wy + dy, (trmap.GetHeight() - 2) * 256));
    punt.SetTarget(kgidNull, wx, wy, wx, wy, 0, 0);
    return true;
  },
  // installed DLC packs (the round-trip assertion reach)
  installedPacks: () => packm.EnumPacks().map((p) => ({ id: p.id >>> 0 })),
  perf: () => ({
    fps: perf.fps,
    pumpMs: perf.pumpMs,
    blitMs: perf.blitMs,
    worstPumpMs: perf.worstPumpMs,
    frames: perf.frames,
  }),
  // the live game surface size (the window-sized display — BUG-3 verification)
  screenSize: () => ({ cx: screen.cx, cy: screen.cy }),
  // the art selection (index.html:216-241 parity) + the live key bitset
  artMode: () => ({
    artSize: gameArtSize,
    artDepth: gameArtDepth,
    pack: kszArtPack,
    gameScale: kGameScale,
  }),
  keyState: () => dwKeysDown,
  // second-finger host-boundary production (host.cpp:284-353 parity)
  pen2Count: () => cPen2Events,
  // the in-game view position (drag-select must NOT pan — BUG-2 verification)
  viewPos: () => {
    const gs = gameHost.runningGame();
    return gs === null ? null : { wx: gs.sim.sim.m_wxView, wy: gs.sim.sim.m_wyView };
  },
  // kfGobSelected holders (the drag-select assertion reach)
  selectedGids: () => {
    const gs = gameHost.runningGame();
    if (gs === null) return [];
    const out: number[] = [];
    for (let g = gs.sim.ctx.gobm.GetFirstGob(); g !== null; g = gs.sim.ctx.gobm.GetNextGob(g)) {
      if (g.GetFlags() & kfGobSelected) out.push(g.GetId());
    }
    return out;
  },
  // screen rect of a control on the current modal form (form-relative + form rect)
  controlRect: (idc: number) => {
    const pfrm = modalFormAuto();
    if (pfrm === null) return null;
    const pctl = pfrm.GetControlPtr(idc);
    if (pctl === null) return null;
    const rc = new Rect();
    pctl.GetRect(rc);
    const rcFrm = new Rect();
    pfrm.GetRect(rcFrm);
    return {
      left: rcFrm.left + rc.left,
      top: rcFrm.top + rc.top,
      right: rcFrm.left + rc.right,
      bottom: rcFrm.top + rc.bottom,
    };
  },
  // a label/button control's text on the current modal form (verify-harness
  // sequence-content assertions, e.g. the ecom From/To addresses; buttons
  // carry their label in m_szLabel — no GetText)
  controlText: (idc: number) => {
    const pctl = modalFormAuto()?.GetControlPtr(idc);
    return pctl?.GetText?.() ?? pctl?.m_szLabel ?? null;
  },
  // control ids on the current modal form (verify-harness aiming aid)
  controls: () => {
    const pfrm = modalFormAuto();
    if (pfrm === null) return null;
    const ids: number[] = [];
    for (let i = 0; i < pfrm.m_cctl; i++) {
      const pctl = pfrm.m_apctl[i];
      if (pctl !== null) ids.push(pctl.GetId());
    }
    return ids;
  },
  // the modal SelectMissionForm's current pick (null elsewhere)
  selectedMission: () => modalFormAuto()?.GetSelectedMission?.() ?? null,
  // mobile units with playfield (=screen) coords — the verify harness aims clicks
  units: () => {
    const gs = gameHost.runningGame();
    if (gs === null) return [];
    const wpt = { wx: 0, wy: 0 };
    gs.sui.GetViewPos(wpt);
    const out: { gid: number; local: boolean; selected: boolean; x: number; y: number }[] = [];
    const gobm = gs.sim.ctx.gobm;
    for (let pgob = gobm.GetFirstGob(); pgob !== null; pgob = gobm.GetNextGob(pgob)) {
      if (!(pgob.GetFlags() & kfGobMobileUnit)) continue;
      const wptT = { wx: 0, wy: 0 };
      pgob.GetCenter(wptT);
      out.push({
        gid: pgob.GetId(),
        local: pgob.GetOwner() === gs.sim.ctx.plrLocal,
        selected: (pgob.GetFlags() & kfGobSelected) !== 0,
        x: coords.PcFromWc(wptT.wx - wpt.wx),
        y: coords.PcFromWc(wptT.wy - wpt.wy),
      });
    }
    return out;
  },
};

// ---- the HTML/DOM general-UI menu (plan Phase 2; ?dom) ----------------------
// The controller (logic + launch boundary) is node-tested; the view renders it.
// Launches forward into the SAME GameHost seam the canvas shell uses; the canvas
// shows only while a game runs.

let domMenu: MenuController | null = null;
// the open COMMUNITY screen's controller + opener (the wiUi automation seams drive these).
let domCommunity: CommunityController | null = null;
let startDomCommunityFn: ((tab?: CommunityTab) => void) | null = null;

function startDomMenu(): void {
  const root = document.createElement('div');
  root.id = 'dom-menu';
  document.body.appendChild(root);
  // the DOM multiplayer lobby (Phase 3) — its own overlay, started on MULTIPLAYER,
  // over the SAME XTransport + runSimulation seam the canvas MP forms use.
  const lobbyRoot = document.createElement('div');
  lobbyRoot.id = 'dom-lobby';
  lobbyRoot.style.display = 'none';
  document.body.appendChild(lobbyRoot);
  // the DOM ADD-ON MISSION PACKS catalog (Phase 3) — its own overlay, started on
  // ADD-ON MISSION PACKS, over the SAME boot()-built gphttp/gppackm/gppim the
  // canvas DownloadMissionPackForm uses.
  const dlcRoot = document.createElement('div');
  dlcRoot.id = 'dom-dlc';
  dlcRoot.style.display = 'none';
  document.body.appendChild(dlcRoot);
  // the native DOM COMMUNITY overlay — one full-viewport screen with PLAYERS (the leaderboard),
  // GAMES (recent MP games + replays, wi-api /stats/games.json) and MY PROFILE (the player's own
  // profile behind the named sign-in gate) tabs, replacing the separate LEADERBOARD / MY PROFILE
  // screens. Fetches JSON from the wi-api worker (kszStatsBase) over the SAME httpGet seam the
  // lobby uses.
  const communityRoot = document.createElement('div');
  communityRoot.id = 'dom-community';
  communityRoot.style.display = 'none';
  document.body.appendChild(communityRoot);
  let communityView: CommunityView | null = null;
  function startDomCommunity(tab: CommunityTab = 'players'): void {
    root.style.display = 'none';
    const ctrl = new CommunityController({
      httpGet: gameHost.lobbyDeps().httpGet,
      onChange: () => communityView?.render(),
      baseUrl: kszStatsBase,
      playerName: currentStatsPlayerName(),
      signIn: signInForProfile,
      signOut: signOutEverywhere,
      openUrl: (url: string) => {
        window.open(url, '_blank');
      },
      // in-client playback (M4): hide the community screen under the viewer overlay and
      // re-show it when the viewer closes (its window keydown ignores keys while hidden).
      watchReplay: (key: string) => {
        communityRoot.style.display = 'none';
        watchReplayInClient(key, () => {
          if (communityView !== null) {
            communityRoot.style.display = 'flex';
            communityView.render();
          }
        });
      },
      storage: {
        get: (k: string) => {
          try {
            return localStorage.getItem(k);
          } catch {
            return null;
          }
        },
        set: (k: string, v: string) => {
          try {
            localStorage.setItem(k, v);
          } catch {
            /* private mode */
          }
        },
      },
    });
    domCommunity = ctrl;
    communityView = new CommunityView({
      controller: ctrl,
      root: communityRoot,
      hideCanvas: () => {
        canvas.style.display = 'none';
      },
      titleBg: bgArt !== null ? titleArtToDataUrl(bgArt) : undefined,
      buttonArt: btnArt ?? undefined,
      playSfx: (sfx: number) => {
        shell.sndm.PlaySfx(sfx);
      },
      openUrl: (url: string) => {
        window.open(url, '_blank');
      },
      onExit: () => {
        communityRoot.style.display = 'none';
        communityView?.destroy();
        communityView = null;
        domCommunity = null;
        root.style.display = 'flex';
        view.render();
      },
    });
    communityRoot.style.display = 'flex';
    ctrl.start(tab);
  }
  startDomCommunityFn = startDomCommunity;

  // Validate name+password against the auth server and, on success, persist the identity so the
  // player is signed in app-wide (the leaderboard pin lights up, MP auto-auths with the saved token).
  // This is LoginHandler.GetToken (a plain HTTPS auth call — NO game-server transport) followed by the
  // same prefs write LoginHandler::SaveToGlobals does after a successful tra.Login; we can't use the
  // full lobby login here because there's no server connection at the menu.
  async function signInForProfile(name: string, password: string): Promise<ProfileSignInResult> {
    const ld = gameHost.lobbyDeps();
    const handler = new LoginHandler({
      mp: ld.mp,
      prefs: ld.env.prefs,
      savePreferences: () => ld.env.savePreferences(),
      httpGet: async (url) => (await ld.httpGet(url)).body,
      deviceId: ld.deviceId,
      platform: ld.platform,
      authUrl: ld.authUrl,
    });
    handler.SetAnonymous(false);
    handler.SetUsername(name);
    handler.SetPassword(password);
    const r = await handler.GetToken();
    if (r === knGetTokenResultSuccess) {
      const prefs = ld.env.prefs;
      prefs.SetString(kszPrefUsername, handler.username());
      prefs.SetString(kszPrefPassword, handler.password());
      prefs.SetString(kszPrefToken, handler.token());
      prefs.SetBool(kfPrefAnonymous, false);
      ld.env.savePreferences();
      return { kind: 'ok', name: handler.username() };
    }
    return r === knGetTokenResultNoPassword ? { kind: 'nopassword' } : { kind: 'fail' };
  }

  // SIGN OUT (from the MY PROFILE modal): clear the persisted identity so the app is signed out
  // everywhere — currentStatsPlayerName then reads no token and returns '' (the leaderboard "your
  // rank" pin goes dark; MP stops auto-authenticating, LoginHandler.SignOut's prefs write). There's
  // no server connection at the menu, so this is a local prefs clear (the inverse of signInForProfile).
  // The username is KEPT so the sign-in / MP-login forms pre-fill it for a quick re-sign-in.
  function signOutEverywhere(): void {
    const prefs = shell.prefs;
    prefs.SetString(kszPrefToken, '');
    prefs.SetString(kszPrefPassword, '');
    SavePreferences(shell.prefs, prefStore, shell.sndm, shell.options, shell.gtGameSpeed);
  }

  let dlcView: DlcView | null = null;
  function startDomDlc(): void {
    root.style.display = 'none';
    const close = (): void => {
      dlcRoot.style.display = 'none';
      dlcView?.destroy();
      dlcView = null;
      // re-enumerate the mission lists so a freshly downloaded pack shows in the
      // Add-On tab next time, then re-show the menu (the MenuController re-reads
      // EnumPacks on its next singlePlayer() call; render() refreshes the view).
      root.style.display = 'flex';
      view.render();
    };
    // PLAY after a download: launch the freshly downloaded pack's FIRST mission,
    // exactly like the canvas Shell::PlayGame WantsPlay seam (shell.ts:504-534 —
    // CreateMissionList(packid, kmltAll) → first mission → PlaySinglePlayer /
    // PlayMultiplayer). The DOM menu's SP launch is gameHost.launchSinglePlayer;
    // for a multiplayer pack we hand the pack to the DOM lobby (startDomLobby).
    const playPack = (packid: PackId): void => {
      const mlDeps = { pakr, packm, ppim, fDemo: false };
      const pmlAll = CreateMissionList(mlDeps, packid, kmltAll);
      if (pmlAll === null || pmlAll.GetCount() === 0) return; // nothing to launch
      const md0 = pmlAll.GetMissionDescription(0);
      if (md0 !== null && pmlAll.IsMultiplayerMissionType(md0.mt)) {
        // a multiplayer pack → the DOM lobby (it picks a room/map from the pack)
        startDomLobby();
        return;
      }
      // a single-player pack → launch its first SP mission (the canvas
      // PlaySinglePlayer scrolls the list to it; the DOM menu launches it).
      const pml = CreateMissionList(mlDeps, packid, kmltSinglePlayer);
      if (pml === null || pml.GetCount() === 0) return;
      const miid = pml.GetMissionIdentifier(0);
      if (miid === null) return;
      // through the menu controller (not gameHost directly): it marks the menu inGame so
      // the view hides — close() above has just re-shown it — and routes game over
      // through the same playLevel seam (incl. the in-game Load Game reload loop).
      controller.launchMission(miid);
    };
    // a FRESH controller per entry — never reuse one holding stale catalog state.
    const ctrl = new DlcController({
      http,
      packm,
      ppim,
      mlDeps: { pakr, packm, ppim, fDemo: false },
      onChange: () => dlcView?.render(),
      openUrl: (url: string) => {
        window.open(url, '_blank');
      },
      // the Custom URL ask: pre-fill + persist through prefs (kszPrefAskUrl), the
      // SAME pref the canvas DownloadMissionPackForm reads/writes (game.cpp). The
      // persist is the SAME SavePreferences round-trip the options form uses.
      askUrlDefault: () => shell.prefs.GetString(kszPrefAskUrl),
      saveAskUrl: (url: string) => {
        shell.prefs.SetString(kszPrefAskUrl, url);
        SavePreferences(shell.prefs, prefStore, shell.sndm, shell.options, shell.gtGameSpeed);
      },
      // done(packid): PLAY launches the freshly downloaded pack's first mission
      // (the canvas WantsPlay seam); done(null): DONE/back to the menu.
      done: (packid: PackId | null) => {
        close();
        if (packid !== null) playPack(packid);
      },
      // REPORT (info line) → the moderator queue (POST /api/reportpack, anonymous did).
      reportPack: (packid: PackId) => reportCommunityPack(packid.id >>> 0),
    });
    dlcView = new DlcView({
      controller: ctrl,
      root: dlcRoot,
      showCanvas: () => {
        canvas.style.display = '';
      },
      hideCanvas: () => {
        canvas.style.display = 'none';
      },
      titleBg: bgArt !== null ? titleArtToDataUrl(bgArt) : undefined,
      buttonArt: btnArt ?? undefined, // the SAME faithful 9-slice chrome as the menu/lobby
      playSfx: (sfx: number) => {
        shell.sndm.PlaySfx(sfx);
      },
    });
    dlcRoot.style.display = 'flex';
  }

  // game/dlmissionpack.cpp:71-106 AskInstallMissionPack over ?dom — the SAME
  // gphttp/gppackm/gppim DLC stack the ADD-ON MISSION PACKS menu (startDomDlc)
  // uses. The C++ pops a Yes/No "you must install/upgrade the mission pack first
  // — download now?" query, then a DownloadBox; this renders both as a DOM modal
  // overlay (faithful chrome shared with the lobby), driving HttpPackManager
  // Install/IsInstalled, and resolves once the pack is (or isn't) installed. It
  // returns the upgraded pack id so the create-game retry re-advertises with it.
  function domAskInstallMissionPack(
    packid: PackId,
    uiTitle: string,
    packTitle: string | null,
  ): Promise<{ installed: boolean; packid?: PackId }> {
    // game/packmanager.cpp IsInstalled: 0 not installed, 1 installed-and-current,
    // 2 needs upgrade (the upgrade target is written into ppackidUpgrade).
    const upgrade: PackId = { id: 0, hash: new Uint8Array(16) };
    const state = packm.IsInstalled(packid, upgrade);
    if (state === 1)
      return Promise.resolve({
        installed: true,
        packid: { id: packid.id >>> 0, hash: packid.hash.slice() },
      });
    // case 2 installs OVER the existing pack id with the resolved hash; case 0 the
    // requested pack (the catalog has its hash). Either way Install() adopts it.
    const target: PackId =
      state === 2
        ? { id: upgrade.id >>> 0, hash: upgrade.hash.slice() }
        : { id: packid.id >>> 0, hash: packid.hash.slice() };
    const prompt =
      state === 2
        ? 'You must upgrade the mission pack first. Download now?'
        : 'You must install the mission pack first. Download now?';

    const s = formScale();
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,0,0,0.45)',
      zIndex: '30',
      fontFamily: 'sans-serif',
    });
    const box = document.createElement('div');
    Object.assign(box.style, {
      position: 'relative',
      width: `${190 * s}px`,
      height: `${70 * s}px`,
      background: 'rgba(7,22,27,0.96)',
      border: `${Math.max(1, Math.round(s))}px solid #fff`,
      boxShadow: '0 6px 24px rgba(0,0,0,0.6)',
      color: '#e8eef0',
    });
    const bar = document.createElement('div');
    Object.assign(bar.style, {
      position: 'absolute',
      left: '0',
      right: '0',
      top: '0',
      height: `${12 * s}px`,
      background: 'rgb(60, 96, 150)',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: `${Math.round(6 * s)}px`,
      fontWeight: '700',
      letterSpacing: '0.06em',
    });
    bar.textContent = uiTitle;
    box.appendChild(bar);
    const msg = document.createElement('div');
    Object.assign(msg.style, {
      position: 'absolute',
      left: `${6 * s}px`,
      top: `${16 * s}px`,
      width: `${178 * s}px`,
      textAlign: 'center',
      fontSize: `${Math.round(5.5 * s)}px`,
      lineHeight: '1.25',
    });
    box.appendChild(msg);
    overlay.appendChild(box);
    document.body.appendChild(overlay);

    return new Promise((resolve) => {
      const close = (result: { installed: boolean; packid?: PackId }): void => {
        try {
          document.body.removeChild(overlay);
        } catch {
          /* already gone */
        }
        resolve(result);
      };
      const mkBtn = (label: string, on: () => void): HTMLButtonElement => {
        const b = document.createElement('button');
        Object.assign(b.style, {
          fontSize: `${Math.round(6 * s)}px`,
          padding: `${2 * s}px ${8 * s}px`,
          cursor: 'pointer',
          background: 'rgb(60, 96, 150)',
          color: '#fff',
          border: 'none',
          borderRadius: `${1 * s}px`,
          fontWeight: '600',
        });
        b.textContent = label;
        b.addEventListener('click', () => {
          shell.sndm.PlaySfx(ksfxGuiButtonTap);
          on();
        });
        return b;
      };
      const buttonRow = (...btns: HTMLButtonElement[]): HTMLElement => {
        const row = document.createElement('div');
        Object.assign(row.style, {
          position: 'absolute',
          left: '0',
          right: '0',
          top: `${50 * s}px`,
          display: 'flex',
          gap: `${8 * s}px`,
          justifyContent: 'center',
        });
        row.replaceChildren(...btns);
        return row;
      };

      // STEP 1 — the Yes/No query (HtMessageBox kidfMessageBoxQuery).
      msg.textContent = prompt;
      const row1 = buttonRow(
        mkBtn('Yes', () => {
          // STEP 2 — the DownloadBox: Install over the SAME HttpPackManager, show
          // progress, then resolve with whether it's installed-and-current now.
          row1.remove();
          msg.textContent = `Downloading ${packTitle ?? 'mission pack'}…`;
          const cb = {
            OnBegin: () => {},
            OnProgress: (_ctx: unknown, cbTotal: number, cbLength: number) => {
              msg.textContent = `Downloaded ${cbTotal} of ${cbLength} bytes…`;
            },
            OnFinished: () => {
              const ok = packm.IsInstalled(target) === 1;
              msg.textContent = ok
                ? 'Download complete!'
                : 'Download complete, but the pack is unavailable.';
              box.appendChild(
                buttonRow(
                  mkBtn('OK', () => close({ installed: ok, packid: ok ? target : undefined })),
                ),
              );
            },
            OnError: (_ctx: unknown, pszError: string) => {
              msg.textContent = `Download error: ${pszError}`;
              box.appendChild(buttonRow(mkBtn('OK', () => close({ installed: false }))));
            },
          };
          if (!packm.Install(target, null, cb)) {
            msg.textContent = 'Could not start the download.';
            box.appendChild(buttonRow(mkBtn('OK', () => close({ installed: false }))));
          }
        }),
        mkBtn('No', () => close({ installed: false })),
      );
      box.appendChild(row1);
    });
  }

  // The canvas LoginForm ACCOUNTINUSE query (loginform.cpp:219-227) as a DOM modal: "Your account
  // is already logged in to this server with another device. Would you like to disconnect that
  // device and then login?" → Yes/No. Same 9-slice-less overlay chrome as domAskInstallMissionPack.
  function domConfirmDisconnectShared(): Promise<boolean> {
    const s = formScale();
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,0,0,0.45)',
      zIndex: '30',
      fontFamily: 'sans-serif',
    });
    const box = document.createElement('div');
    Object.assign(box.style, {
      position: 'relative',
      width: `${190 * s}px`,
      height: `${86 * s}px`,
      background: 'rgba(7,22,27,0.96)',
      border: `${Math.max(1, Math.round(s))}px solid #fff`,
      boxShadow: '0 6px 24px rgba(0,0,0,0.6)',
      color: '#e8eef0',
    });
    const bar = document.createElement('div');
    Object.assign(bar.style, {
      position: 'absolute',
      left: '0',
      right: '0',
      top: '0',
      height: `${12 * s}px`,
      background: 'rgb(60, 96, 150)',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: `${Math.round(6 * s)}px`,
      fontWeight: '700',
      letterSpacing: '0.06em',
    });
    bar.textContent = 'Login Failure';
    box.appendChild(bar);
    const msg = document.createElement('div');
    Object.assign(msg.style, {
      position: 'absolute',
      left: `${6 * s}px`,
      top: `${16 * s}px`,
      width: `${178 * s}px`,
      textAlign: 'center',
      fontSize: `${Math.round(5.5 * s)}px`,
      lineHeight: '1.25',
    });
    msg.textContent =
      'Your account is already logged in to this server with another device. Would you like to disconnect that device and then login?';
    box.appendChild(msg);
    overlay.appendChild(box);
    document.body.appendChild(overlay);

    return new Promise((resolve) => {
      const close = (result: boolean): void => {
        try {
          document.body.removeChild(overlay);
        } catch {
          /* already gone */
        }
        resolve(result);
      };
      const mkBtn = (label: string, on: () => void): HTMLButtonElement => {
        const b = document.createElement('button');
        Object.assign(b.style, {
          fontSize: `${Math.round(6 * s)}px`,
          padding: `${2 * s}px ${8 * s}px`,
          cursor: 'pointer',
          background: 'rgb(60, 96, 150)',
          color: '#fff',
          border: 'none',
          borderRadius: `${1 * s}px`,
          fontWeight: '600',
        });
        b.textContent = label;
        b.addEventListener('click', () => {
          shell.sndm.PlaySfx(ksfxGuiButtonTap);
          on();
        });
        return b;
      };
      const row = document.createElement('div');
      Object.assign(row.style, {
        position: 'absolute',
        left: '0',
        right: '0',
        top: `${66 * s}px`,
        display: 'flex',
        gap: `${8 * s}px`,
        justifyContent: 'center',
      });
      row.replaceChildren(
        mkBtn('Yes', () => close(true)),
        mkBtn('No', () => close(false)),
      );
      box.appendChild(row);
    });
  }

  let lobbyView: LobbyView | null = null;
  function startDomLobby(): void {
    root.style.display = 'none';
    // a FRESH controller per entry — never reuse a controller holding a dead
    // transport / stale screen from a prior session (adversarial-review fix).
    const ctrl = new LobbyController({
      deps: gameHost.lobbyDeps(),
      onChange: () => lobbyView?.render(),
      installMissionPack: domAskInstallMissionPack,
      telemetry: (kind, data) => track(kind, data, 'warn'),
      confirmDisconnectShared: domConfirmDisconnectShared,
    });
    lobbyView = new LobbyView({
      controller: ctrl,
      root: lobbyRoot,
      showCanvas: () => {
        canvas.style.display = '';
      },
      hideCanvas: () => {
        canvas.style.display = 'none';
      },
      titleBg: bgArt !== null ? titleArtToDataUrl(bgArt) : undefined,
      buttonArt: btnArt ?? undefined, // the SAME faithful 9-slice chrome as the menu
      playSfx: (sfx: number) => {
        shell.sndm.PlaySfx(sfx);
      },
      openUrl: (url: string) => {
        window.open(url, '_blank');
      },
      onExit: () => {
        lobbyRoot.style.display = 'none';
        lobbyView?.destroy();
        lobbyView = null;
        root.style.display = 'flex';
        view.render();
      },
      // the error screen's RELOAD (protocol mismatch = this build is stale, #206)
      onReload: () => {
        void forceReloadLatest();
      },
    });
    lobbyRoot.style.display = 'flex';
    void ctrl.start();
  }
  // Shell.cpp:70-87 do { } while (nGo == knGoLoadSavedGame): the in-game "Load Game"
  // tears down the current session (gameOverEvent → knGoLoadSavedGame, stream stashed via
  // setSavedGameStream) and re-launches the chosen save AS gameplay. The DOM menu launches
  // on the host directly (bypassing the canvas Shell), so it owns this loop. Any other
  // outcome (win/lose/abort/app-stop) is the real end → hand it to the menu's done().
  const onGameGo = (nGo: number, done: (nGo: number) => void): void => {
    if (nGo === knGoLoadSavedGame) {
      const pstm = gameHost.takeSavedGameStream(); // = gpstmSavedGame (Shell.cpp:85)
      if (pstm !== null) {
        // an in-game "Load Game" — re-launch the chosen save instead of returning to the
        // menu; the resume slot stays (the next autosave tick re-writes it).
        gameHost.launchSavedGame(pstm, (nGoNext) => onGameGo(nGoNext, done));
        return;
      }
    }
    // Reaching the DOM menu means no game is in progress: drop the resume slot so a
    // refresh at the title does NOT drop back into a finished mission. The session's
    // gameOverEvent already DeleteReinitializeGame's on a clean win/lose (gamesession.ts);
    // this is the canvas LaunchLoop's defensive belt-and-braces (shell.ts:307) for the
    // abort/load-failure/app-stop paths the DOM menu owns. Extracted + unit-tested as
    // ClearResumeSlotOnReturnToMenu (io/savegame.ts); here we always reach it with NO
    // resuming saved game (the resume branch above already returned).
    ClearResumeSlotOnReturnToMenu(saveStore, /* fResumingSavedGame */ false);
    done(nGo);
  };
  // The menu HELP renders the in-game manual. Prefer the loose /help.txt that
  // build-ts-client.mjs stages from data/help.txt (the SAME updatable source the
  // build-panel HelpView fetches) over the pack's htdata2432.pdb copy, which is a frozen
  // pre-built artifact copied from game/ — so a content edit in data/help.txt (e.g. a
  // copyright-year bump) reaches the menu HELP without repacking the pdb. Fetched just
  // below (after the controller/view exist); loadHelp falls back to the pack copy until
  // it lands.
  let looseHelpRaw: string | null = null;
  const controller = new MenuController({
    mlDeps: { pakr, packm, ppim, fDemo: false },
    flow: {
      // The DOM menu launches games DIRECTLY on the host (no canvas Shell::PlayGame),
      // so the knGoLoadSavedGame reload loop (Shell.cpp:70-87 / shell.ts:208-235) must
      // live here too — otherwise an IN-GAME "Load Game" posts gameOverEvent(knGoLoadSavedGame),
      // the session ends, and `done(nGo)` drops to the menu/title instead of resuming the
      // chosen save. On that outcome we re-launch from gameHost.takeSavedGameStream()
      // (the stream stashed by the in-game Load's setSavedGameStream), mirroring the canvas.
      playLevel: (m, nRank, done) => {
        gameHost.launchSinglePlayer(m, nRank, (nGo) => onGameGo(nGo, done));
      },
      playSavedGame: (pstm, done) => {
        gameHost.launchSavedGame(pstm, (nGo) => onGameGo(nGo, done));
      },
      playMultiplayer: () => {
        startDomLobby();
      },
      showAddOnPacks: () => {
        startDomDlc();
      },
      showCommunity: (tab?: CommunityTab) => {
        startDomCommunity(tab);
      },
    },
    onChange: () => {
      view.render();
    },
    openUrl: (url: string) => {
      window.open(url, '_blank');
    },
    saves: makeMenuSaves(saveStore),
    // options: the SAME OptionsState/sndm + SavePreferences round-trip the canvas
    // InGameOptionsForm uses (the shell stays attached)
    options: makeMenuOptionsIO({
      options: shell.options,
      sndm: shell.sndm,
      prefs: shell.prefs,
      prefStore,
      gameSpeed: () => shell.gtGameSpeed,
    }),
    loadHelp: () => {
      if (looseHelpRaw !== null && looseHelpRaw.length > 0) return stripHelpTags(looseHelpRaw);
      const pb = pakr.MapFile('help.txt'); // fallback: the frozen pack copy if /help.txt hasn't loaded
      return pb === null ? '' : stripHelpTags(new TextDecoder().decode(pb));
    },
    // the FULL-FIDELITY manual reads the RAW help.txt (tags intact). Same source as loadHelp
    // (the loose /help.txt, falling back to the frozen pack copy) — NOT stripped.
    loadHelpRaw: () => {
      if (looseHelpRaw !== null && looseHelpRaw.length > 0) return looseHelpRaw;
      const pb = pakr.MapFile('help.txt');
      return pb === null ? '' : new TextDecoder().decode(pb);
    },
    // resolve the manual's inline <IMG SRC="*.anir"> to the pack's 'help'-strip frame — the
    // same ClientAnimationData path the canvas HelpControl renders inline (ui/help.ts).
    resolveHelpImage: (src: string) => resolveHelpImageDataUrl(pakr, tam, src),
  });
  // the real shell chrome from the pack atlas: title backdrop + WI wordmark +
  // the 9-slice button art (faithful chrome — built to tasks/ui-parity-matrix.md)
  const bgArt = extractAtlasArt(tam, 'titlescreenbkgd.png');
  const logoArt = extractAtlasArt(tam, 'title.png');
  const btnArt = extractButtonArt(tam);
  const view = new MenuView({
    controller,
    root,
    // intro-over-black: while the opening CUTSCENE is up the canvas stays HIDDEN
    // (the intro plays over black). Defer the reveal to syncReveal, which shows #screen
    // only once the cutscene clears. Outside a cutscene this is the plain reveal.
    showCanvas: () => syncReveal(),
    hideCanvas: () => {
      canvas.style.display = 'none';
    },
    version: gszVersion,
    titleBg: bgArt !== null ? titleArtToDataUrl(bgArt) : undefined,
    titleLogo: logoArt !== null ? titleArtToDataUrl(logoArt) : undefined,
    // raw 9-slice pieces — the view composites each button into one bitmap at the
    // needed width (the canvas ButtonControl: caps + tiled middle), then scales it
    buttonArt: btnArt ?? undefined,
    playSfx: (sfx: number) => {
      shell.sndm.PlaySfx(sfx);
    },
  });
  domMenu = controller;

  // Load the loose /help.txt (see loadHelp). If the user opened HELP before it arrived
  // (the screen captured the frozen pack copy), refresh it once the loose source lands.
  void fetch('/help.txt')
    .then((r) => (r.ok ? r.text() : ''))
    .then((t) => {
      if (t.length === 0 || t === looseHelpRaw) return;
      looseHelpRaw = t;
      if (controller.screen === 'help') controller.help();
    })
    .catch(() => {});

  // Refresh-to-resume (the canvas Shell::Launch slot-(-3) arm, shell.ts:257-274):
  // a single-player mission autosaves to the resume slot (knGameReinitializeSave)
  // every ~500ms while it runs and on tab-hide (gamesession.ts:1570 / the pagehide
  // flush below). If that slot is present at boot, re-enter gameplay BEFORE showing
  // the title — exactly as the WASM client did. open-with-delete consumes the slot
  // (it's re-written by the next autosave tick); a stale/corrupt save fails
  // launchSavedGame → onGameGo's saveStore.delete + done(knGoInitFailure) drops to
  // the menu (matching the canvas knGoInitFailure fall-through). MP never autosaves
  // the slot, so this is single-player only by construction.
  const resumeStm = HostOpenSaveGameStream(saveStore, knGameReinitializeSave, true);
  if (resumeStm !== null) controller.resume(resumeStm);
}

// the DOM-menu automation handle (the e2e harness reads it; cf. window.wiTs)
declare global {
  interface Window {
    wiUi?: Record<string, unknown>;
  }
}

window.wiUi = {
  active: () => domMenu !== null,
  screen: () => domMenu?.screen ?? null,
  inGame: () => domMenu?.inGame ?? false,
  // Clear Fog — the SAME fogm.RevealAll() the Test Options panel action and
  // SimUIForm::SetObserving use. Exposed as an automation seam because a freshly
  // started mission is almost entirely unexplored black, which makes the captured
  // in-game shots useless for anything that wants to show the map (the marketing
  // screenshot sweep, screenshot-all.mjs). Returns false when no game is running.
  // This adds no capability a player does not already have: the Test Options panel
  // ships this action, and it is a single-player concern — MP is unaffected (each
  // client runs its own sim and the leaderboard is server-scored).
  clearFog: () => {
    const session = gameHost.runningGame();
    const ctx = session?.sim.ctx;
    const fogm = ctx?.fogm;
    if (session === null || ctx === undefined || fogm === undefined || fogm === null) return false;
    // SINGLE-PLAYER ONLY. Revealing the fog in a rated multiplayer game is a real
    // competitive advantage — it does not desync (fog is client-side visibility over an
    // identical sim) but it shows you the whole enemy position on a leaderboard game.
    // The shipped Test Options panel is at least gated behind the prod debug token; an
    // ungated wiUi seam would drop that to one devtools call, so guard it the same way
    // God mode is guarded (fingerhandler: ctx.fGodMode && !ctx.fMultiplayer).
    if (ctx.fMultiplayer) return false;
    fogm.RevealAll();
    // RevealAll only marks the MINIMAP dirty-all; the playfield is damage-driven, so
    // tiles that were fogged stay painted black until something else damages them —
    // leaving stale black rectangles wherever an overlay sat. Force the full repaint,
    // exactly as SimUIForm::SetObserving does after its own RevealAll().
    session.evm.SetRedrawFlags(kfRedrawDirty);
    return true;
  },
  // Force a full playfield repaint. The canvas is damage-driven and is NOT invalidated
  // when a DOM overlay closes or shrinks, so the region a previous overlay covered keeps
  // its stale contents. Normally invisible (that area is usually fogged black anyway);
  // obvious once the fog is cleared, as a black rectangle hanging off the panel.
  forceRedraw: () => {
    const session = gameHost.runningGame();
    if (session === null) return false;
    session.evm.SetRedrawFlags(kfRedrawDirty);
    return true;
  },
  // Centre the view on the local side's forces. The capture sweep opens a mission and
  // shoots wherever the map happens to start, which is often empty terrain — this points
  // the camera at the player's base, and in a mission that opens under attack (c_05
  // "Get Ready") that is exactly where the fighting is. Returns the world point used,
  // or null when there is no running game / no local player / nothing to centre on.
  focusLocalForces: () => {
    const session = gameHost.runningGame();
    const ctx = session?.sim.ctx;
    const side = ctx?.plrLocal?.GetSide();
    if (session === null || ctx === undefined || side === undefined) return null;
    const { ctx: mapW, cty: mapH } = ctx.gobm.GetMapSize();
    const found: Gob[] = [];
    const bounds = new TRect();
    bounds.Set(0, 0, mapW - 1, mapH - 1);
    ctx.gobm.FindGobsT(bounds, found, 4096);
    let sx = 0,
      sy = 0,
      n = 0;
    const wpt = { wx: 0, wy: 0 };
    for (const gob of found) {
      if (gob.GetSide() !== side) continue;
      gob.GetPosition(wpt);
      sx += wpt.wx;
      sy += wpt.wy;
      n++;
    }
    if (n === 0) return null;
    const wx = Math.round(sx / n),
      wy = Math.round(sy / n);
    session.minimapSetViewPos(wx, wy);
    session.evm.SetRedrawFlags(kfRedrawDirty);
    return { wx, wy, gobs: n };
  },
  missions: () =>
    (domMenu?.missions() ?? []).map((r) => ({ lvl: r.lvl, title: r.title, bucket: r.bucket })),
  play: () => {
    domMenu?.play();
  },
  singlePlayer: () => {
    domMenu?.singlePlayer();
  },
  back: () => {
    domMenu?.back();
  },
  // launch a mission by level filename (resolves the row's list slot)
  launchMission: (lvl: string) => {
    const m = domMenu?.missions().find((r) => r.lvl === lvl);
    if (m === undefined) return false;
    domMenu!.launch(m.index);
    return true;
  },
  // options + help (Phase 2 part 2)
  openOptions: () => {
    domMenu?.options();
  },
  openHelp: () => {
    domMenu?.help();
  },
  // FULL-FIDELITY HELP manual probes (the smoke drives the rendered DOM directly: clicks the
  // INDEX/BACK/MENU buttons + TOC cross-links; these helpers expose the manual's state so the
  // harness can assert image resolution + the link-jump/Back nav worked).
  helpHasManual: () => domMenu?.hasHelpManual() ?? false,
  helpImageReport: () => {
    const body = document.querySelector('[data-help-manual]');
    if (body === null) return null;
    const imgs = [...body.querySelectorAll('[data-help-img]')].map(
      (n) => (n as HTMLElement).dataset.helpImg,
    );
    const missing = [...body.querySelectorAll('[data-help-img-missing]')].map(
      (n) => (n as HTMLElement).dataset.helpImgMissing,
    );
    return { resolved: imgs, unresolved: missing };
  },
  difficulty: () => domMenu?.difficulty() ?? null,
  setDifficulty: (d: string) => {
    domMenu?.setDifficulty(d as Difficulty);
  },
  muted: () => domMenu?.muted() ?? false,
  setMuted: (f: boolean) => {
    domMenu?.setMuted(f);
  },
  saveOptions: () => {
    domMenu?.saveOptions();
  },
  helpText: () => domMenu?.helpContent() ?? '',
  openLoad: () => {
    domMenu?.load();
  },
  savedGames: () => (domMenu?.savedGames() ?? []).map((s) => ({ slot: s.slot, name: s.name })),
  loadSlot: (slot: number) => {
    domMenu?.loadSlot(slot);
  },
  // ADD-ON MISSION PACKS (Phase 3) — open the catalog overlay (the DlcView lives
  // in #dom-dlc; the smoke drives it by clicking the rendered buttons).
  openAddOnPacks: () => {
    domMenu?.showAddOnPacks();
  },
  dlcUp: () => getComputedStyle(document.getElementById('dom-dlc')!).display !== 'none',
  // COMMUNITY (PLAYERS / GAMES / MY PROFILE tabs) — the e2e harness opens the screen, switches
  // tabs, and reads the loaded rows without scraping the rendered DOM.
  openCommunity: (tab?: string) => {
    startDomCommunityFn?.(tab as CommunityTab | undefined);
  },
  communityUp: () => {
    const el = document.getElementById('dom-community');
    return el !== null && getComputedStyle(el).display !== 'none';
  },
  communityTab: () => domCommunity?.tab ?? null,
  setCommunityTab: (tab: string) => {
    domCommunity?.setTab(tab as CommunityTab);
  },
  communityPlayers: () =>
    (domCommunity?.players.rows ?? []).map((r) => ({ rank: r.rank, name: r.name })),
  communityGames: () =>
    (domCommunity?.visibleGames() ?? []).map((g) => ({
      key: g.key,
      map: g.map,
      hasReplay: g.hasReplay,
    })),
  communityStatus: () => ({
    players: domCommunity?.players.status ?? null,
    games: domCommunity?.gamesStatus ?? null,
    profile: domCommunity?.profile.mode ?? null,
  }),
  communityOpenPlayer: (name: string) => {
    domCommunity?.openPlayer(name);
  },
  communityWatch: (replayKey: string) => {
    domCommunity?.watchReplay(replayKey);
  },
  // OBJECTIVES briefing (Phase 5) — trigger the in-game briefing the SAME way the
  // credits-label tap does (SP pause held, resumed on OK), WITHOUT driving the
  // canvas pause menu. Shows the DOM overlay (#dom-objectives). The smoke drives
  // the overlay by clicking its rendered buttons.
  openObjectives: () => {
    gameHost.runningGame()?.openObjectivesForAutomation();
  },
  objectivesUp: () => {
    const el = document.getElementById('dom-objectives');
    return (
      (gameHost.runningGame()?.objectivesUp() ?? false) ||
      (el !== null && getComputedStyle(el).display !== 'none')
    );
  },
  closeObjectives: () => {
    objectivesClose();
  },
  // E-COM OVERLAY (Phase 5, Form 6) — the in-mission narrative overlay. It is driven
  // by the REAL game (host.Ecom → Ecom() → env.domEcom) inside the story-modal queue;
  // the smoke launches s_01 (whose intro IS an E-Com) and reads/dismisses it here.
  // ecomForceShow previews a given from/to/message/large in isolation (no live game).
  ecomUp: () => {
    const el = document.getElementById('dom-ecom');
    return el !== null && getComputedStyle(el).display !== 'none' && el.childElementCount > 0;
  },
  // the CURRENTLY-REVEALED message text of the E-Com overlay (the typewriter output).
  // The smoke captures this at t0 and t+Δ and asserts it GREW — proving the DOM timer
  // drives the reveal (unlike the frozen canvas one). The speaker-name prefixes ride
  // in the text; the colors are on the <span>s (ecomColors() reads those).
  ecomText: () => {
    const el = document.getElementById('dom-ecom');
    const box = el?.querySelector('[data-ecom-text]');
    if (!box) return '';
    // the box holds the FULL page text (stable wrap); the typewriter reveals by flipping
    // spans from the invisible style to visible, so the REVEALED text is the VISIBLE spans.
    return [...box.querySelectorAll('span')]
      .filter((s) => s.style.visibility !== 'hidden')
      .map((s) => s.textContent ?? '')
      .join('');
  },
  // the visible button label of the E-Com overlay ("More…" / "OK"), '' when hidden.
  ecomButton: () => {
    const el = document.getElementById('dom-ecom');
    const b = el?.querySelector('[data-ecom-ok]') as HTMLElement | null;
    if (b === null || b === undefined || getComputedStyle(b).visibility === 'hidden') return '';
    return (b.textContent ?? '').trim();
  },
  // the distinct CSS colors of the revealed text spans (the smoke asserts a multi-
  // speaker message renders >1 speaker color — proving the speaker coloring works).
  ecomColors: () => {
    const el = document.getElementById('dom-ecom');
    if (el === null) return [];
    const box = el.querySelector('[data-ecom-text]');
    if (box === null) return [];
    // only the VISIBLE (revealed) spans — the box now carries the not-yet-revealed text
    // in invisible spans (stable-wrap reveal), whose colors are not "revealed" yet.
    return [
      ...new Set(
        [...box.querySelectorAll('span')]
          .filter((s) => s.style.visibility !== 'hidden')
          .map((s) => s.style.color)
          .filter((c) => c !== ''),
      ),
    ];
  },
  // does the LARGE panel show its From/To portraits (the <img>s)?
  ecomPortraits: () => {
    const el = document.getElementById('dom-ecom');
    if (el === null) return 0;
    return el.querySelectorAll('[data-ecom-portrait]').length;
  },
  // tap the overlay backdrop → reveal the whole current page (ShowAll). The smoke
  // uses this to skip the typewriter, then asserts the OK/More button appears.
  ecomTap: () => {
    const el = document.getElementById('dom-ecom');
    el?.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true }));
  },
  // click the OK / More… button (advance a page / dismiss).
  ecomOk: () => {
    const el = document.getElementById('dom-ecom');
    const b = el?.querySelector('[data-ecom-ok]') as HTMLButtonElement | null;
    b?.click();
  },
  // E-COM PREVIEW (Phase 5, Form 6) — mount the REAL EcomView for a given
  // from/to/message/large in isolation (no live game), for the multi-speaker +
  // LARGE-portrait capture. onClose just unmounts (there is no story-modal queue
  // to release). The from/to default to Olstrom→Andy; pass a multi-speaker message
  // ("@OHello @AHi there") to exercise the speaker colors + names + paging.
  ecomForceShow: (
    opts: {
      fromIdx?: number;
      toIdx?: number;
      message?: string;
      large?: boolean;
      nBackground?: number;
    } = {},
  ) => {
    if (ecomMount === null) return false;
    const large = opts.large ?? false;
    const info: EcomInfo = {
      fromIdx: opts.fromIdx ?? 3, // Olstrom
      toIdx: opts.toIdx ?? 1, // Andy
      message: opts.message ?? '@OWelcome, Commander. @AThank you, sir.',
      large,
      nBackground: opts.nBackground ?? (large ? 2 : 1),
      fMore: false,
    };
    ecomMount(info, () => {
      /* preview: no story-modal queue to release */
    });
    return true;
  },
  closeEcomPreview: () => {
    ecomUnmount?.();
  },
  // CUTSCENE PLAYER (Phase 5, kidfCutScene 1047) — the in-mission "opening screens"
  // overlay (a TOP image + typed narration that reflows below it, paged with "More…").
  // Driven by the REAL game (host.CutScene → CutScene() → env.domCutScene) inside the
  // story-modal queue; the smoke launches s_00 (a PURE cutscene mission) and
  // reads/dismisses it here. cutsceneForceShow previews a given message in isolation.
  cutsceneUp: () => {
    const el = document.getElementById('dom-cutscene');
    return el !== null && getComputedStyle(el).display !== 'none' && el.childElementCount > 0;
  },
  // the CURRENTLY-REVEALED narration text of the cutscene overlay (the typewriter
  // output). The smoke captures this at t0 and t+Δ and asserts it GREW — proving the
  // DOM browser-timer drives the reveal while the SP sim is paused.
  cutsceneText: () => {
    const el = document.getElementById('dom-cutscene');
    const box = el?.querySelector('[data-cutscene-text]');
    if (!box) return '';
    // the box holds the FULL narration page (stable wrap); the REVEALED text is the
    // VISIBLE spans (unrevealed chars ride along invisibly to fix the wrapping).
    return [...box.querySelectorAll('span')]
      .filter((s) => s.style.visibility !== 'hidden')
      .map((s) => s.textContent ?? '')
      .join('');
  },
  // the rendered cutscene image's {fp, w, h, …} in CSS px (or null when hidden) — the
  // smoke asserts the image FITS the 150-design panel (NOT 2× oversized) + below it
  // sits the text. The width must be ≤ the panel width (BITMAP 150 design × scale). `fp`
  // is a stable per-image fingerprint (full-src length + natural size) that
  // DISTINGUISHES images — the 24-char data-URL prefix is identical for every PNG, so
  // the smoke's image-SWAP assertion needs this.
  cutsceneImg: () => {
    const el = document.getElementById('dom-cutscene');
    const img = el?.querySelector('[data-cutscene-img]') as HTMLImageElement | null;
    if (img === null || img === undefined) return null;
    const r = img.getBoundingClientRect();
    const stage = el?.querySelector('[data-cutscene-stage]') as HTMLElement | null;
    const sr = stage?.getBoundingClientRect();
    const src = img.getAttribute('src') ?? '';
    return {
      fp: `${src.length}:${img.naturalWidth}x${img.naturalHeight}`,
      w: r.width,
      h: r.height,
      stageW: sr?.width ?? 0,
      stageH: sr?.height ?? 0,
      left: r.left - (sr?.left ?? 0),
      top: r.top - (sr?.top ?? 0),
    };
  },
  // the visible button label of the cutscene overlay ("More…" / "OK"), '' when hidden.
  cutsceneButton: () => {
    const el = document.getElementById('dom-cutscene');
    const b = el?.querySelector('[data-cutscene-ok]') as HTMLElement | null;
    if (b === null || b === undefined || getComputedStyle(b).visibility === 'hidden') return '';
    return (b.textContent ?? '').trim();
  },
  // tap the overlay backdrop → reveal the whole current page (ShowAll). The smoke uses
  // this to skip the typewriter, then asserts the OK/More button appears.
  cutsceneTap: () => {
    const el = document.getElementById('dom-cutscene');
    el?.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true }));
  },
  // click the OK / More… button (advance a page / dismiss).
  cutsceneOk: () => {
    const el = document.getElementById('dom-cutscene');
    const b = el?.querySelector('[data-cutscene-ok]') as HTMLButtonElement | null;
    b?.click();
  },
  // automation-only: surface the cutscene immediately (no story-modal queue) — used to
  // confirm the overlay mounts even if a real-game trigger is hard to drive headless.
  cutsceneForceShow: (opts: { message?: string } = {}) => {
    if (cutSceneMount === null) return false;
    const info: CutSceneInfo = {
      // the real s_00 intro by default (orbitshot4.rbm + galaxite.rbm), so the preview
      // loads REAL atlas cutscene art at full resolution.
      message:
        opts.message ??
        '<img orbitshot4.rbm>ACME Corporation: Icarus Survey Report. ' +
          'We have confirmed massive fields of Galaxite, the galaxy’s most valuable mineral. ' +
          '<img galaxite.rbm>I estimate trillions of credits. Let’s get the mining teams down here!',
    };
    cutSceneMount(info, () => {
      /* preview: no story-modal queue to release */
    });
    return true;
  },
  closeCutscenePreview: () => {
    cutSceneUnmount?.();
  },
  // "ARE YOU SURE?" CONFIRMATION (Phase 5 — build/order STEP 1) — the in-game
  // kidfAreYouSure overlay. Driven by the REAL game (BuildUI.PopupConfirmation →
  // env.domConfirm) on SELL BUILDING / ABORT BUILD; confirmForceShow previews a given
  // title in isolation (no live game) since the canvas unit context menu that triggers
  // it isn't ported yet. confirmYes/confirmNo click the rendered buttons → the
  // ConfirmView reports the choice (YES → the command action in buildui.ts; NO → none).
  confirmUp: () => {
    const el = document.getElementById('dom-confirm');
    return el !== null && getComputedStyle(el).display !== 'none' && el.childElementCount > 0;
  },
  // the runtime title (kidcTitle) rendered on the overlay (e.g. "SELL BUILDING").
  confirmTitle: () => {
    const el = document.getElementById('dom-confirm');
    const t = el?.querySelector('[data-confirm-title]') as HTMLElement | null;
    return t ? (t.textContent ?? '') : '';
  },
  // the static "Are you sure?" prompt text (asserts the body renders).
  confirmPrompt: () => {
    const el = document.getElementById('dom-confirm');
    const p = el?.querySelector('[data-confirm-prompt]') as HTMLElement | null;
    return p ? (p.textContent ?? '') : '';
  },
  // click YES (kidcOk) → onResult(true). Returns whether the button was present.
  confirmYes: () => {
    const el = document.getElementById('dom-confirm');
    const b = el?.querySelector('[data-confirm-yes]') as HTMLButtonElement | null;
    if (b === null || b === undefined) return false;
    b.click();
    return true;
  },
  // click NO (kidcCancel) → onResult(false). Returns whether the button was present.
  confirmNo: () => {
    const el = document.getElementById('dom-confirm');
    const b = el?.querySelector('[data-confirm-no]') as HTMLButtonElement | null;
    if (b === null || b === undefined) return false;
    b.click();
    return true;
  },
  // CONFIRM PREVIEW — mount the REAL ConfirmView for a given title in isolation (no
  // live game). onResult just unmounts (the preview has no command continuation to
  // run). The smoke uses this to render + assert the overlay since the canvas unit
  // context menu that triggers the confirm in-game isn't ported yet (STEP 1 scope).
  confirmForceShow: (title = 'SELL BUILDING') => {
    if (confirmMount === null) return false;
    // preview: no gob anchor (null ⇒ screen-centred) + a stand-in side colour.
    confirmMount(title, 'rgb(40, 86, 150)', null, () => {
      /* preview: no continuation */
    });
    return true;
  },
  closeConfirmPreview: () => {
    confirmUnmount?.();
  },
  // MESSAGE BOX (Phase 5 — survey I10) — the in-game HtMessageBox family overlay
  // (alerts / warnings / errors / Yes-No queries). Driven by the REAL game (HtMessageBox
  // → env.domMessageBox) on the resign/leave WARNING, the GAME OVER observe query, the
  // MP comm/server-message errors. messageBoxForceShow renders a given title/message/
  // variant in isolation (those triggers need a live MP game / error path); the view
  // reports OK/Yes/No → wiUi.messageBoxResult captures the last reported choice.
  messageBoxUp: () => {
    const el = document.getElementById('dom-messagebox');
    return el !== null && getComputedStyle(el).display !== 'none' && el.childElementCount > 0;
  },
  // the runtime title (kidcTitle) rendered on the overlay (e.g. "Warning").
  messageBoxTitle: () => {
    const el = document.getElementById('dom-messagebox');
    const t = el?.querySelector('[data-msgbox-title]') as HTMLElement | null;
    return t ? (t.textContent ?? '') : '';
  },
  // the full message body (kidcMessage) text — asserts it renders unclipped.
  messageBoxMessage: () => {
    const el = document.getElementById('dom-messagebox');
    const m = el?.querySelector('[data-msgbox-message]') as HTMLElement | null;
    return m ? (m.textContent ?? '') : '';
  },
  // the visible buttons in render order ('OK' | 'Yes' | 'No') — asserts the variant.
  messageBoxButtons: () => {
    const el = document.getElementById('dom-messagebox');
    if (el === null) return [];
    return ['data-msgbox-ok', 'data-msgbox-yes', 'data-msgbox-no']
      .map((a) => el.querySelector(`[${a}]`) as HTMLButtonElement | null)
      .filter((b): b is HTMLButtonElement => b !== null)
      .map((b) => b.textContent ?? '');
  },
  // is the title bar RED (the unconditional HtMessageBox SetTitleColor(kiclrRed))?
  messageBoxTitleBarRed: () => {
    const el = document.getElementById('dom-messagebox');
    const stage = el?.querySelector('[data-msgbox-stage]') as HTMLElement | null;
    if (stage === null || stage === undefined) return false;
    // the title bar is the child whose background is the red title colour.
    const bar = [...stage.children].find(
      (c) => getComputedStyle(c as HTMLElement).backgroundColor === 'rgb(252, 0, 0)',
    );
    return bar !== undefined;
  },
  // click OK (kidcOk, the OK-only variant) → done(true). Returns whether present.
  messageBoxOk: () => {
    const el = document.getElementById('dom-messagebox');
    const b = el?.querySelector('[data-msgbox-ok]') as HTMLButtonElement | null;
    if (b === null || b === undefined) return false;
    b.click();
    return true;
  },
  // click YES (kidcOk, the query variant) → done(true). Returns whether present.
  messageBoxYes: () => {
    const el = document.getElementById('dom-messagebox');
    const b = el?.querySelector('[data-msgbox-yes]') as HTMLButtonElement | null;
    if (b === null || b === undefined) return false;
    b.click();
    return true;
  },
  // click NO (kidcCancel, the query variant) → done(false). Returns whether present.
  messageBoxNo: () => {
    const el = document.getElementById('dom-messagebox');
    const b = el?.querySelector('[data-msgbox-no]') as HTMLButtonElement | null;
    if (b === null || b === undefined) return false;
    b.click();
    return true;
  },
  // the LAST choice the view reported through done() (null until one fires) — proves the
  // click resolved with the right value. Reset on each messageBoxForceShow.
  messageBoxResult: () => messageBoxLastResult,
  // MESSAGE BOX PREVIEW — mount the REAL MessageBoxView for a given title/message/variant
  // in isolation (no live MP game). done() records the choice into messageBoxLastResult,
  // so the smoke can assert OK/Yes/No resolved correctly. The whiteBorder flag mirrors
  // kfMbWhiteBorder (the MP forms set it; the in-game resign/observe do not).
  messageBoxForceShow: (
    title = 'Warning',
    message = 'You cannot return to a multiplayer ' +
      'game in progress. Are you sure you want to leave?',
    variant: 'ok' | 'query' = 'query',
    whiteBorder = false,
  ) => {
    if (messageBoxMount === null) return false;
    messageBoxLastResult = null;
    messageBoxMount(title, message, variant, whiteBorder, (fOk) => {
      messageBoxLastResult = fOk;
    });
    return true;
  },
  closeMessageBoxPreview: () => {
    messageBoxUnmount?.();
  },
  // GOB CONTEXT MENU (Phase 5 — build/order STEP 2) — the in-game UnitMenu overlay.
  // Driven by the REAL game (BuildUI.PopupMenu → env.domUnitMenu) on a tap of an owned
  // gob. unitMenuForceShow(gid) drives the SAME PopupMenu path on a chosen owned gob
  // (the canvas tap → host.PopupMenu → buildui.PopupMenu). The view re-polls the live
  // read-model; unitMenuButtons()/unitMenuSelect(idc) read + click the rendered buttons.
  unitMenuUp: () => {
    const el = document.getElementById('dom-unitmenu');
    return el !== null && getComputedStyle(el).display !== 'none' && el.childElementCount > 0;
  },
  // the rendered title (kidcTitle, the gob name).
  unitMenuTitle: () => {
    const el = document.getElementById('dom-unitmenu');
    const t = el?.querySelector('[data-unit-menu-title]') as HTMLElement | null;
    return t ? (t.textContent ?? '') : '';
  },
  // the visible buttons: { idc, label, enabled, hasArt } in render order — the smoke
  // asserts InitMenu parity (which buttons for which gob).
  unitMenuButtons: () => {
    const el = document.getElementById('dom-unitmenu');
    if (el === null) return [];
    return [...el.querySelectorAll('[data-unit-menu-btn]')].map((b) => {
      const e = b as HTMLElement;
      return {
        idc: Number(e.dataset.unitMenuBtn),
        label: e.getAttribute('aria-label') ?? e.textContent ?? '',
        enabled: e.dataset.unitMenuEnabled === '1',
        hasArt: e.dataset.unitMenuArt === '1',
      };
    });
  },
  // the "Can't Transform…" label (kidfMobileHqMenu), '' when not shown.
  unitMenuCantTransform: () => {
    const el = document.getElementById('dom-unitmenu');
    const t = el?.querySelector('[data-unit-menu-cant-transform]') as HTMLElement | null;
    return t ? (t.textContent ?? '') : '';
  },
  // the panel's screen rect (CSS px) — the smoke asserts it anchors near the gob.
  unitMenuRect: () => {
    const el = document.getElementById('dom-unitmenu');
    const p = el?.querySelector('[data-unit-menu-panel]') as HTMLElement | null;
    if (p === null || p === undefined) return null;
    const r = p.getBoundingClientRect();
    return { left: r.left, top: r.top, w: r.width, h: r.height };
  },
  // the per-button bounding rects (CSS px) in render order — the smoke asserts a
  // multi-button menu (HQ) lays its buttons in a single HORIZONTAL ROW (~equal tops),
  // matching the canvas SetOwner button bar (B-G1).
  unitMenuButtonRects: () => {
    const el = document.getElementById('dom-unitmenu');
    if (el === null) return [];
    return [...el.querySelectorAll('[data-unit-menu-btn]')].map((b) => {
      const r = (b as HTMLElement).getBoundingClientRect();
      return {
        idc: Number((b as HTMLElement).dataset.unitMenuBtn),
        left: r.left,
        top: r.top,
        w: r.width,
        h: r.height,
      };
    });
  },
  // the chrome the canvas OnPaintBackground draws (buildui.ts:664-683): the TITLE strip
  // has a 1px black border (B-G2); the BUTTON BAR has a 1px black border EXCEPT a
  // single-button menu where it is suppressed (B-G3, kind === 'single'). Returns the
  // computed border + the bar kind so the smoke can assert the faithful chrome.
  unitMenuChrome: () => {
    const el = document.getElementById('dom-unitmenu');
    if (el === null) return null;
    const title = el.querySelector('[data-unit-menu-title]') as HTMLElement | null;
    const bar = el.querySelector('[data-unit-menu-bar]') as HTMLElement | null;
    const hasBlackBorder = (e: HTMLElement | null): boolean => {
      if (e === null) return false;
      const cs = getComputedStyle(e);
      const w = parseFloat(cs.borderTopWidth);
      // rgb(0, 0, 0) — the kiclrBlack DrawBorder frame.
      return w >= 1 && /\b0,\s*0,\s*0\b/.test(cs.borderTopColor);
    };
    return {
      titleBorder: hasBlackBorder(title),
      barKind: bar ? (bar.dataset.unitMenuBar ?? '') : null, // '1' (bordered) | 'single' (no border) | null (no bar)
      barBorder: hasBlackBorder(bar),
    };
  },
  // click a button by idc → onSelect (the host routes to OnMenuItemSelected). Returns
  // whether the (enabled) button was present + clicked.
  unitMenuSelect: (idc: number) => {
    const el = document.getElementById('dom-unitmenu');
    const b = el?.querySelector(`[data-unit-menu-btn="${idc}"]`) as HTMLButtonElement | null;
    if (b === null || b === undefined || b.dataset.unitMenuEnabled !== '1') return false;
    b.click();
    return true;
  },
  // enumerate the LIVE game's gobs with their type + owner + on-screen rect (game px ×
  // gameScale → CSS px, the SAME anchor pipeline) — the smoke uses this to find an owned
  // HQ + a plain structure to pop a menu on. No mobile-only filter (unlike units()).
  gobs: () => {
    const gs = gameHost.runningGame();
    if (gs === null) return [];
    const wpt = { wx: 0, wy: 0 };
    gs.sui.GetViewPos(wpt);
    const xv = coords.PcFromUwc(wpt.wx) & 0xfffe;
    const yv = coords.PcFromUwc(wpt.wy) & 0xfffe;
    const out: {
      gid: number;
      type: number;
      ut: number;
      local: boolean;
      x: number;
      y: number;
      w: number;
      h: number;
    }[] = [];
    const gobm = gs.sim.ctx.gobm;
    for (let pgob = gobm.GetFirstGob(); pgob !== null; pgob = gobm.GetNextGob(pgob)) {
      const b = (
        pgob as {
          GetConsts?(): {
            wrcUIBounds?: { left: number; top: number; right: number; bottom: number };
          };
        }
      ).GetConsts?.();
      const ui = b?.wrcUIBounds;
      const pos = { wx: 0, wy: 0 };
      pgob.GetPosition(pos);
      const px = coords.PcFromWc(pos.wx) - xv;
      const py = coords.PcFromWc(pos.wy) - yv;
      out.push({
        gid: pgob.GetId(),
        type: pgob.GetType(),
        ut: (pgob as { GetUnitType?(): number }).GetUnitType?.() ?? -1,
        local: pgob.GetOwner() === gs.sim.ctx.plrLocal,
        x: ui ? coords.PcFromWc(ui.left) + px : px,
        y: ui ? coords.PcFromWc(ui.top) + py : py,
        w: ui ? coords.PcFromWc(ui.right) - coords.PcFromWc(ui.left) : 0,
        h: ui ? coords.PcFromWc(ui.bottom) - coords.PcFromWc(ui.top) : 0,
      });
    }
    return out;
  },
  // drive the REAL UnitMenu flow on an owned gob (gid) — the SAME path the canvas tap
  // takes (host.PopupMenu → buildui.PopupMenu → env.domUnitMenu). Returns whether the
  // gob was found + the menu fired.
  unitMenuForceShow: (gid: number) => {
    const gs = gameHost.runningGame();
    if (gs === null) return false;
    const gobm = gs.sim.ctx.gobm;
    for (let pgob = gobm.GetFirstGob(); pgob !== null; pgob = gobm.GetNextGob(pgob)) {
      if (pgob.GetId() === gid) {
        gs.buildui.PopupMenu(pgob);
        return unitMenuUnmount !== null;
      }
    }
    return false;
  },
  closeUnitMenuPreview: () => {
    unitMenuUnmount?.();
  },
  // UNIT BUILD CAROUSEL (Phase 5, build/order STEP 3) — the in-game VTS/HRC build panel
  // (MobileUnitBuildForm) DOM overlay. buildPanelForceShow(gid) drives the SAME flow the
  // canvas Build tap takes (host PopupMenu → Build → buildui.OnMenuItemSelected(kidcBuild)
  // → env.domBuildPanel) on a chosen owned VTS/HRC builder. The view re-polls the live
  // read-model; buildPanelUnits()/buildPanelSelect(ut)/buildPanelOrder() read + drive the
  // rendered carousel. The command stays in buildui.ts (proven by the seam unit test).
  buildPanelUp: () => {
    const el = document.getElementById('dom-buildpanel');
    return el !== null && getComputedStyle(el).display !== 'none' && el.childElementCount > 0;
  },
  // the heading ("ORDER VEHICLE" / "RECRUIT PERSONNEL").
  buildPanelHeading: () => {
    const el = document.getElementById('dom-buildpanel');
    const t = el?.querySelector('[data-buildpanel-heading]') as HTMLElement | null;
    return t ? (t.textContent ?? '') : '';
  },
  // the rendered icon list: { ut, disabled, selected, queueCount, building } in render order.
  buildPanelUnits: () => {
    const el = document.getElementById('dom-buildpanel');
    if (el === null) return [];
    return [...el.querySelectorAll('[data-buildpanel-item]')].map((r) => {
      const e = r as HTMLElement;
      const cnt = e.querySelector('[data-buildpanel-count]') as HTMLElement | null;
      const prog = e.querySelector('[data-buildpanel-progress]') as HTMLElement | null;
      const icon = e.querySelector('[data-buildpanel-icon]') as HTMLElement | null;
      return {
        ut: Number(e.dataset.buildpanelItem),
        disabled: e.dataset.buildpanelDisabled === '1',
        selected: e.dataset.buildpanelSelected === '1',
        hasIcon: icon !== null,
        queueCount: cnt !== null && cnt.style.display !== 'none' ? Number(cnt.textContent) : 0,
        building: prog !== null && prog.style.display !== 'none',
      };
    });
  },
  // the selected-unit stat block (the rendered text + which meters have pips).
  buildPanelInfo: () => {
    const el = document.getElementById('dom-buildpanel');
    if (el === null) return null;
    const txt = (sel: string): string => {
      const e = el.querySelector(sel) as HTMLElement | null;
      return e ? (e.textContent ?? '') : '';
    };
    const pips = (key: string): number => {
      const m = el.querySelector(`[data-buildpanel-meter="${key}"]`) as HTMLElement | null;
      return m ? m.childElementCount : 0;
    };
    const dmg = el.querySelector('[data-buildpanel-damage]') as HTMLElement | null;
    return {
      name: txt('[data-buildpanel-name]'),
      cost: txt('[data-buildpanel-value="cost"]'),
      armor: txt('[data-buildpanel-value="armor"]'),
      speed: txt('[data-buildpanel-value="speed"]'),
      range: txt('[data-buildpanel-value="range"]'),
      description: txt('[data-buildpanel-description]'),
      costPips: pips('cost'),
      armorPips: pips('armor'),
      speedPips: pips('speed'),
      rangePips: pips('range'),
      damagePips: dmg ? dmg.childElementCount : 0,
    };
  },
  // the limit label is shown (at the unit cap → Order hidden).
  buildPanelLimitReached: () => {
    const el = document.getElementById('dom-buildpanel');
    const lim = el?.querySelector('[data-buildpanel-limit]') as HTMLElement | null;
    return lim !== null && lim !== undefined && getComputedStyle(lim).display !== 'none';
  },
  // the Order button is shown.
  buildPanelOrderShown: () => {
    const el = document.getElementById('dom-buildpanel');
    const o = el?.querySelector('[data-buildpanel-order]') as HTMLElement | null;
    return o !== null && o !== undefined && getComputedStyle(o).display !== 'none';
  },
  // the panel stage rect (CSS px) — the smoke asserts it is screen-centred at the top.
  buildPanelRect: () => {
    const el = document.getElementById('dom-buildpanel');
    const p = el?.querySelector('[data-buildpanel-stage]') as HTMLElement | null;
    if (p === null || p === undefined) return null;
    const r = p.getBoundingClientRect();
    return { left: r.left, top: r.top, w: r.width, h: r.height };
  },
  // select a unit row by ut → onSelect (the host routes to the form's list Select). A 2nd
  // call on the already-selected unit orders it (D-R6). Returns whether the row was clicked.
  buildPanelSelect: (ut: number) => {
    const el = document.getElementById('dom-buildpanel');
    const b = el?.querySelector(`[data-buildpanel-item="${ut}"]`) as HTMLButtonElement | null;
    if (b === null || b === undefined) return false;
    b.click();
    return true;
  },
  // click the Order button → onOrder (OnControlSelected(kidcOrder) in buildui.ts).
  buildPanelOrder: () => {
    const el = document.getElementById('dom-buildpanel');
    const b = el?.querySelector('[data-buildpanel-order]') as HTMLButtonElement | null;
    if (b === null || b === undefined || getComputedStyle(b).display === 'none') return false;
    b.click();
    return true;
  },
  // click the Cancel Order button → onCancelOrder.
  buildPanelCancelOrder: () => {
    const el = document.getElementById('dom-buildpanel');
    const b = el?.querySelector('[data-buildpanel-cancel-order]') as HTMLButtonElement | null;
    if (b === null || b === undefined || getComputedStyle(b).display === 'none') return false;
    b.click();
    return true;
  },
  // drive the REAL build-panel flow on an owned VTS/HRC builder (gid) — the SAME path the
  // canvas Build tap takes (buildui.OnMenuItemSelected(gob, kidcBuild) → env.domBuildPanel).
  buildPanelForceShow: (gid: number) => {
    const gs = gameHost.runningGame();
    if (gs === null) return false;
    const gobm = gs.sim.ctx.gobm;
    for (let pgob = gobm.GetFirstGob(); pgob !== null; pgob = gobm.GetNextGob(pgob)) {
      if (pgob.GetId() === gid) {
        gs.buildui.OnMenuItemSelected(pgob as never, 1304 /* kidcBuild */);
        return buildPanelUnmount !== null;
      }
    }
    return false;
  },
  closeBuildPanelPreview: () => {
    buildPanelUnmount?.();
  },

  // STRUCTURE BUILD CAROUSEL (Phase 5, FORM C) — the in-game HQ build panel
  // (StructureBuildForm) DOM overlay. structurePanelForceShow(gid) drives the SAME path the
  // canvas Build tap takes (buildui.OnMenuItemSelected(hq, kidcBuild) → env.domStructurePanel)
  // on a chosen owned HQ. The view re-polls the live read-model; the seams below read +
  // drive the rendered carousel. "Build" returns a gt → CANVAS placement (no command here).
  structurePanelUp: () => {
    const el = document.getElementById('dom-structurepanel');
    return el !== null && getComputedStyle(el).display !== 'none' && el.childElementCount > 0;
  },
  // the heading ("ORDER BUILDING").
  structurePanelHeading: () => {
    const el = document.getElementById('dom-structurepanel');
    const t = el?.querySelector('[data-buildpanel-heading]') as HTMLElement | null;
    return t ? (t.textContent ?? '') : '';
  },
  // the rendered icon list: { ut, disabled, selected, hasIcon, queueCount, building }. For
  // structures queueCount/building are ALWAYS 0/false (no overlays — C-R5); proven below.
  structurePanelUnits: () => {
    const el = document.getElementById('dom-structurepanel');
    if (el === null) return [];
    return [...el.querySelectorAll('[data-buildpanel-item]')].map((r) => {
      const e = r as HTMLElement;
      const cnt = e.querySelector('[data-buildpanel-count]') as HTMLElement | null;
      const prog = e.querySelector('[data-buildpanel-progress]') as HTMLElement | null;
      const icon = e.querySelector('[data-buildpanel-icon]') as HTMLElement | null;
      return {
        ut: Number(e.dataset.buildpanelItem),
        disabled: e.dataset.buildpanelDisabled === '1',
        selected: e.dataset.buildpanelSelected === '1',
        hasIcon: icon !== null,
        // the structure variant creates NO count/progress overlay elements at all (C-R5).
        hasCountEl: cnt !== null,
        hasProgressEl: prog !== null,
        queueCount: cnt !== null && cnt.style.display !== 'none' ? Number(cnt.textContent) : 0,
        building: prog !== null && prog.style.display !== 'none',
      };
    });
  },
  // the selected-structure stat block — the structure variant has P DEMAND / P SUPPLY rows
  // (not SPEED / RANGE). Reads the rendered text + which meters have pips.
  structurePanelInfo: () => {
    const el = document.getElementById('dom-structurepanel');
    if (el === null) return null;
    const txt = (sel: string): string => {
      const e = el.querySelector(sel) as HTMLElement | null;
      return e ? (e.textContent ?? '') : '';
    };
    const pips = (key: string): number => {
      const m = el.querySelector(`[data-buildpanel-meter="${key}"]`) as HTMLElement | null;
      return m ? m.childElementCount : 0;
    };
    const dmg = el.querySelector('[data-buildpanel-damage]') as HTMLElement | null;
    // probe whether SPEED/RANGE rows exist (they must NOT for the structure variant).
    const speedEl = el.querySelector('[data-buildpanel-value="speed"]');
    const rangeEl = el.querySelector('[data-buildpanel-value="range"]');
    const pdEl = el.querySelector('[data-buildpanel-value="powerDemand"]');
    const psEl = el.querySelector('[data-buildpanel-value="powerSupply"]');
    return {
      name: txt('[data-buildpanel-name]'),
      cost: txt('[data-buildpanel-value="cost"]'),
      armor: txt('[data-buildpanel-value="armor"]'),
      powerDemand: txt('[data-buildpanel-value="powerDemand"]'),
      powerSupply: txt('[data-buildpanel-value="powerSupply"]'),
      description: txt('[data-buildpanel-description]'),
      costPips: pips('cost'),
      armorPips: pips('armor'),
      powerDemandPips: pips('powerDemand'),
      powerSupplyPips: pips('powerSupply'),
      damagePips: dmg ? dmg.childElementCount : 0,
      // structure-specific row presence (the adversarial check: P DEMAND/P SUPPLY present,
      // SPEED/RANGE absent).
      hasPowerDemandRow: pdEl !== null,
      hasPowerSupplyRow: psEl !== null,
      hasSpeedRow: speedEl !== null,
      hasRangeRow: rangeEl !== null,
    };
  },
  // the "Building Limit Reached" label is shown (at the structure cap → Build hidden).
  structurePanelLimitReached: () => {
    const el = document.getElementById('dom-structurepanel');
    const lim = el?.querySelector('[data-buildpanel-limit]') as HTMLElement | null;
    return lim !== null && lim !== undefined && getComputedStyle(lim).display !== 'none';
  },
  // the "Build" button is shown.
  structurePanelBuildShown: () => {
    const el = document.getElementById('dom-structurepanel');
    const b = el?.querySelector('[data-buildpanel-build]') as HTMLElement | null;
    return b !== null && b !== undefined && getComputedStyle(b).display !== 'none';
  },
  // the panel stage rect (CSS px) — the smoke asserts it is screen-centred at the top.
  structurePanelRect: () => {
    const el = document.getElementById('dom-structurepanel');
    const p = el?.querySelector('[data-buildpanel-stage]') as HTMLElement | null;
    if (p === null || p === undefined) return null;
    const r = p.getBoundingClientRect();
    return { left: r.left, top: r.top, w: r.width, h: r.height };
  },
  // select a structure row by ut → onSelect (the host routes to the form's list Select +
  // UpdateStructureInfo). Returns whether the row was clicked.
  structurePanelSelect: (ut: number) => {
    const el = document.getElementById('dom-structurepanel');
    const b = el?.querySelector(`[data-buildpanel-item="${ut}"]`) as HTMLButtonElement | null;
    if (b === null || b === undefined) return false;
    b.click();
    return true;
  },
  // click the "Build" button → onBuild (EndForm(gt) → CANVAS gpfrmPlace handoff; NO command
  // from the panel). Returns whether the button was clicked.
  structurePanelBuild: () => {
    const el = document.getElementById('dom-structurepanel');
    const b = el?.querySelector('[data-buildpanel-build]') as HTMLButtonElement | null;
    if (b === null || b === undefined || getComputedStyle(b).display === 'none') return false;
    b.click();
    return true;
  },
  // drive the REAL structure-panel flow on an owned HQ (gid) — the SAME path the canvas
  // Build tap takes (buildui.OnMenuItemSelected(hq, kidcBuild) → env.domStructurePanel).
  structurePanelForceShow: (gid: number) => {
    const gs = gameHost.runningGame();
    if (gs === null) return false;
    const gobm = gs.sim.ctx.gobm;
    for (let pgob = gobm.GetFirstGob(); pgob !== null; pgob = gobm.GetNextGob(pgob)) {
      if (pgob.GetId() === gid) {
        gs.buildui.OnMenuItemSelected(pgob as never, 1304 /* kidcBuild */);
        return structurePanelUnmount !== null;
      }
    }
    return false;
  },
  closeStructurePanelPreview: () => {
    structurePanelUnmount?.();
  },

  // SECTION-ONLY HELP (Phase 5) — the build panels' "?" opens a small DOM help panel showing
  // ONLY the relevant help.txt section. These seams click a rendered build panel's "?" and
  // read the resulting help overlay (#dom-help). buildPanelHelpClick(which) clicks the "?" of
  // the open unit / structure / research panel (which = 'build' | 'structure' | 'upgrade').
  buildPanelHelpClick: (which: 'build' | 'structure' | 'upgrade' = 'structure') => {
    const id =
      which === 'structure'
        ? 'dom-structurepanel'
        : which === 'upgrade'
          ? 'dom-upgradepanel'
          : 'dom-buildpanel';
    const el = document.getElementById(id);
    const h = el?.querySelector('[data-buildpanel-help]') as HTMLButtonElement | null;
    if (h === null || h === undefined) return false;
    h.click();
    return true;
  },
  // the help overlay is up (visible, with content). (Prefixed `helpPanel*` to avoid the
  // pre-existing `helpText`/`helpUp` seams used elsewhere — the DOM-menu help content + the
  // build-panel header art.)
  helpPanelUp: () => {
    const el = document.getElementById('dom-help');
    return el !== null && getComputedStyle(el).display !== 'none' && el.childElementCount > 0;
  },
  // the help section title (the <H1> in the title bar).
  helpPanelTitle: () => {
    const t = document
      .getElementById('dom-help')
      ?.querySelector('[data-help-title]') as HTMLElement | null;
    return t ? (t.textContent ?? '') : '';
  },
  // the rendered help body text (the concatenated section paragraphs).
  helpPanelText: () => {
    const b = document
      .getElementById('dom-help')
      ?.querySelector('[data-help-text]') as HTMLElement | null;
    return b ? (b.textContent ?? '') : '';
  },
  // is the body still loading (the fetch hasn't resolved)?
  helpPanelLoading: () => {
    const el = document.getElementById('dom-help');
    return el !== null && el.querySelector('[data-help-loading]') !== null;
  },
  // the help panel stage rect (CSS px).
  helpPanelRect: () => {
    const p = document
      .getElementById('dom-help')
      ?.querySelector('[data-help-stage]') as HTMLElement | null;
    if (p === null || p === undefined) return null;
    const r = p.getBoundingClientRect();
    return { left: r.left, top: r.top, w: r.width, h: r.height };
  },
  // close the help overlay via its X (the SAME path the close button takes).
  helpPanelClose: () => {
    const x = document
      .getElementById('dom-help')
      ?.querySelector('[data-help-close]') as HTMLButtonElement | null;
    if (x === null || x === undefined) return false;
    x.click();
    return true;
  },
  // render the REAL HelpView for a given anchor in isolation (no build panel needed).
  helpPanelForceShow: (anchor: string) => {
    helpMount?.(anchor);
    return helpUnmount !== null;
  },
  closeHelpPanelPreview: () => {
    helpUnmount?.();
  },

  // FULL-MANUAL HELP (the pause-menu HELP) — the in-game overlay rendering the WHOLE
  // data/help.txt manual (mountHelpManual). The forceShow drives the SAME helpManualMount
  // the env.domHelpManual seam uses, in isolation (no in-game game needed); done is a no-op
  // preview. helpManualText reads the rendered manual body ([data-help-manual]).
  helpManualForceShow: () => {
    helpManualMount?.(() => {});
    return helpManualUnmount !== null;
  },
  helpManualText: () => {
    const b = document
      .getElementById('dom-help-manual')
      ?.querySelector('[data-help-manual]') as HTMLElement | null;
    return b ? (b.textContent ?? '') : '';
  },
  closeHelpManualPreview: () => {
    helpManualUnmount?.();
  },

  // RESEARCH CAROUSEL (Phase 5, FORM E) — the in-game research-center panel (UpgradeForm)
  // DOM overlay. researchPanelForceShow(gid) drives the SAME path the canvas Research tap takes
  // (buildui.OnMenuItemSelected(research, kidcResearch=1305) → env.domUpgradePanel) on a chosen
  // owned ResearchGob. The view re-polls the live read-model; the seams below read + drive the
  // rendered carousel. "Research" returns the upgm → BuildUI enqueues kmidUpgradeCommand (E-R5).
  researchPanelUp: () => {
    const el = document.getElementById('dom-upgradepanel');
    return el !== null && getComputedStyle(el).display !== 'none' && el.childElementCount > 0;
  },
  // the heading ("R&D TECHNOLOGIES").
  researchPanelHeading: () => {
    const el = document.getElementById('dom-upgradepanel');
    const t = el?.querySelector('[data-buildpanel-heading]') as HTMLElement | null;
    return t ? (t.textContent ?? '') : '';
  },
  // the rendered upgrade list: { upgm (in the ut field), disabled, selected, hasIcon }. NO
  // queue/progress overlays exist on research (proven via hasCountEl/hasProgressEl false).
  researchPanelUpgrades: () => {
    const el = document.getElementById('dom-upgradepanel');
    if (el === null) return [];
    return [...el.querySelectorAll('[data-buildpanel-item]')].map((r) => {
      const e = r as HTMLElement;
      const cnt = e.querySelector('[data-buildpanel-count]') as HTMLElement | null;
      const prog = e.querySelector('[data-buildpanel-progress]') as HTMLElement | null;
      const icon = e.querySelector('[data-buildpanel-icon]') as HTMLElement | null;
      return {
        upgm: Number(e.dataset.buildpanelItem),
        disabled: e.dataset.buildpanelDisabled === '1',
        selected: e.dataset.buildpanelSelected === '1',
        hasIcon: icon !== null,
        hasCountEl: cnt !== null,
        hasProgressEl: prog !== null,
      };
    });
  },
  // the selected-upgrade info block — FORM E has ONLY a COST row + PREREQUISITES + DESCRIPTION
  // (NO armor/damage/speed/range/power — E-R3). Probes which rows exist (the adversarial check).
  researchPanelInfo: () => {
    const el = document.getElementById('dom-upgradepanel');
    if (el === null) return null;
    const txt = (sel: string): string => {
      const e = el.querySelector(sel) as HTMLElement | null;
      return e ? (e.textContent ?? '') : '';
    };
    const shown = (sel: string): boolean => {
      const e = el.querySelector(sel) as HTMLElement | null;
      return e !== null && getComputedStyle(e).display !== 'none';
    };
    const costMeter = el.querySelector('[data-buildpanel-meter="cost"]') as HTMLElement | null;
    return {
      name: txt('[data-buildpanel-name]'),
      cost: txt('[data-buildpanel-value="cost"]'),
      description: txt('[data-buildpanel-description]'),
      prerequisites: txt('[data-buildpanel-prerequisites]'),
      costPips: costMeter ? costMeter.childElementCount : 0,
      costLabelShown: shown('[data-buildpanel-label="cost"]'),
      prereqLabelShown: shown('[data-buildpanel-prereq-label]'),
      // the adversarial NO-STAT-ROWS check: armor/damage/speed/range/power must be ABSENT.
      hasArmorRow: el.querySelector('[data-buildpanel-value="armor"]') !== null,
      hasSpeedRow: el.querySelector('[data-buildpanel-value="speed"]') !== null,
      hasRangeRow: el.querySelector('[data-buildpanel-value="range"]') !== null,
      hasPowerDemandRow: el.querySelector('[data-buildpanel-value="powerDemand"]') !== null,
      hasPowerSupplyRow: el.querySelector('[data-buildpanel-value="powerSupply"]') !== null,
      hasDamageRow: el.querySelector('[data-buildpanel-damage]') !== null,
      hasPrereqRow: el.querySelector('[data-buildpanel-prerequisites]') !== null,
    };
  },
  // the "Research" button is shown.
  researchPanelResearchShown: () => {
    const el = document.getElementById('dom-upgradepanel');
    const b = el?.querySelector('[data-buildpanel-research]') as HTMLElement | null;
    return b !== null && b !== undefined && getComputedStyle(b).display !== 'none';
  },
  // the panel stage rect (CSS px) — the smoke asserts it is screen-centred at the top.
  researchPanelRect: () => {
    const el = document.getElementById('dom-upgradepanel');
    const p = el?.querySelector('[data-buildpanel-stage]') as HTMLElement | null;
    if (p === null || p === undefined) return null;
    const r = p.getBoundingClientRect();
    return { left: r.left, top: r.top, w: r.width, h: r.height };
  },
  // select an upgrade row by upgm → onSelect (the host routes to the form's list Select +
  // UpdateUpgradeInfo). Returns whether the row was clicked.
  researchPanelSelect: (upgm: number) => {
    const el = document.getElementById('dom-upgradepanel');
    const b = el?.querySelector(`[data-buildpanel-item="${upgm}"]`) as HTMLButtonElement | null;
    if (b === null || b === undefined) return false;
    b.click();
    return true;
  },
  // click the "Research" button → onResearch (EndForm(upgm) → BuildUI enqueues
  // kmidUpgradeCommand; NO command from the panel). Returns whether the button was clicked.
  researchPanelResearch: () => {
    const el = document.getElementById('dom-upgradepanel');
    const b = el?.querySelector('[data-buildpanel-research]') as HTMLButtonElement | null;
    if (b === null || b === undefined || getComputedStyle(b).display === 'none') return false;
    b.click();
    return true;
  },
  // drive the REAL research-panel flow on an owned ResearchGob (gid) — the SAME path the canvas
  // Research tap takes (buildui.OnMenuItemSelected(research, kidcResearch) → env.domUpgradePanel).
  researchPanelForceShow: (gid: number) => {
    const gs = gameHost.runningGame();
    if (gs === null) return false;
    const gobm = gs.sim.ctx.gobm;
    for (let pgob = gobm.GetFirstGob(); pgob !== null; pgob = gobm.GetNextGob(pgob)) {
      if (pgob.GetId() === gid) {
        gs.buildui.OnMenuItemSelected(pgob as never, 1305 /* kidcResearch */);
        return upgradePanelUnmount !== null;
      }
    }
    return false;
  },
  closeResearchPanelPreview: () => {
    upgradePanelUnmount?.();
  },
  // automation-only: the LIVE GameSession (or null), so a smoke can drive the real game
  // setup it needs (e.g. build a structure + fast-forward the sim to ready a builder) via
  // the SAME session API the UI uses. The same reach gobs()/*ForceShow already have.
  runningGame: () => gameHost.runningGame(),
  // HUD BOTTOM BAR (Phase 5, Form 5) — the PERSISTENT in-game HUD overlay (over the
  // live canvas). hudUp(): the #dom-hud container is mounted + shown. hudState():
  // the rendered credits/power readout text (the smoke asserts it matches
  // plrLocal.GetCredits()/power). The HUD is display-only; the MENU/credits taps
  // route through the existing pause-menu / objectives seams.
  hudUp: () => {
    const el = document.getElementById('dom-hud');
    return el !== null && getComputedStyle(el).display !== 'none' && el.childElementCount > 0;
  },
  // automation-only DISPLAY probe (no sim/command/wire touch): push a SYNTHETIC
  // HudState into the LIVE HudView so the smoke can exercise pip states the running
  // game may not reach at start (RED low / YELLOW warning / GREEN normal) and the
  // symbol-flash phase. The view's update() is the SAME render path the per-frame
  // push uses; the next real per-frame push overwrites this immediately.
  hudForceRender: (state: {
    credits: number;
    needCredits: number;
    powerDemand: number;
    powerSupply: number;
    demandMax: number;
    flashOn: boolean;
  }) => hudForceRenderSeam?.(state) ?? false,
  // the LIVE values the HUD should be displaying — read off the running game with
  // the EXACT getters the canvas InputUIForm reads (plrLocal.GetCredits / etc). The
  // smoke asserts the rendered hudState() matches this ground truth (no faked value).
  hudLive: () => {
    const gs = gameHost.runningGame();
    const plr = gs?.sim.ctx.plrLocal;
    if (gs === null || plr === undefined || plr === null) return null;
    return {
      credits: plr.GetCredits(),
      needCredits: plr.GetNeedCreditsCount(),
      powerDemand: plr.GetPowerDemand(),
      powerSupply: plr.GetPowerSupply(),
      powerSupplyPerPip: gs.powerSupplyPerPip(),
      // m_nDemandMax — the yellow-zone threshold (inputui.ts:620-630). The smoke
      // asserts the rendered pip color reflects the 3-state PowerControl math.
      powerDemandMax: gs.powerDemandMax(),
    };
  },
  hudState: () => {
    const el = document.getElementById('dom-hud');
    if (el === null) return null;
    const credits = el.querySelector('[data-hud="credits"]');
    const power = el.querySelector('[data-hud="power"]');
    // the readouts are now font-rendered IMAGES (the kifntHud glyphs A–L are the
    // box/icon/pip art) — assert each <img> is present with a real (PNG data-URL)
    // source and a non-zero rendered size.
    const creditsImg = credits?.querySelector('[data-hud-credits-img]') as HTMLImageElement | null;
    const powerImg = power?.querySelector('[data-hud-power-img]') as HTMLImageElement | null;
    const imgInfo = (img: HTMLImageElement | null) => {
      if (img === null) return { present: false, src: '', w: 0, h: 0 };
      const r = img.getBoundingClientRect();
      return { present: true, src: img.getAttribute('src') ?? '', w: r.width, h: r.height };
    };
    const ci = imgInfo(creditsImg);
    const pi = imgInfo(powerImg);
    // the credit NUMBER is the WEB-font overlay (the box/$ icon stay bitmap).
    const creditsNum = (credits?.querySelector('[data-hud-credits-num]')?.textContent ?? '').trim();
    // the straddle layout: the credits' right edge < center, the power's left edge >
    // center (inputui.ts:208-210). Report the bounding-rect edges + the bar center.
    const cr = (credits as HTMLElement | null)?.getBoundingClientRect();
    const pr = (power as HTMLElement | null)?.getBoundingClientRect();
    const br = el.querySelector('div')?.getBoundingClientRect();
    return {
      // the CREDITS readout image — present, a real data URL, and sized.
      creditsImgPresent: ci.present,
      creditsImgSrc: ci.src,
      creditsImgW: ci.w,
      creditsImgH: ci.h,
      // the credit NUMBER (the web-font overlay text).
      creditsNum,
      // the POWER readout image — present, a real data URL, and sized.
      powerImgPresent: pi.present,
      powerImgSrc: pi.src,
      powerImgW: pi.w,
      powerImgH: pi.h,
      // the layout straddle edges (CSS px) + the bar's horizontal center.
      creditsRight: cr?.right ?? null,
      powerLeft: pr?.left ?? null,
      barCenter: br ? (br.left + br.right) / 2 : null,
    };
  },
  // IN-GAME OVERLAY TEXT LABELS (Phase 5) — the objective / alert / countdown / FPS
  // labels the SimUIForm paints over the live canvas, now web-font DOM (#dom-simui).
  // simUiUp(): the container is mounted + shown. simUiState(): the per-label rendered
  // text + whether each is displayed + the objective's computed font-family (the smoke
  // asserts the objective shows the live plrLocal.GetObjective() in the WEB body font).
  simUiUp: () => {
    const el = document.getElementById('dom-simui');
    return el !== null && getComputedStyle(el).display !== 'none' && el.childElementCount > 0;
  },
  simUiState: () => {
    const el = document.getElementById('dom-simui');
    if (el === null) return null;
    const read = (sel: string) => {
      const e = el.querySelector(`[data-simui="${sel}"]`) as HTMLElement | null;
      if (e === null) return { text: '', shown: false, fontFamily: '' };
      const cs = getComputedStyle(e);
      return {
        text: (e.textContent ?? '').trim(),
        shown: cs.display !== 'none',
        fontFamily: cs.fontFamily,
      };
    };
    return {
      objective: read('objective'),
      alert: read('alert'),
      countdown: read('countdown'),
      fps: read('fps'),
    };
  },
  // the LIVE objective the overlay should show — read off the running game with the
  // EXACT getter the canvas SimUIForm reads (plrLocal.GetObjective(), simuiform.ts:495).
  simUiLiveObjective: () => {
    const gs = gameHost.runningGame();
    const plr = gs?.sim.ctx.plrLocal;
    if (gs === null || plr === undefined || plr === null) return null;
    return plr.GetObjective();
  },
  // ?dom Phase 2 (forms.ini elimination): the RADAR (minimap) DOM <canvas>. minimapUp():
  // mounted + shown + has a canvas. minimapInfo(): the canvas geometry + a non-blank
  // check (counts non-(0,0,0) pixels) + the current view world pos — the smoke asserts the
  // radar is mounted, non-blank, pointer-events:auto, and that a tap moves the view.
  minimapUp: () => {
    const el = document.getElementById('dom-minimap');
    return (
      el !== null &&
      getComputedStyle(el).display !== 'none' &&
      el.querySelector('#dom-minimap-canvas') !== null
    );
  },
  minimapInfo: () => {
    const el = document.getElementById('dom-minimap');
    if (el === null) return null;
    const cv = el.querySelector('#dom-minimap-canvas') as HTMLCanvasElement | null;
    if (cv === null) return null;
    const cs = getComputedStyle(el);
    const ctx2d = cv.getContext('2d');
    let nonBlack = 0;
    let white = 0;
    if (ctx2d !== null && cv.width > 0 && cv.height > 0) {
      const d = ctx2d.getImageData(0, 0, cv.width, cv.height).data;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i] || d[i + 1] || d[i + 2]) nonBlack++;
        if (d[i] === 252 && d[i + 1] === 252 && d[i + 2] === 252) white++;
      }
    }
    const r = cv.getBoundingClientRect();
    const gs = gameHost.runningGame();
    const wpt = { wx: 0, wy: 0 };
    gs?.sui.GetViewPos(wpt);
    return {
      mounted: true,
      pointerEvents: cs.pointerEvents,
      zIndex: Number(cs.zIndex),
      cssWidth: r.width,
      cssHeight: r.height,
      pxWidth: cv.width,
      pxHeight: cv.height,
      rectLeft: r.left,
      rectTop: r.top,
      nonBlack,
      white,
      viewWx: wpt.wx,
      viewWy: wpt.wy,
    };
  },
  // DEVELOPER TEST OPTIONS (Alt+T) — open/close the debug panel (test-options-view.ts)
  // the SAME way the keyboard shortcut does (testOptionsToggle). testOptionsUp() reports
  // whether it's mounted; testOptions() reads its rendered toggles/actions. The smoke
  // drives the panel by clicking its rendered checkboxes ([data-testopt="…"]) + reads
  // the live flag back off the running game.
  toggleTestOptions: () => {
    testOptionsToggle?.();
  },
  testOptionsUp: () => {
    const el = document.getElementById('dom-test-options');
    return el !== null && getComputedStyle(el).display !== 'none' && el.childElementCount > 0;
  },
  testOptions: () => {
    const el = document.getElementById('dom-test-options');
    if (el === null) return null;
    const checks = [...el.querySelectorAll('[data-testopt]')].map((e) => ({
      label: (e as HTMLElement).dataset.testopt ?? '',
      // checked iff the box's inner X span is present (squareCheck only appends it when set).
      checked: e.querySelector('span > span') !== null,
    }));
    const actions = [...el.querySelectorAll('[data-testaction]')].map(
      (e) => (e as HTMLElement).dataset.testaction ?? '',
    );
    return { checks, actions };
  },
  // click a Test Options checkbox by its label (e.g. "Show FPS" / "God mode") — the
  // smoke uses this to flip a live flag through the real DOM, like a user would.
  clickTestOption: (label: string) => {
    const el = document.getElementById('dom-test-options');
    const row = el?.querySelector(`[data-testopt="${label}"]`) as HTMLElement | null;
    if (row === null || row === undefined) return false;
    row.click();
    return true;
  },
  // the live ctx.fGodMode off the running game — the smoke asserts "God mode" flips it.
  godMode: () => gameHost.runningGame()?.sim.ctx.fGodMode ?? null,
  // PAUSE MENU (Phase 5, Form 2) — open the in-game pause menu the SAME way the HUD
  // MENU button does (the inGameMenu seam → DoInGameMenu). Mounts the DOM
  // overlay (#dom-pausemenu) + holds the SP pause. The smoke drives the overlay
  // by clicking its rendered buttons.
  openPauseMenu: () => {
    gameHost.runningGame()?.openInGameMenuForAutomation();
  },
  pauseMenuUp: () => {
    const el = document.getElementById('dom-pausemenu');
    return el !== null && getComputedStyle(el).display !== 'none';
  },
  // the visible button labels of the DOM pause-menu overlay, in DOM order (the
  // SP/MP descriptor) — the smoke asserts the right state's buttons are present.
  pauseMenuButtons: () => {
    const el = document.getElementById('dom-pausemenu');
    if (el === null) return [];
    return [...el.querySelectorAll('button')].map((b) => b.textContent ?? '');
  },
  // PAUSE MENU PREVIEW (Phase 5, Form 2) — mount the REAL PauseMenuView with the
  // REAL inGameMenuButtons() descriptor for a given state ('sp' | 'mp' | 'mp-obs'),
  // WITHOUT a live game. This renders the EXACT geometry/labels the real in-game
  // menu would (e.g. the MP RESIGN + the Chat-at-y=70 gap), so a visual capture of
  // the MP pause menu is faithful even when standing up a full MP game in the smoke
  // is impractical. onSelect just unmounts (there is no real game to act on).
  openPauseMenuPreview: (state: 'sp' | 'mp' | 'mp-obs' = 'sp') => {
    if (pauseMenuMount === null) return false;
    const fMultiplayer = state !== 'sp';
    const plrFlags = state === 'mp-obs' ? kfPlrObserver : 0;
    // the descriptor reads ONLY fMultiplayer + plrLocal.GetFlags() — the rest is
    // never touched, so an otherwise-empty shaped env is faithful.
    const env = { fMultiplayer, plrLocal: { GetFlags: () => plrFlags } } as unknown as ShellEnv;
    const buttons = inGameMenuButtons(env);
    pauseMenuMount(buttons, () => {
      /* preview: no live game to act on */
    });
    return true;
  },
  // WIN / LOSE RESULTS SUMMARY (Phase 5, Form 3) — the summary overlay is driven by
  // the real game end / pause-menu abort (ShowObjectives → env.domObjectivesSummary).
  // The smoke opens it for real via the pause-menu ABORT MISSION button; these read
  // its state + click its buttons.
  summaryUp: () => {
    const el = document.getElementById('dom-objectives-summary');
    return el !== null && getComputedStyle(el).display !== 'none';
  },
  summaryButtons: () => {
    const el = document.getElementById('dom-objectives-summary');
    if (el === null) return [];
    return [...el.querySelectorAll('button')].map((b) => b.textContent ?? '');
  },
  // the RESULT-LINE text the summary shows ("OBJECTIVES COMPLETED!" / "MISSION
  // FAILED" / "MISSION ABORTED"), for the smoke's assertion + capture. Match a LEAF
  // div whose text is EXACTLY a result line (the result-line div is a kifntTitle
  // label with no children — container divs would also contain the result text).
  summaryResult: () => {
    const el = document.getElementById('dom-objectives-summary');
    if (el === null) return '';
    const hit = el.querySelector('[data-summary-result]'); // the result-line element (SP or MP)
    return hit ? (hit.textContent ?? '').trim() : '';
  },
  // SUMMARY PREVIEW (Phase 5, Form 3) — mount the REAL ObjectivesView (kind win/lose)
  // with a shaped snapshot, WITHOUT a live game end. Used for a faithful capture of
  // the win/lose layout in isolation; onChoice just unmounts (no real game to act on).
  openSummaryPreview: (kind: 'win' | 'lose' = 'win', mp = false, missionResult?: string) => {
    if (summaryMount === null) return false;
    const snapshot: ObjectivesSnapshot = {
      missionTitle: 'PREVIEW MISSION',
      objectives: [
        { text: 'Destroy the enemy HQ', status: 'COMPLETE' },
        { text: 'Defend your base', status: 'INCOMPLETE' },
        { text: '', status: '' },
        { text: '', status: '' },
      ],
      briefing: '',
      statistics: {
        enemyMobileUnitsKilled: 12,
        enemyStructuresKilled: 3,
        mobileUnitsLost: 7,
        structuresLost: 1,
        creditsAcquiredSpent: '4500 / 3200',
        elapsedTime: '00:12:34',
        rankTitle: 'General',
      },
    };
    const result =
      missionResult ??
      (mp
        ? kind === 'lose'
          ? 'YOU LOSE!'
          : 'YOU WIN!'
        : kind === 'lose'
          ? 'MISSION FAILED'
          : 'OBJECTIVES COMPLETED!');
    summaryMount({ snapshot, kind, fAborting: false, mp, missionResult: result }, () => {
      /* preview: no live game to act on */
    });
    return true;
  },
  closeSummaryPreview: () => {
    summaryUnmount?.();
  },
  // LOAD / SAVE (Phase 5) — the in-game slot-list overlay (#dom-loadsave). Driven for real
  // by the pause-menu LOAD/SAVE GAME + the LOSE-summary LOAD SAVED (→ env.domLoadGame/
  // domSaveGame); these read its state + open it in isolation for a faithful capture.
  loadSaveUp: () => {
    const el = document.getElementById('dom-loadsave');
    return el !== null && getComputedStyle(el).display !== 'none';
  },
  loadSaveButtons: () => {
    const el = document.getElementById('dom-loadsave');
    if (el === null) return [];
    return [...el.querySelectorAll('button')]
      .filter((b) => b.dataset.loadsaveOk !== undefined || b.dataset.loadsaveCancel !== undefined)
      .map((b) => b.textContent ?? '');
  },
  loadSaveRows: () => {
    const el = document.getElementById('dom-loadsave');
    if (el === null) return [];
    return [...el.querySelectorAll('[data-loadsave-list] button')].map((b) => b.textContent ?? '');
  },
  // open the REAL LoadSaveView (mode load/save) against the live saveStore, WITHOUT a game.
  openLoadPreview: () => {
    if (loadGameMount === null) return false;
    loadGameMount(saveStore, () => {
      /* preview */
    });
    return true;
  },
  openSavePreview: () => {
    if (saveGameMount === null) return false;
    saveGameMount(saveStore, () => {
      /* preview */
    });
    return true;
  },
  closeLoadSavePreview: () => {
    loadSaveUnmount?.();
  },
  // MP WAITING (Phase 5) — the "WAITING FOR ALL PLAYERS…" overlay (#dom-waiting). Driven for
  // real on the MP sim-start path (env.domWaiting); these open/read it in isolation.
  waitingUp: () => {
    const el = document.getElementById('dom-waiting');
    return el !== null && getComputedStyle(el).display !== 'none';
  },
  openWaitingPreview: () => {
    if (waitingMount === null) return false;
    waitingMount('WAITING FOR ALL PLAYERS...');
    return true;
  },
  closeWaitingPreview: () => {
    waitingUnmount?.();
  },
  // canvas-removal R2 PROOF (teeth): the shipped forms.ini is no longer PACKED into the pdb.
  // This reports whether the loaded art pack still carries a forms.ini section — it MUST be
  // false (the structural guarantee that nothing CAN read the shipped forms.ini: it isn't
  // there). The forms.ini no-read smoke asserts this is false. Supersedes the old runtime
  // read-tripwire (a read is now impossible, not merely unobserved).
  shippedFormsIniPresent: () => pakr.MapFile('forms.ini') !== null,
  // MP "LAG ALERT" (Phase 5, forms.ini-elim) — the modeless lag Yes/No box now rides a
  // DEDICATED DOM overlay (#dom-lagbox). lagBoxUp reports it; openLagBoxPreview renders
  // it in isolation (no live game) so the MP smoke proves it's DOM.
  lagBoxUp: () => {
    const el = document.getElementById('dom-lagbox');
    return el !== null && getComputedStyle(el).display !== 'none' && el.childElementCount > 0;
  },
  openLagBoxPreview: () => {
    if (lagBoxMount === null) return false;
    lagBoxMount(
      'LAG ALERT',
      'PlayerX is communicating erratically and causing the game to lag. Disconnect PlayerX?',
      () => {},
    );
    return true;
  },
  closeLagBoxPreview: () => {
    lagBoxUnmount?.();
  },
  // GAME OPTIONS (Phase 5, Form 4) — the in-game GAME OPTIONS overlay is driven by
  // the real pause-menu OPTIONS (handleInGameMenuChoice kidcOptions →
  // DoModalGameOptionsForm → env.domGameOptions). These read its state + the smoke
  // clicks its buttons (OK/CANCEL/DEFAULT) directly. Shows the DOM overlay
  // (#dom-game-options).
  gameOptionsUp: () => {
    const el = document.getElementById('dom-game-options');
    return el !== null && getComputedStyle(el).display !== 'none';
  },
  gameOptionsButtons: () => {
    const el = document.getElementById('dom-game-options');
    if (el === null) return [];
    return [...el.querySelectorAll('button')].map((b) => b.textContent ?? '');
  },
  // the LIVE running-game options the in-game form applies to — the smoke asserts
  // OK persisted them to the actual game (the SAME options/sndm/gtGameSpeed the
  // canvas InGameOptionsForm writes), not just the overlay DOM.
  gameOptionsState: () => {
    const gs = gameHost.runningGame();
    if (gs === null) return null;
    return {
      wfHandicap: gs.options.wfHandicap,
      nScrollSpeed: gs.options.nScrollSpeed,
      gtGameSpeed: gs.gtGameSpeed,
      soundEnabled: shell.sndm.IsEnabled(),
    };
  },
  // the visible CHECK/RADIO labels of the GAME OPTIONS overlay (Difficulty radios +
  // Misc checkboxes), in DOM order — the smoke asserts the controls are present.
  gameOptionsChecks: () => {
    const el = document.getElementById('dom-game-options');
    if (el === null) return [];
    return [...el.querySelectorAll('label')].map((l) => (l.textContent ?? '').trim());
  },
  // GAME OPTIONS PREVIEW (Phase 5, Form 4) — mount the REAL GameOptionsView with a
  // fake-but-faithful GameOptionsModel (no live game), for a visual capture in
  // isolation. onOk/onCancel just unmount (there is no real game to apply to). The
  // smoke prefers the REAL pause-menu path; this is a capture/standalone aid.
  openGameOptionsPreview: () => {
    if (gameOptionsMount === null) return false;
    let speed = 11,
      scroll = 0;
    let diff: 'easy' | 'normal' | 'hard' = 'normal';
    let muted = false;
    let mouse = false;
    const model: GameOptionsModel = {
      gameSpeedNotch: () => speed,
      gameSpeedNotchMax: () => 15,
      defaultGameSpeedNotch: () => 11,
      setGameSpeedNotch: (i) => {
        speed = i;
      },
      gameSpeedLabel: () => `${speed}x`,
      scrollSpeedNotch: () => scroll,
      scrollSpeedNotchMax: () => 16,
      setScrollSpeedNotch: (i) => {
        scroll = i;
      },
      scrollSpeedLabel: () => '1.0x',
      difficulty: () => diff,
      setDifficulty: (d) => {
        diff = d;
      },
      muted: () => muted,
      setMuted: (f) => {
        muted = f;
      },
      mouseMode: () => mouse,
      setMouseMode: (f) => {
        mouse = f;
      },
      lassoSelection: () => false,
      ok: () => {},
      cancel: () => {},
      setDefaults: () => {
        diff = 'normal';
        muted = false;
        speed = 11;
        scroll = 0;
      },
    };
    gameOptionsMount(model, () => {
      /* preview: no live game to act on */
    });
    return true;
  },
  closeGameOptionsPreview: () => {
    gameOptionsUnmount?.();
  },
};

// click the OBJECTIVES overlay's OK programmatically (the automation close path).
function objectivesClose(): void {
  const root = document.getElementById('dom-objectives');
  if (root === null) return;
  const ok = [...root.querySelectorAll('button')].find((b) => b.textContent === 'OK');
  ok?.click();
}

// Force Reload (the TEST OPTIONS / Alt+T panel): guarantee the freshest build.
// Navigations are already network-first (sw.js), so this is belt-and-braces — unregister
// every service worker and delete its offline caches, THEN reload. The next load
// re-registers the current SW and re-precaches the live build.
async function forceReloadLatest(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (e) {
    console.warn('force reload cleanup failed', e);
  }
  location.reload();
}

// Reloading is safe only when nothing in-flight would be lost: not while a game
// runs (SP or MP), and not while the MP lobby flow (dom-lobby: room/staging/
// connected transport) or a DLC download (dom-dlc) is up. Everything else — the
// title, the DOM menu screens, profile/leaderboard — reloads losslessly (saves/
// prefs live in IndexedDB). gameHost is built in boot(); before that (or after a
// boot failure) reloading is always safe.
function updateSafeToReload(): boolean {
  const g = gameHost as GameHost | undefined; // pre-boot: not yet assigned
  if (g !== undefined && g.inGame()) return false;
  for (const id of ['dom-lobby', 'dom-dlc']) {
    const overlay = document.getElementById(id);
    if (overlay !== null && overlay.style.display !== 'none') return false;
  }
  return true;
}

// PWA installability (tasks/pwa-install-plan.md): register the inert service
// worker (Chromium fires `beforeinstallprompt` only when one is present) and arm
// the install affordance now, before boot, so its beforeinstallprompt listener
// is attached before the event can fire. SW registration is deferred to `load`
// so it never competes with boot's critical pack fetches.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((e: unknown) => {
      console.warn('service worker registration failed', e);
    });
  });

  // #206: an already-running client (iOS Home-Screen PWA resumed from the app
  // switcher, a long-lived tab) never re-navigates, so sw.js's network-first
  // navigation path never runs and the old bundle executes forever. Poll the SW
  // registration for a byte-different sw.js on every resume (visibilitychange →
  // visible fires on iOS standalone PWA resume too; pageshow covers bfcache
  // restores) plus a periodic timer for a tab that just stays open. A found
  // update installs → skipWaiting/clients.claim (sw.js) → controllerchange here
  // → reload at the first safe moment (immediately at the menu; a running game
  // finishes first — the 5s tick lands the deferred reload once it's safe).
  const updateCheck = new UpdateCheck({
    checkForUpdate: async () => {
      await (await navigator.serviceWorker.getRegistration())?.update();
    },
    safeToReload: updateSafeToReload,
    reload: () => {
      location.reload();
    },
    now: () => Date.now(),
  });
  // controllerchange with NO prior controller is the first install's
  // clients.claim() on this (previously uncontrolled) page — not an update.
  let hadController = navigator.serviceWorker.controller !== null;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) {
      hadController = true;
      return;
    }
    updateCheck.updateActivated();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') updateCheck.resume();
  });
  window.addEventListener('pageshow', () => {
    updateCheck.resume();
  });
  setInterval(() => {
    updateCheck.resume();
  }, 30 * 60_000); // the long-lived visible tab
  setInterval(() => {
    updateCheck.maybeReload();
  }, 5_000); // land a deferred (mid-game) reload
}
armInstallPrompt();

boot().catch((e: unknown) => {
  console.error(e);
  progress(null); // don't leave the download bar hanging next to the failure text
  status(`BOOT FAILED: ${e instanceof Error ? e.message : String(e)}`);
});
